import { FormSection } from '../types/form';
import { formSections } from './formFields';
import { standardPrompt, enhancedPrompt, premiumPrompt } from '../services/claude';
import { createDocumentPrompts } from './documentPromptTemplates';

export const documentTypeConfig: Record<string, {
  formSections: { [key: string]: FormSection };
  prompts: {
    standard: string;
    enhanced: string;
    premium: string;
  };
}> = {
  'standard-residential': {
    formSections: {
      ast: formSections.ast
    },
    prompts: {
      standard: standardPrompt,
      enhanced: enhancedPrompt,
      premium: premiumPrompt
    }
  },
  'periodic-tenancy': {
    formSections: {
      details: {
        id: 'details',
        title: 'Periodic Tenancy Agreement (2025 Renters\' Rights Bill Compliant)',
        fields: [
          {
            id: 'landlordName',
            type: 'text',
            question: 'What is the landlord\'s full name?',
            validation: { required: true },
            description: 'Under the 2025 Renters\' Rights Bill, all new tenancies are periodic (rolling monthly) by default.',
            examples: ['John Smith', 'Sarah Williams', 'Property Holdings Ltd', 'Mr. David Thompson'],
            exampleHint: 'Individual name or company name'
          },
          {
            id: 'landlordEmail',
            type: 'text',
            question: 'What is the landlord\'s email address?',
            validation: { required: true, pattern: '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$' },
            examples: ['john.smith@gmail.com', 'landlord@propertymanagement.co.uk', 'sarah.williams@outlook.com'],
            exampleHint: 'Primary contact email for correspondence'
          },
          {
            id: 'landlordAddress',
            type: 'text',
            question: 'What is the landlord\'s correspondence address?',
            validation: { required: true },
            examples: ['15 Oak Avenue, Manchester, M15 6BH', 'Flat 3, The Maples, Bristol, BS1 2AA', '42 High Street, London, SW1A 1AA'],
            exampleHint: 'Full correspondence address including postcode'
          },
          {
            id: 'tenantName',
            type: 'text',
            question: 'What is the tenant\'s full name?',
            validation: { required: true },
            examples: ['Emma Johnson', 'Michael Chen', 'Dr. Rebecca Taylor', 'Ms. Priya Patel'],
            exampleHint: 'Full legal name as appears on ID'
          },
          {
            id: 'tenantEmail',
            type: 'text',
            question: 'What is the tenant\'s email address?',
            validation: { required: true, pattern: '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$' },
            examples: ['emma.johnson@outlook.com', 'm.chen@company.co.uk', 'rebecca.taylor@gmail.com'],
            exampleHint: 'Primary contact email for the tenant'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the property address?',
            validation: { required: true },
            examples: ['42 High Street, London, SW1A 1AA', 'Apartment 5B, Riverside Tower, Leeds, LS1 4AP', 'Unit 12, Park View Court, Birmingham, B1 3RL'],
            exampleHint: 'Complete property address including postcode'
          },
          {
            id: 'startDate',
            type: 'date',
            question: 'When does the tenancy begin?',
            validation: { required: true },
            description: 'The tenancy will continue on a rolling monthly basis with no fixed end date. Tenant can give 2 months\' notice at any time.',
            examples: ['2025-03-01', '2025-04-01', '2025-05-01'],
            exampleHint: 'Common start dates align with monthly rent cycles'
          },
          {
            id: 'rentAmount',
            type: 'number',
            question: 'What is the monthly rent? (in GBP)',
            validation: { required: true, min: 0 },
            examples: ['1250', '850', '2100', '1750', '950'],
            exampleHint: 'Monthly rent amount in pounds',
            smartDefaults: true
          },
          {
            id: 'advertisedRent',
            type: 'number',
            question: 'What was the advertised rent amount? (in GBP)',
            validation: { required: true, min: 0 },
            description: '2025 Reform: Rent cannot be set higher than the advertised amount to prevent rental bidding wars.',
            examples: ['1250', '850', '2100'],
            exampleHint: 'Must match actual rent to prevent bidding wars',
            smartDefaults: true
          },
          {
            id: 'depositAmount',
            type: 'number',
            question: 'What is the security deposit? (in GBP)',
            validation: { required: true, min: 0 },
            description: 'Maximum 5 weeks\' rent (or 6 weeks if annual rent exceeds £50,000).',
            examples: ['1442', '980', '2423'],
            exampleHint: 'Calculate: (Monthly rent ÷ 52) × 5 weeks',
            smartDefaults: true
          },
          {
            id: 'depositScheme',
            type: 'select',
            question: 'Which deposit protection scheme will be used?',
            options: ['Deposit Protection Service (DPS)', 'MyDeposits', 'Tenancy Deposit Scheme (TDS)'],
            validation: { required: true }
          },
          {
            id: 'rentIncreaseFrequency',
            type: 'radio',
            question: 'Confirm rent increases are limited to once per year:',
            options: ['Yes - Maximum once per year'],
            validation: { required: true },
            description: '2025 Reform: Rent can only be increased once per 12-month period. Tenants have the right to challenge above-market increases via tribunal.'
          },
          {
            id: 'petPolicy',
            type: 'select',
            question: 'What is the pet policy?',
            options: ['Pets allowed with written consent', 'No pets', 'Pets allowed without restrictions'],
            validation: { required: true },
            description: '2025 Reform: Landlords cannot unreasonably refuse pet requests. Must respond in writing within 28 days.'
          },
          {
            id: 'petBondAmount',
            type: 'number',
            question: 'If pets allowed, what is the pet bond/additional deposit? (in GBP)',
            validation: { required: false, min: 0 },
            description: 'Pet bond must be reasonable and cannot exceed total deposit cap.',
            examples: ['250', '300', '0', '200'],
            exampleHint: 'Additional deposit for pets, if applicable'
          },
          {
            id: 'benefitsAccepted',
            type: 'radio',
            question: 'Confirm no discrimination against tenants on benefits:',
            options: ['Yes - Benefits recipients considered equally'],
            validation: { required: true },
            description: '2025 Reform: Landlords cannot refuse tenants solely because they receive housing benefits.'
          },
          {
            id: 'childrenAccepted',
            type: 'radio',
            question: 'Confirm no discrimination against families with children:',
            options: ['Yes - Families with children considered equally'],
            validation: { required: true },
            description: '2025 Reform: Blanket bans on families with children are prohibited.'
          },
          {
            id: 'decentHomesCompliant',
            type: 'radio',
            question: 'Does the property meet Decent Homes Standard?',
            options: ['Yes', 'No - Improvements planned'],
            validation: { required: true },
            description: '2025 Reform: All rental properties must meet Decent Homes Standard (reasonable repair, modern facilities, adequate heating, thermal comfort).'
          },
          {
            id: 'awaabsLawCompliance',
            type: 'text',
            question: 'What are the hazard response timescales for serious issues (damp, mould)?',
            validation: { required: true },
            description: 'Awaab\'s Law 2025: Statutory timeframes apply for responding to serious hazards like dangerous damp and mould.',
            examples: [
              'Emergency repairs within 24 hours, damp/mould within 14 days',
              'Category 1 hazards within 7 days, Category 2 within 28 days',
              'Urgent hazards 24-48 hours, non-urgent within 14 days'
            ],
            exampleHint: 'Statutory timeframes for serious hazards'
          },
          {
            id: 'epcRating',
            type: 'select',
            question: 'What is the Energy Performance Certificate (EPC) rating?',
            options: ['A', 'B', 'C', 'D', 'E'],
            validation: { required: true },
            description: 'Properties must have minimum EPC rating of E.'
          },
          {
            id: 'gasSafetyCertificate',
            type: 'radio',
            question: 'Is there a valid Gas Safety Certificate?',
            options: ['Yes', 'No gas supply'],
            validation: { required: true }
          },
          {
            id: 'eicrCertificate',
            type: 'radio',
            question: 'Is there a valid Electrical Installation Condition Report (EICR)?',
            options: ['Yes'],
            validation: { required: true },
            description: 'Required every 5 years for rental properties in England.'
          },
          {
            id: 'section21Abolished',
            type: 'radio',
            question: 'Acknowledge Section 21 (no-fault evictions) abolished:',
            options: ['Yes - Understood'],
            validation: { required: true },
            description: '2025 Reform: Section 21 notices abolished. Landlords must use Section 8 with specific grounds for possession.'
          },
          {
            id: 'possessionGroundsAwareness',
            type: 'checkbox',
            question: 'Which possession grounds are you aware of?',
            options: [
              'Ground 1 (landlord intends to sell - after 12 months)',
              'Ground 1A (landlord/family needs to occupy - after 12 months)',
              'Ground 8 (serious rent arrears)',
              'Ground 14 (anti-social behaviour)',
              'Other grounds'
            ],
            validation: { required: true },
            description: '2025 Reform: New possession grounds with 12-month protection period for sale/occupation grounds.'
          },
          {
            id: 'tenantNoticeRights',
            type: 'radio',
            question: 'Acknowledge tenant can give 2 months\' notice at any time:',
            options: ['Yes - Understood'],
            validation: { required: true },
            description: '2025 Reform: Tenants can end periodic tenancy with 2 months\' written notice, providing flexibility and security.'
          },
          {
            id: 'furnishingStatus',
            type: 'select',
            question: 'What is the furnishing status?',
            options: ['Furnished', 'Part Furnished', 'Unfurnished'],
            validation: { required: true }
          },
          {
            id: 'utilitiesIncluded',
            type: 'checkbox',
            question: 'Which utilities/services are INCLUDED in rent?',
            options: ['Gas', 'Electricity', 'Water', 'Council Tax', 'Internet', 'None'],
            validation: { required: true }
          },
          {
            id: 'smokeAlarms',
            type: 'radio',
            question: 'Are working smoke alarms installed on each floor?',
            options: ['Yes'],
            validation: { required: true }
          },
          {
            id: 'carbonMonoxideAlarms',
            type: 'radio',
            question: 'Are carbon monoxide alarms installed where required?',
            options: ['Yes', 'No solid fuel appliances'],
            validation: { required: true }
          },
          {
            id: 'rightToRentChecked',
            type: 'radio',
            question: 'Has Right to Rent check been completed?',
            options: ['Yes', 'Pending'],
            validation: { required: true }
          },
          {
            id: 'howToRentProvided',
            type: 'radio',
            question: 'Will "How to Rent" guide be provided?',
            options: ['Yes'],
            validation: { required: true }
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'periodic tenancy agreements under the 2025 Renters\' Rights Bill',
      'Generate a comprehensive UK periodic tenancy agreement fully compliant with the 2025 Renters\' Rights Bill. Include: abolition of Section 21 (no-fault evictions), periodic tenancy structure with rolling monthly terms, tenant 2-month notice rights, new possession grounds (Ground 1 for sale, Ground 1A for landlord occupation) with 12-month protection periods, once-per-year rent increase limits with tribunal challenge rights, pet provisions with 28-day landlord response requirements, anti-discrimination protections for families with children and benefits recipients, Decent Homes Standard compliance, Awaab\'s Law hazard response timescales, rental bidding prohibition, and all essential residential tenancy clauses. Incorporate ALL form data.',
      'Generate an enhanced UK periodic tenancy agreement with comprehensive 2025 Renters\' Rights Bill compliance. Include detailed provisions for: periodic tenancy operation and tenant security, abolished Section 21 with full Section 8 grounds framework, 12-month protection periods preventing landlord repossession for sale/occupation, rent increase procedures limited to once annually with market rate justifications and tribunal pathways, pet request and approval processes with 28-day timelines, anti-discrimination safeguards, Decent Homes Standard criteria and inspection protocols, Awaab\'s Law hazard identification and response requirements, deposit protection with scheme details, maintenance responsibilities, repair timescales, dispute resolution procedures, and penalty frameworks (£7,000 minor, £40,000 serious offenses). Incorporate ALL form data.',
      'Generate a premium professional-grade UK periodic tenancy agreement with exhaustive 2025 Renters\' Rights Bill integration. Include comprehensive provisions: periodic tenancy legal framework with statutory security of tenure, abolished Section 21 with detailed Section 8 possession grounds analysis (mandatory vs discretionary), 12-month protection clauses with exceptions, rent increase mechanisms with annual limitation, market review procedures, tribunal challenge protocols with evidence standards, pet policy with written consent procedures and 28-day statutory timelines, complete anti-discrimination framework (benefits, children, protected characteristics), Decent Homes Standard compliance matrix with inspection schedules, Awaab\'s Law hazard categories with statutory response timescales (24 hours emergency, 7 days urgent, 28 days routine), deposit protection with scheme registration requirements and prescribed information, comprehensive repair and maintenance schedules, health and safety compliance (gas, electrical, fire), energy efficiency requirements, landlord penalty provisions with appeal rights, dispute resolution pathways (negotiation, mediation, tribunal, court), and full compliance documentation. Incorporate ALL form data with professional legal formatting and cross-references.'
    )
  },
  'tenancy-conversion-pack': {
    formSections: {
      details: {
        id: 'details',
        title: 'Fixed-Term to Periodic Tenancy Conversion Pack',
        fields: [
          {
            id: 'landlordName',
            type: 'text',
            question: 'What is the landlord\'s full name?',
            validation: { required: true },
            examples: ['John Smith', 'Property Management Ltd'],
            exampleHint: 'Name as appears on current tenancy agreement'
          },
          {
            id: 'tenantName',
            type: 'text',
            question: 'What is the tenant\'s full name?',
            validation: { required: true },
            examples: ['Emma Johnson', 'Michael Chen'],
            exampleHint: 'Name as appears on current tenancy agreement'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the property address?',
            validation: { required: true },
            examples: ['42 High Street, London, SW1A 1AA'],
            exampleHint: 'Full address including postcode'
          },
          {
            id: 'currentTenancyStartDate',
            type: 'date',
            question: 'When did the current tenancy start?',
            validation: { required: true },
            examples: ['2023-06-01', '2024-01-15'],
            exampleHint: 'Original start date of existing tenancy'
          },
          {
            id: 'currentTenancyType',
            type: 'select',
            question: 'What type of tenancy is currently in place?',
            options: ['Fixed-term AST', 'Periodic AST (already periodic)', 'Fixed-term with break clause'],
            validation: { required: true }
          },
          {
            id: 'currentFixedTermEndDate',
            type: 'date',
            question: 'When does/did the fixed term end (if applicable)?',
            validation: { required: false },
            examples: ['2024-06-01', '2025-01-15'],
            exampleHint: 'Leave blank if already periodic'
          },
          {
            id: 'currentRentAmount',
            type: 'number',
            question: 'What is the current monthly rent? (in GBP)',
            validation: { required: true, min: 0 },
            examples: ['1250', '850', '2100'],
            exampleHint: 'Current monthly rent amount'
          },
          {
            id: 'lastRentIncreaseDate',
            type: 'date',
            question: 'When was the last rent increase (if any)?',
            validation: { required: false },
            examples: ['2024-01-01', '2023-06-01'],
            exampleHint: 'Leave blank if never increased'
          },
          {
            id: 'depositAmount',
            type: 'number',
            question: 'What is the current deposit amount? (in GBP)',
            validation: { required: true, min: 0 },
            examples: ['1442', '980', '2423'],
            exampleHint: 'Current deposit held'
          },
          {
            id: 'depositScheme',
            type: 'select',
            question: 'Which deposit protection scheme is the deposit in?',
            options: ['Deposit Protection Service (DPS)', 'MyDeposits', 'Tenancy Deposit Scheme (TDS)'],
            validation: { required: true }
          },
          {
            id: 'conversionEffectiveDate',
            type: 'date',
            question: 'When should the conversion take effect?',
            validation: { required: true },
            description: 'We recommend using the May 2026 reform date or the natural end of current fixed term.',
            examples: ['2026-05-30', '2025-06-01'],
            exampleHint: 'Suggested: May 30, 2026 (reform date)'
          },
          {
            id: 'tenantEmailForNotice',
            type: 'text',
            question: 'What is the tenant\'s email address?',
            validation: { required: true, pattern: '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$' },
            examples: ['emma.johnson@outlook.com'],
            exampleHint: 'For sending conversion notice'
          },
          {
            id: 'additionalNotes',
            type: 'textarea',
            question: 'Any additional notes or special circumstances?',
            validation: { required: false },
            examples: ['Tenant has been excellent, no issues', 'Property will undergo minor improvements'],
            exampleHint: 'Optional context for the conversion'
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'tenancy conversion documentation under the 2025 Renters\' Rights Bill',
      'Generate a comprehensive conversion pack for transitioning an existing fixed-term tenancy to a periodic tenancy under the 2025 Renters\' Rights Bill. The pack must include: (1) CONVERSION LETTER explaining the law changes effective May 2026, (2) TENANCY TERMS SUMMARY comparing old vs new rights, (3) Q&A EXPLAINER covering common concerns. Include sections on: what\'s changing (Section 21 abolition, periodic nature, possession grounds), what stays the same (rent, deposit, property), tenant\'s new rights (2-month notice, rent increase protections, pet request rights), landlord\'s updated obligations (Section 8 only, 12-month protection periods), and contact information for questions. Use clear, reassuring, plain English language. Incorporate ALL form data.',
      'Generate an enhanced conversion pack with detailed legal analysis for transitioning an existing tenancy to periodic under the 2025 Renters\' Rights Bill. Include: (1) DETAILED CONVERSION LETTER with timeline, (2) COMPREHENSIVE TERMS COMPARISON with side-by-side analysis, (3) EXTENDED Q&A with 20+ questions, (4) RIGHTS SCHEDULE outlining all tenant protections, (5) OBLIGATIONS SUMMARY for landlord. Cover: abolished Section 21 with Section 8 framework, periodic tenancy operation, 12-month protection periods, rent increase limitations (once per year, tribunal rights), pet policy changes, anti-discrimination protections, Decent Homes Standard, Awaab\'s Law requirements, deposit continuity, dispute resolution pathways. Provide reassurance while ensuring legal accuracy. Incorporate ALL form data.',
      'Generate a premium professional conversion pack with exhaustive documentation for transitioning existing tenancies under 2025 Renters\' Rights Bill. Include: (1) EXECUTIVE CONVERSION LETTER with legal framework, (2) COMPLETE TERMS COMPARISON with before/after matrix, (3) COMPREHENSIVE Q&A GUIDE (30+ questions with legal references), (4) DETAILED TENANT RIGHTS SCHEDULE with statutory basis, (5) LANDLORD OBLIGATIONS FRAMEWORK, (6) TIMELINE AND MILESTONES CHART, (7) COMPLIANCE CHECKLIST, (8) DISPUTE RESOLUTION GUIDE. Provide exhaustive coverage of: Section 21 abolition rationale and Section 8 grounds, periodic tenancy legal framework, 12-month protection period mechanics, rent increase procedures with tribunal challenge protocols, comprehensive pet policy framework, anti-discrimination protections (benefits, children, families), Decent Homes Standard compliance requirements, Awaab\'s Law hazard response obligations, deposit protection continuity, penalty frameworks for non-compliance, ombudsman scheme preparation. Include flowcharts, decision trees, and visual aids. Incorporate ALL form data with professional formatting.'
    )
  },
  'commercial-lease': {
    formSections: {
      details: {
        id: 'details',
        title: 'Commercial Lease Agreement Details',
        fields: [
          {
            id: 'landlordName',
            type: 'text',
            question: 'What is the landlord\'s full name or company name?',
            validation: { required: true },
            examples: ['Commercial Property Estates Ltd', 'Retail Spaces UK Limited', 'Industrial Holdings PLC', 'Mr. James Anderson'],
            exampleHint: 'Registered company name or individual name'
          },
          {
            id: 'landlordCompanyNumber',
            type: 'text',
            question: 'What is the landlord\'s company registration number (if applicable)?',
            validation: { required: false },
            examples: ['12345678', 'SC654321', '09876543', '11223344'],
            exampleHint: '8-digit Companies House registration number'
          },
          {
            id: 'landlordEmail',
            type: 'text',
            question: 'What is the landlord\'s email address?',
            validation: {
              required: true,
              pattern: '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$'
            }
          },
          {
            id: 'tenantCompanyName',
            type: 'text',
            question: 'What is the tenant company name?',
            validation: { required: true },
            examples: ['Tech Innovations Ltd', 'The Coffee Shop Company', 'Green Retail Solutions PLC', 'Digital Marketing Partners Ltd'],
            exampleHint: 'Registered trading name of tenant company'
          },
          {
            id: 'tenantCompanyNumber',
            type: 'text',
            question: 'What is the tenant\'s company registration number?',
            validation: { required: true },
            examples: ['87654321', '10293847', 'SC123456', '11556677'],
            exampleHint: '8-digit Companies House number'
          },
          {
            id: 'tenantEmail',
            type: 'text',
            question: 'What is the tenant\'s email address?',
            validation: {
              required: true,
              pattern: '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$'
            }
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the commercial property address?',
            validation: { required: true }
          },
          {
            id: 'propertyDescription',
            type: 'text',
            question: 'Describe the commercial property (size, facilities, parking, etc.):',
            validation: { required: true },
            examples: [
              'Ground floor retail unit, 1,200 sq ft, with rear storage and 2 customer parking spaces',
              'First floor office suite, 800 sq ft, open plan with kitchenette and dedicated WC',
              'Industrial warehouse, 5,000 sq ft, with loading bay, 15ft ceiling height, and 8 parking spaces'
            ],
            exampleHint: 'Include size, features, parking, and special facilities'
          },
          {
            id: 'businessType',
            type: 'text',
            question: 'What type of business will operate from this property?',
            validation: { required: true },
            examples: ['Independent coffee shop', 'Accounting and tax advisory', 'Light manufacturing - electronics assembly', 'Boutique retail - clothing', 'Digital marketing agency'],
            exampleHint: 'Specific business activity description'
          },
          {
            id: 'permittedUse',
            type: 'text',
            question: 'What is the permitted use class and any restrictions?',
            validation: { required: true },
            description: 'Specify use class (e.g., A1 retail, B1 office, D2 leisure) and any restrictions on operations.',
            examples: [
              'Class E (Commercial, Business and Service) - Retail shop, no hot food preparation',
              'Class B2 (General Industrial) - Light manufacturing only, no chemicals',
              'Class F1 (Learning) - Educational use, max 50 students at a time'
            ],
            exampleHint: 'Specify UK use class and any operational restrictions'
          },
          {
            id: 'startDate',
            type: 'date',
            question: 'When does the lease begin?',
            validation: { required: true },
            examples: ['2025-03-25', '2025-04-01', '2025-06-24'],
            exampleHint: 'Common lease commencement dates (quarter days preferred)'
          },
          {
            id: 'leaseTerm',
            type: 'select',
            question: 'What is the lease term?',
            options: ['1 year', '3 years', '5 years', '10 years', '15 years', '20 years', '25 years'],
            validation: { required: true }
          },
          {
            id: 'securityOfTenure',
            type: 'radio',
            question: 'Will the lease be protected under Landlord and Tenant Act 1954?',
            options: ['Yes - Protected (security of tenure)', 'No - Contracted out (Section 38 notice)'],
            validation: { required: true },
            description: 'Protected leases give tenants automatic right to renew. Contracting out requires formal notice and tenant declaration.'
          },
          {
            id: 'rentAmount',
            type: 'number',
            question: 'What is the annual rent? (in GBP)',
            validation: { required: true, min: 0 },
            examples: ['35000', '52500', '120000', '28000', '75000'],
            exampleHint: 'Annual rent amount for commercial properties'
          },
          {
            id: 'rentPaymentFrequency',
            type: 'select',
            question: 'How is rent paid?',
            options: ['Quarterly in advance', 'Monthly in advance', 'Annually in advance'],
            validation: { required: true }
          },
          {
            id: 'rentReview',
            type: 'select',
            question: 'How will rent be reviewed?',
            options: ['Annual RPI increase', 'Market review every 3 years', 'Market review every 5 years', 'Fixed increase', 'No review'],
            validation: { required: true }
          },
          {
            id: 'rentReviewMechanism',
            type: 'text',
            question: 'Describe the rent review mechanism and any assumptions:',
            validation: { required: false },
            description: 'Include assumptions (e.g., vacant possession, willing landlord/tenant) and procedure for appointing surveyors.',
            examples: [
              'Open market rent review every 5 years with upward-only provision, vacant possession assumed',
              'Annual RPI increase capped at 3%, review date anniversary of lease commencement',
              'Market review at year 5, RICS surveyor appointment if parties disagree, upward and downward provision'
            ],
            exampleHint: 'Specify review triggers, assumptions, and appointment procedures'
          },
          {
            id: 'serviceCharge',
            type: 'radio',
            question: 'Is there a service charge?',
            options: ['Yes', 'No'],
            validation: { required: true }
          },
          {
            id: 'serviceChargeDetails',
            type: 'text',
            question: 'Describe service charge items and estimated annual amount:',
            validation: { required: false },
            description: 'Include items covered (maintenance, insurance, management, utilities) and tenant\'s percentage contribution.',
            examples: [
              '£3,500/year covering building insurance, common area maintenance, refuse collection - tenant pays 15% of total',
              '£5,200 annually: structural repairs, landscaping, security, management fees - 20% contribution',
              '£2,800 per annum: exterior maintenance, communal lighting, cleaning - pro-rata by floor area'
            ],
            exampleHint: 'Include covered items, amount, and calculation method'
          },
          {
            id: 'deposit',
            type: 'number',
            question: 'What is the rent deposit amount? (in GBP)',
            validation: { required: false, min: 0 },
            description: 'Typically 3-6 months\' rent for commercial leases.',
            examples: ['8750', '13125', '15000', '10000'],
            exampleHint: 'Usually 3-6 months of annual rent'
          },
          {
            id: 'breakClause',
            type: 'radio',
            question: 'Is there a break clause?',
            options: ['Yes - Tenant break', 'Yes - Landlord break', 'Yes - Mutual break', 'No'],
            validation: { required: true }
          },
          {
            id: 'breakClauseTerms',
            type: 'text',
            question: 'Specify break clause terms (when exercisable, notice period, conditions):',
            validation: { required: false },
            examples: [
              'Tenant may break after year 3 with 6 months\' notice, no arrears condition',
              'Mutual break at year 5, conditional on no outstanding breaches, 9 months\' notice required',
              'Tenant break at end of year 2, 4 and 6, 6 months written notice, all rent paid up to date'
            ],
            exampleHint: 'State timing, notice period, and conditions'
          },
          {
            id: 'repairObligation',
            type: 'select',
            question: 'What is the repair obligation structure?',
            options: ['Full Repairing and Insuring (FRI)', 'Internal Repairing Only', 'Landlord maintains structure and exterior', 'Schedule of condition'],
            validation: { required: true },
            description: 'FRI leases require tenant to maintain entire property. Schedule of condition limits liability to current state.'
          },
          {
            id: 'alterations',
            type: 'select',
            question: 'What alterations are permitted?',
            options: ['None without consent', 'Non-structural with consent (not to be unreasonably withheld)', 'Structural prohibited, non-structural with consent', 'As specified in schedule'],
            validation: { required: true }
          },
          {
            id: 'alienation',
            type: 'select',
            question: 'What are the alienation provisions?',
            options: ['Assignment permitted with landlord consent', 'Assignment prohibited', 'Subletting permitted with consent', 'No assignment or subletting', 'Group company sharing permitted'],
            validation: { required: true },
            description: 'Alienation controls assignment, subletting, sharing occupation, and charging the lease.'
          },
          {
            id: 'insurance',
            type: 'select',
            question: 'Who arranges buildings insurance?',
            options: ['Landlord insures, tenant reimburses', 'Tenant insures directly', 'Multi-let building - landlord insures'],
            validation: { required: true }
          },
          {
            id: 'insuranceRent',
            type: 'number',
            question: 'What is the estimated annual insurance premium? (in GBP)',
            validation: { required: false, min: 0 }
          },
          {
            id: 'userClause',
            type: 'text',
            question: 'Specify the user clause and any restrictions on hours/operations:',
            validation: { required: true }
          },
          {
            id: 'yieldUpCondition',
            type: 'text',
            question: 'In what condition must premises be yielded up at lease end?',
            validation: { required: true },
            description: 'Typically: good repair, decorated, all alterations removed or regularized, vacant possession.'
          },
          {
            id: 'guarantor',
            type: 'radio',
            question: 'Is a guarantor required?',
            options: ['Yes', 'No'],
            validation: { required: true }
          },
          {
            id: 'guarantorDetails',
            type: 'text',
            question: 'Provide guarantor name and address:',
            validation: { required: false }
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'commercial lease agreements',
      'Generate a comprehensive UK commercial lease agreement with essential business letting clauses. Include: property description and demise, lease term and commencement, rent and payment terms, rent review provisions, repairing obligations, insurance arrangements, permitted use and restrictions, alienation provisions (assignment, subletting), alterations policy, service charge (if applicable), break clause terms (if applicable), landlord and tenant covenants, forfeiture and re-entry rights, notices provisions, and dispute resolution. Incorporate ALL form data.',
      'Generate an enhanced UK commercial lease agreement with comprehensive business protections. Include detailed provisions for: premises demise with plans, term and security of tenure under Landlord & Tenant Act 1954, rent and rent review with market assumptions and surveyor appointment procedures, comprehensive repairing obligations (FRI or modified), insurance with reinstatement provisions and rent suspension, service charge with transparent accounting and challenge mechanisms, permitted use with change of use procedures, alienation with authorized guarantee agreements (AGAs), alterations with landlord consent procedures and reinstatement obligations, break clause with strict compliance requirements, yield up and dilapidations, guarantor covenants, forfeiture with relief provisions, and schedules (property description, services, insurance). Incorporate ALL form data.',
      'Generate a premium professional-grade UK commercial lease agreement with exhaustive commercial provisions. Include comprehensive clauses: detailed premises description with annexed plans and measurements, term commencement with rent-free period (if applicable), security of tenure analysis (protected/contracted out with Section 38 documentation), principal rent with VAT provisions, rent review with RICS-compliant procedures (upward-only, hypothetical lease terms, assumptions, disregards, dispute resolution via independent expert/arbitration), comprehensive service charge with specification, certification, and tenant challenge rights, insurance rent with full reinstatement cost, repair covenant (full repairing and insuring with schedule of condition protection), decoration requirements, alterations regime (structural prohibited, non-structural with qualified consent, reinstatement obligations), permitted use with hours restrictions, alienation provisions (assignment with conditions precedent, AGAs, privity of contract releases, subletting prohibitions/permissions, charging, sharing occupation), break clause with strict preconditions and timing, forfeiture with Section 146 notice requirements, guarantor covenants with principal debtor provisions, landlord covenants (quiet enjoyment, insurance, services), yield up conditions and dilapidations protocol, dispute resolution (mediation, arbitration, expert determination), notices provisions, interpretation clauses, and comprehensive schedules (property plan, services specification, permitted use details, rent review memorandum, guarantor deed). Incorporate ALL form data with professional commercial lease formatting, definitions section, and cross-references.'
    )
  },
  'deposit-protection': {
    formSections: {
      details: {
        id: 'details',
        title: 'Tenancy Deposit Protection Notice',
        fields: [
          {
            id: 'landlordName',
            type: 'text',
            question: 'What is the landlord\'s full name?',
            validation: { required: true },
            examples: ['John Smith', 'Property Management Ltd', 'Sarah Williams'],
            exampleHint: 'Landlord name as on tenancy agreement'
          },
          {
            id: 'landlordAddress',
            type: 'text',
            question: 'What is the landlord\'s correspondence address?',
            validation: { required: true },
            examples: ['15 Oak Avenue, Manchester, M15 6BH', 'Flat 3, The Maples, Bristol, BS1 2AA'],
            exampleHint: 'Full postal address for the landlord'
          },
          {
            id: 'agentDetails',
            type: 'text',
            question: 'If managed by a letting agent, what is the agent\'s name and address?',
            validation: { required: false },
            description: 'Leave blank if the landlord manages the property directly.',
            examples: ['ABC Lettings Ltd, 100 High Street, London, EC1A 2BB', 'N/A - self-managed by landlord'],
            exampleHint: 'Agent company name and address, or leave blank'
          },
          {
            id: 'tenantName',
            type: 'text',
            question: 'What is the tenant\'s full name?',
            validation: { required: true },
            examples: ['Emma Johnson', 'Michael Chen', 'Dr. Rebecca Taylor'],
            exampleHint: 'Tenant name as on tenancy agreement'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the property address?',
            validation: { required: true },
            examples: ['42 High Street, London, SW1A 1AA', 'Flat 5B, Riverside Tower, Leeds, LS1 4AP'],
            exampleHint: 'Full property address from tenancy agreement'
          },
          {
            id: 'tenancyStartDate',
            type: 'date',
            question: 'When did the tenancy start?',
            validation: { required: true },
            description: 'The deposit must be protected within 30 days of the tenancy start date.'
          },
          {
            id: 'depositAmount',
            type: 'number',
            question: 'What is the deposit amount? (in GBP)',
            validation: { required: true, min: 0 },
            description: 'Maximum 5 weeks\' rent for annual rent under £50,000, or 6 weeks\' rent if annual rent is £50,000 or more.',
            examples: ['1442', '980', '2423', '1615'],
            exampleHint: 'Deposit amount held (maximum 5 weeks rent)'
          },
          {
            id: 'depositReceivedDate',
            type: 'date',
            question: 'When was the deposit received from the tenant?',
            validation: { required: true },
            description: 'Critical date - the 30-day protection deadline is calculated from this date. Failure to protect within 30 days can result in penalties of 1-3x the deposit amount.'
          },
          {
            id: 'depositScheme',
            type: 'select',
            question: 'Which government-approved deposit protection scheme is being used?',
            options: ['Deposit Protection Service (DPS) - Custodial', 'MyDeposits - Insurance', 'MyDeposits - Custodial', 'Tenancy Deposit Scheme (TDS) - Insurance', 'Tenancy Deposit Scheme (TDS) - Custodial'],
            validation: { required: true },
            description: 'Custodial schemes hold the deposit directly. Insurance schemes allow the landlord to hold the deposit but require paying a premium for protection.'
          },
          {
            id: 'protectionDate',
            type: 'date',
            question: 'When was the deposit protected with the scheme?',
            validation: { required: true },
            description: 'Must be within 30 days of receiving the deposit. The prescribed information must also be served within this 30-day window.',
            examples: ['2025-03-15', '2025-04-20', '2025-05-10'],
            exampleHint: 'Must be within 30 days of receiving deposit'
          },
          {
            id: 'certificateNumber',
            type: 'text',
            question: 'What is the deposit protection certificate/reference number?',
            validation: { required: true },
            examples: ['DPS-123456789', 'TDS-00987654', 'MYD-456123789'],
            exampleHint: 'Reference number from the deposit protection scheme'
          },
          {
            id: 'schemeContactDetails',
            type: 'text',
            question: 'What are the deposit scheme\'s contact details for disputes?',
            validation: { required: true },
            description: 'Tenants must be told how to contact the scheme for dispute resolution.',
            examples: [
              'DPS: The Pavilions, Bridgwater Road, Bristol BS99 6AA. Tel: 0330 303 0030. www.depositprotection.com',
              'TDS: PO Box 1255, Hemel Hempstead, HP1 9GN. Tel: 0300 037 1000. www.tenancydepositscheme.com',
              'MyDeposits: Premiere House, 1st Floor, Elstree Way, Borehamwood, WD6 1JH. Tel: 0333 321 9401. www.mydeposits.co.uk'
            ],
            exampleHint: 'Address, phone, and website for the chosen scheme'
          },
          {
            id: 'deductionCircumstances',
            type: 'textarea',
            question: 'Under what circumstances may deductions be made from the deposit?',
            validation: { required: true },
            rows: 4,
            placeholder: 'List the specific circumstances under which the landlord may claim against the deposit...',
            description: 'This is a legal requirement under the prescribed information rules. You must tell the tenant the specific reasons deductions might be made.',
            examples: [
              '1. Unpaid rent or other charges due under the tenancy agreement. 2. Damage to the property or its contents beyond fair wear and tear (evidenced by check-in/check-out inventory comparison). 3. Missing items from the inventory. 4. Cleaning costs if property not returned to the standard documented at check-in. 5. Replacement of lost keys or security devices. 6. Any outstanding utility bills the tenant is responsible for under the agreement.',
              '1. Rent arrears at end of tenancy. 2. Property damage beyond reasonable wear and tear as documented in the inventory report. 3. Cleaning required beyond that noted at check-in. 4. Garden restoration if not maintained per agreement terms. 5. Missing or damaged inventory items. 6. Key replacement costs.'
            ],
            exampleHint: 'List all circumstances clearly - this is a legal requirement'
          },
          {
            id: 'prescribedInfoServed',
            type: 'radio',
            question: 'Has the prescribed information been provided to the tenant within 30 days?',
            options: ['Yes - served within 30 days of deposit receipt', 'Not yet - will be served with this notice'],
            validation: { required: true },
            description: 'You MUST serve the prescribed information within 30 days of receiving the deposit. This includes: scheme details, landlord/agent contact, property address, deposit amount, how to apply for release, scheme dispute procedure, and circumstances for deductions.'
          },
          {
            id: 'monthlyRent',
            type: 'number',
            question: 'What is the monthly rent amount? (in GBP)',
            validation: { required: true, min: 0 },
            description: 'Used to verify the deposit does not exceed the legal maximum (5 weeks\' rent).',
            examples: ['1250', '850', '2100'],
            exampleHint: 'Monthly rent to verify deposit cap compliance'
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'tenancy deposit protection notices',
      'Generate a comprehensive tenancy deposit protection notice complying with the Housing Act 2004 prescribed information requirements. Include: landlord details (name, address, agent if applicable), tenant details, property address, deposit amount and protection date, scheme details with contact information, certificate number, circumstances for deductions, dispute resolution procedure, and tenant rights. Verify deposit cap compliance (5 weeks rent). Incorporate ALL form data.',
      'Generate an enhanced tenancy deposit protection notice with all prescribed information requirements, detailed deduction circumstances with fair wear and tear guidance, step-by-step dispute resolution procedures for the specific scheme chosen, deposit return timescales, penalty warnings for non-compliance (1-3x deposit), tenant challenge rights, and a compliance checklist confirming all legal obligations met. Include clear formatting with numbered sections. Incorporate ALL form data.',
      'Generate a premium professional tenancy deposit protection notice pack including: (1) Prescribed Information Notice with all Housing Act 2004 requirements (landlord/agent details, property, deposit amount, protection scheme with certificate number, contact details, deduction circumstances, dispute procedure), (2) Deposit Protection Compliance Checklist (received date, protection date, prescribed info served date, 30-day deadline verification), (3) Tenant Rights Summary explaining: how to check deposit is protected, how to raise disputes, free scheme dispute resolution vs court, penalty claims for non-protection (1-3x deposit amount), time limits for claims, (4) Deposit Return Guide explaining end-of-tenancy process, inventory comparison, proposed deductions procedure, negotiation period, scheme adjudication process. Professional legal formatting. Incorporate ALL form data.'
    )
  },
  'section8-ground8': {
    formSections: {
      details: {
        id: 'details',
        title: 'Section 8 Notice - Ground 8 (Serious Rent Arrears)',
        fields: [
          {
            id: 'landlordName',
            type: 'text',
            question: 'What is the landlord\'s full name?',
            validation: { required: true },
            examples: ['John Smith', 'Property Management Ltd'],
            exampleHint: 'Name as on the tenancy agreement'
          },
          {
            id: 'landlordAddress',
            type: 'text',
            question: 'What is the landlord\'s correspondence address?',
            validation: { required: true },
            examples: ['15 Oak Avenue, Manchester, M15 6BH'],
            exampleHint: 'Full postal address'
          },
          {
            id: 'tenantName',
            type: 'text',
            question: 'What is the tenant\'s full name?',
            validation: { required: true },
            examples: ['Emma Johnson', 'Michael Chen'],
            exampleHint: 'Name as on the tenancy agreement'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the property address?',
            validation: { required: true },
            examples: ['42 High Street, London, SW1A 1AA'],
            exampleHint: 'Full property address including postcode'
          },
          {
            id: 'tenancyType',
            type: 'select',
            question: 'What type of tenancy is in place?',
            options: ['Assured Shorthold Tenancy (AST)', 'Assured Tenancy', 'Periodic Tenancy (post-2025 reform)'],
            validation: { required: true },
            description: 'Ground 8 applies to assured tenancies (including ASTs). The tenancy type affects the notice form and procedure.'
          },
          {
            id: 'tenancyStartDate',
            type: 'date',
            question: 'When did the tenancy start?',
            validation: { required: true },
            description: 'Needed to establish the tenancy history and calculate arrears periods.'
          },
          {
            id: 'rentAmount',
            type: 'number',
            question: 'What is the monthly rent amount? (in GBP)',
            validation: { required: true, min: 0 },
            examples: ['1250', '850', '2100'],
            exampleHint: 'Monthly rent as stated in tenancy agreement'
          },
          {
            id: 'rentPaymentFrequency',
            type: 'select',
            question: 'How often is rent due?',
            options: ['Monthly', 'Weekly', 'Fortnightly', 'Quarterly'],
            validation: { required: true },
            description: 'Ground 8 requires at least 8 weeks\' arrears (if paid weekly/fortnightly) or 2 months\' arrears (if paid monthly/quarterly) at both the date of notice service AND the date of the court hearing.'
          },
          {
            id: 'arrearsAmount',
            type: 'number',
            question: 'What is the total amount of rent arrears? (in GBP)',
            validation: { required: true, min: 0 },
            description: 'Must be at least 2 months\' rent (for monthly payers) or 8 weeks\' rent (for weekly payers) to qualify for Ground 8.',
            examples: ['3750', '5100', '2500'],
            exampleHint: 'Total unpaid rent as of today'
          },
          {
            id: 'arrearsStartDate',
            type: 'date',
            question: 'From what date did the arrears begin accumulating?',
            validation: { required: true },
            description: 'The date the first missed or short payment occurred.'
          },
          {
            id: 'lastPaymentDate',
            type: 'date',
            question: 'When was the last rent payment received?',
            validation: { required: true }
          },
          {
            id: 'lastPaymentAmount',
            type: 'number',
            question: 'What was the amount of the last payment received? (in GBP)',
            validation: { required: false, min: 0 },
            description: 'Helps establish whether partial payments have been made.'
          },
          {
            id: 'previousCommunications',
            type: 'textarea',
            question: 'Summarise previous communications with the tenant about the arrears:',
            validation: { required: true },
            rows: 4,
            placeholder: 'List dates and methods of contact: reminder letters, emails, phone calls, text messages, and the tenant\'s responses...',
            description: 'Courts expect landlords to have attempted to resolve arrears before serving a Section 8 notice. This demonstrates good practice.',
            examples: [
              '15/01/2025: Email sent reminding of overdue rent. No response. 01/02/2025: Phone call - tenant said would pay by end of week, did not. 10/02/2025: Formal letter sent via recorded delivery requesting payment plan. 20/02/2025: Tenant responded saying experiencing financial difficulties. 01/03/2025: Proposed payment plan of £200/month on top of rent. Tenant did not respond or make payment.',
              '3 reminder emails sent (January, February, March). 1 phone call in February - tenant acknowledged debt. 1 formal letter before action sent 15 March with 14-day deadline. No payment received. Tenant has not engaged with offers of support or payment plans.'
            ],
            exampleHint: 'Chronological list of all contact attempts and outcomes'
          },
          {
            id: 'preActionProtocol',
            type: 'radio',
            question: 'Has the Pre-Action Protocol for Possession Claims been followed?',
            options: ['Yes - all steps completed', 'In progress', 'Not yet started'],
            validation: { required: true },
            description: 'Courts expect compliance with the Pre-Action Protocol including: early contact, signposting to debt advice (e.g., Citizens Advice, StepChange), considering a payment plan, and serving a formal letter before action.'
          },
          {
            id: 'noticeDate',
            type: 'date',
            question: 'What date should appear on this Section 8 notice?',
            validation: { required: true },
            description: 'The notice must give at least 2 weeks (14 days) before court proceedings can begin for Ground 8.'
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'Section 8 Notices (Ground 8)',
      'Generate a Section 8 Notice for Ground 8 (serious rent arrears) complying with the Housing Act 1988. Include: landlord and tenant details, property address, tenancy type, Ground 8 particulars with arrears calculation, rent payment history, required 2-week notice period, and court proceedings warning. Verify the arrears meet the Ground 8 threshold (2 months for monthly, 8 weeks for weekly). Incorporate ALL form data.',
      'Generate an enhanced Section 8 Notice for Ground 8 with: formal notice in prescribed format, detailed arrears schedule (period-by-period breakdown), Ground 8 legal requirements explained, notice period calculation (earliest hearing date), pre-action protocol compliance confirmation, signposting to tenant debt advice services, payment plan offer, and covering letter explaining next steps if arrears are not cleared. Incorporate ALL form data.',
      'Generate a premium Section 8 Notice for Ground 8 with comprehensive court-ready documentation including: (1) Prescribed Form notice with Ground 8 particulars and arrears calculation, (2) Detailed rent arrears schedule with period-by-period breakdown, (3) Chronological communication log as evidence of attempts to resolve, (4) Pre-Action Protocol compliance statement, (5) Covering letter to tenant with debt advice signposting (Citizens Advice, StepChange, Shelter), (6) Guidance note on Ground 8 requirements (mandatory ground, arrears must be maintained at both notice date and hearing date, no discretion for court if proven), (7) Evidence bundle checklist for court proceedings. Professional legal formatting. Incorporate ALL form data.'
    )
  },
  'mediation-agreement': {
    formSections: {
      details: {
        id: 'details',
        title: 'Mediation Agreement Template',
        fields: [
          {
            id: 'party1Name',
            type: 'text',
            question: 'What is the first party\'s full name?',
            validation: { required: true },
            examples: ['John Smith (landlord)', 'Property Management Ltd'],
            exampleHint: 'Name of the first party in the dispute'
          },
          {
            id: 'party1Role',
            type: 'select',
            question: 'What is the first party\'s role?',
            options: ['Landlord', 'Tenant', 'Letting agent', 'Guarantor'],
            validation: { required: true }
          },
          {
            id: 'party1Contact',
            type: 'text',
            question: 'What is the first party\'s email address or phone number?',
            validation: { required: true },
            examples: ['john.smith@email.com', '07700 900123'],
            exampleHint: 'Primary contact for mediation correspondence'
          },
          {
            id: 'party2Name',
            type: 'text',
            question: 'What is the second party\'s full name?',
            validation: { required: true },
            examples: ['Emma Johnson (tenant)', 'Michael Chen'],
            exampleHint: 'Name of the second party in the dispute'
          },
          {
            id: 'party2Role',
            type: 'select',
            question: 'What is the second party\'s role?',
            options: ['Landlord', 'Tenant', 'Letting agent', 'Guarantor'],
            validation: { required: true }
          },
          {
            id: 'party2Contact',
            type: 'text',
            question: 'What is the second party\'s email address or phone number?',
            validation: { required: false },
            examples: ['emma.johnson@email.com', '07700 900456'],
            exampleHint: 'Primary contact, if known'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the property address related to the dispute?',
            validation: { required: true },
            examples: ['42 High Street, London, SW1A 1AA'],
            exampleHint: 'Full property address including postcode'
          },
          {
            id: 'tenancyReference',
            type: 'text',
            question: 'What is the tenancy agreement reference or start date?',
            validation: { required: false },
            examples: ['AST dated 01/06/2024', 'Ref: TEN-2024-0456', 'Tenancy commenced 15 March 2024'],
            exampleHint: 'Reference number or tenancy start date'
          },
          {
            id: 'disputeType',
            type: 'select',
            question: 'What type of dispute is being mediated?',
            options: ['Rent arrears', 'Property damage', 'Deposit deductions', 'Disrepair / maintenance failure', 'Lease breach', 'Anti-social behaviour', 'Harassment / illegal eviction', 'Rent increase dispute', 'Other'],
            validation: { required: true }
          },
          {
            id: 'disputeDescription',
            type: 'textarea',
            question: 'Briefly describe the dispute and key issues to be resolved:',
            validation: { required: true },
            rows: 4,
            placeholder: 'Summarise what the dispute is about, when it started, and what each party wants...',
            description: 'A clear summary helps the mediator prepare and ensures both parties understand what will be discussed.',
            examples: [
              'Tenant claims landlord has failed to address persistent damp and mould in the bathroom and bedroom 2 despite multiple reports since October 2024. Landlord states tenant has not been using ventilation properly. Tenant is withholding partial rent (£200/month) since January 2025. Both parties want to resolve without court proceedings.',
              'Dispute over deposit deductions at end of tenancy. Landlord claims £1,200 for carpet staining, wall damage, and cleaning. Tenant disputes all deductions, stating damage is fair wear and tear after 3-year tenancy. Deposit scheme (DPS) has offered free adjudication but both parties prefer mediation first.'
            ],
            exampleHint: 'What happened, when, and what each party wants'
          },
          {
            id: 'financialValue',
            type: 'number',
            question: 'What is the approximate financial value of the dispute? (in GBP)',
            validation: { required: false, min: 0 },
            description: 'Helps determine the proportionate level of mediation service needed.',
            examples: ['1200', '3500', '800'],
            exampleHint: 'Total amount in dispute'
          },
          {
            id: 'keyIssues',
            type: 'checkbox',
            question: 'What are the key issues to be resolved in mediation?',
            options: ['Financial settlement / payment', 'Property repairs or improvements', 'Tenancy continuation terms', 'Deposit return amount', 'Behavioural changes', 'Communication improvements', 'Tenancy termination terms', 'Compensation claim', 'Other'],
            validation: { required: true }
          },
          {
            id: 'mediationFormat',
            type: 'select',
            question: 'What format will the mediation take?',
            options: ['In-person meeting', 'Online video call', 'Telephone mediation', 'Shuttle mediation (parties in separate rooms)'],
            validation: { required: true },
            description: 'Shuttle mediation can be helpful when parties are hostile or there is a power imbalance.'
          },
          {
            id: 'mediatorName',
            type: 'text',
            question: 'Who is the appointed mediator (if known)?',
            validation: { required: false },
            examples: ['Jane Mitchell (CEDR accredited)', 'To be appointed by Property Redress Scheme', 'TDS Adjudicator'],
            exampleHint: 'Name and credentials if known, or appointing body'
          },
          {
            id: 'mediationDate',
            type: 'date',
            question: 'When is the mediation scheduled or proposed?',
            validation: { required: true }
          },
          {
            id: 'confidentialityAgreed',
            type: 'radio',
            question: 'Do both parties agree that mediation discussions will be confidential?',
            options: ['Yes - standard confidentiality', 'To be discussed at mediation'],
            validation: { required: true },
            description: 'Mediation discussions are typically confidential and cannot be used as evidence in court if mediation fails. This encourages open and honest negotiation.'
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'mediation agreements',
      'Generate a comprehensive mediation agreement for a landlord-tenant dispute. Include: party details with roles and contact information, property reference, dispute summary, agreed mediation format, mediator details, confidentiality agreement, ground rules, process outline, and provision for recording any settlement reached. Incorporate ALL form data.',
      'Generate an enhanced mediation agreement with: party identification and representation details, dispute background and chronology, key issues schedule, confidentiality provisions with exceptions, ground rules (respectful communication, no interruptions, good faith participation), mediation process outline (opening statements, issue identification, negotiation, private sessions if needed, settlement discussion), mediator role and authority, costs allocation, settlement recording procedure, and post-mediation follow-up provisions. Incorporate ALL form data.',
      'Generate a premium professional mediation agreement pack including: (1) Agreement to Mediate with comprehensive terms (parties, property, dispute description, confidentiality provisions with clear exceptions for illegal activity, mediator appointment and impartiality, ground rules, process structure), (2) Dispute Summary for mediator preparation (chronological background, key issues matrix, financial quantification, each party\'s position and interests, previous resolution attempts), (3) Mediation Process Guide explaining what to expect (opening, exploration, negotiation, private caucuses, agreement), (4) Settlement Agreement Template (if agreement is reached, with binding terms, implementation timeline, consequences of breach), (5) Post-Mediation Evaluation. Professional legal formatting suitable for formal dispute resolution. Incorporate ALL form data.'
    )
  },
  'rent-increase': {
    formSections: {
      details: {
        id: 'details',
        title: 'Rent Increase Notice',
        fields: [
          {
            id: 'landlordName',
            type: 'text',
            question: 'What is the landlord\'s full name?',
            validation: { required: true },
            examples: ['John Smith', 'Property Management Ltd', 'Sarah Williams'],
            exampleHint: 'Landlord name as on tenancy agreement'
          },
          {
            id: 'tenantName',
            type: 'text',
            question: 'What is the tenant\'s full name?',
            validation: { required: true },
            examples: ['Emma Johnson', 'Michael Chen', 'Dr. Rebecca Taylor'],
            exampleHint: 'Tenant name as on tenancy agreement'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the property address?',
            validation: { required: true },
            examples: ['42 High Street, London, SW1A 1AA', 'Flat 5B, Riverside Tower, Leeds, LS1 4AP'],
            exampleHint: 'Full property address from tenancy agreement'
          },
          {
            id: 'tenancyType',
            type: 'select',
            question: 'What type of tenancy is currently in place?',
            options: ['Periodic tenancy (rolling)', 'Fixed-term AST (with rent review clause)', 'Post-2025 periodic tenancy'],
            validation: { required: true },
            description: 'For periodic tenancies, a Section 13 notice (Form 4) is typically required. For fixed-term tenancies, rent can only be increased if the agreement includes a rent review clause.'
          },
          {
            id: 'lastRentIncreaseDate',
            type: 'date',
            question: 'When was the rent last increased (if applicable)?',
            validation: { required: false },
            description: 'Rent can generally only be increased once per year. Under the 2025 Renters\' Rights Bill reforms, there are additional restrictions on frequency and amount.'
          },
          {
            id: 'currentRent',
            type: 'number',
            question: 'What is the current monthly rent? (in GBP)',
            validation: { required: true, min: 0 },
            examples: ['1200', '950', '1750', '850'],
            exampleHint: 'Existing monthly rent amount'
          },
          {
            id: 'newRent',
            type: 'number',
            question: 'What is the proposed new monthly rent? (in GBP)',
            validation: { required: true, min: 0 },
            examples: ['1260', '985', '1820', '885'],
            exampleHint: 'Should reflect market rates (typically 3-7% increase)',
            smartDefaults: true
          },
          {
            id: 'effectiveDate',
            type: 'date',
            question: 'When will the new rent take effect?',
            validation: { required: true },
            description: 'A Section 13 notice requires at least 1 month notice for monthly tenancies, or 1 rental period for other periods. The increase takes effect at the start of a new rental period.',
            examples: ['2025-06-01', '2025-09-01', '2025-12-01'],
            exampleHint: 'Must align with a rent period start date'
          },
          {
            id: 'justification',
            type: 'textarea',
            question: 'What is the justification for the rent increase?',
            validation: { required: true },
            rows: 4,
            placeholder: 'Explain why the increase is reasonable: market comparisons, property improvements, inflation, service cost increases...',
            description: 'Under the 2025 reforms, tenants can challenge above-market increases at a First-tier Tribunal. Providing clear justification strengthens your position.',
            examples: [
              'Market rents for similar 2-bed properties in SW1 have increased from £1,200 to £1,350/month over the past 12 months (source: Rightmove average rents, March 2025). Proposed increase of £60/month (5%) is below market rate. Property improvements in past year: new boiler installed (£3,200), bathroom re-grouted. Running costs have increased: insurance up 15%, maintenance costs up 8%.',
              'Comparable properties: 14 High Street (same spec) - £1,400/month, 8 Oak Lane - £1,380/month, 22 Park Road - £1,350/month. Current rent of £1,200 is significantly below market rate. Inflation (CPI) has been 4.2% over the past year. Proposed increase of 5% brings rent closer to (but still below) local comparables.'
            ],
            exampleHint: 'Include specific evidence: comparable rents, improvements, inflation data'
          },
          {
            id: 'tenantRightsAcknowledged',
            type: 'radio',
            question: 'Are you aware the tenant has the right to challenge this increase?',
            options: ['Yes - I understand the tenant may apply to the First-tier Tribunal'],
            validation: { required: true },
            description: 'Under the Renters\' Rights Bill 2025, tenants can challenge rent increases at a First-tier Tribunal, which will determine whether the proposed rent exceeds market rate. The Tribunal can set a lower amount.'
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'rent increase notices',
      'Generate a compliant rent increase notice following UK tenancy law. Include: landlord and tenant details, property address, current and proposed rent, effective date, notice period verification, justification, and tenant rights information including the right to challenge at First-tier Tribunal. Format as a Section 13 notice where applicable. Incorporate ALL form data.',
      'Generate an enhanced rent increase notice with: formal Section 13 format (where applicable), detailed justification with market evidence, comparable property analysis, improvement and maintenance cost breakdown, clear calculation showing percentage increase, full tenant rights explanation (First-tier Tribunal challenge process, timescales, what happens during challenge), payment instruction for new amount, and professional covering letter. Incorporate ALL form data.',
      'Generate a premium rent increase notice pack including: (1) Formal Section 13 Notice (Form 4 where applicable) with all required particulars, (2) Market Evidence Report with comparable property analysis, area rent trends, and justification, (3) Property Improvement and Maintenance Schedule showing landlord investment, (4) Covering Letter explaining the increase in plain English with positive tone, (5) Tenant Rights Guide explaining: how to accept, how to negotiate, how to challenge at First-tier Tribunal (form, timescale, process, what Tribunal considers, potential outcomes), what happens to rent during a challenge, and advice services available. Professional formatting suitable for formal notice requirements. Incorporate ALL form data.'
    )
  },
  'inventory-report': {
    formSections: {
      details: {
        id: 'details',
        title: 'Inventory and Condition Report',
        fields: [
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the full address of the property being inventoried?',
            validation: { required: true },
            examples: ['42 High Street, London, SW1A 1AA', 'Flat 5B, Riverside Tower, Leeds, LS1 4AP'],
            exampleHint: 'Full property address including postcode'
          },
          {
            id: 'landlordName',
            type: 'text',
            question: 'What is the landlord\'s full name?',
            validation: { required: true },
            examples: ['John Smith', 'Property Management Ltd'],
            exampleHint: 'Name as appears on the tenancy agreement'
          },
          {
            id: 'tenantName',
            type: 'text',
            question: 'What is the tenant\'s full name?',
            validation: { required: true },
            examples: ['Emma Johnson', 'Michael Chen'],
            exampleHint: 'Name as appears on the tenancy agreement'
          },
          {
            id: 'inspectionDate',
            type: 'date',
            question: 'What is the date of this inventory inspection?',
            validation: { required: true },
            description: 'This date will be used as the official record of property condition at handover.'
          },
          {
            id: 'inspectionType',
            type: 'select',
            question: 'What type of inventory inspection is this?',
            options: ['Check-in (start of tenancy)', 'Check-out (end of tenancy)', 'Mid-tenancy review', 'Interim inspection'],
            validation: { required: true },
            description: 'Check-in inventories establish the baseline condition. Check-out inventories are compared against this baseline for deposit deductions.'
          },
          {
            id: 'inspectorName',
            type: 'text',
            question: 'Who is conducting this inventory inspection?',
            validation: { required: true },
            description: 'The person physically present at the property conducting the inspection.',
            examples: ['Sarah Williams (independent inventory clerk)', 'Landlord: John Smith', 'ABC Lettings Agent: David Brown'],
            exampleHint: 'Include role or company if applicable'
          },
          {
            id: 'propertyType',
            type: 'select',
            question: 'What type of property is this?',
            options: ['Detached house', 'Semi-detached house', 'Terraced house', 'End-terrace house', 'Flat/apartment', 'Maisonette', 'Studio flat', 'Bungalow', 'Converted flat', 'Purpose-built flat'],
            validation: { required: true }
          },
          {
            id: 'numBedrooms',
            type: 'number',
            question: 'How many bedrooms does the property have?',
            validation: { required: true, min: 0 },
            description: 'Include all rooms used as bedrooms, even if described differently in listings.'
          },
          {
            id: 'numBathrooms',
            type: 'number',
            question: 'How many bathrooms/shower rooms does the property have?',
            validation: { required: true, min: 1 },
            description: 'Include en-suites, main bathrooms, and separate WCs.'
          },
          {
            id: 'numReceptionRooms',
            type: 'number',
            question: 'How many reception rooms (living/dining rooms) are there?',
            validation: { required: true, min: 0 }
          },
          {
            id: 'furnishingStatus',
            type: 'select',
            question: 'What is the furnishing status of the property?',
            options: ['Fully furnished', 'Part furnished', 'Unfurnished'],
            validation: { required: true },
            description: 'Fully furnished includes beds, sofas, dining table, and white goods. Part furnished typically means white goods only. Unfurnished means bare property.'
          },
          {
            id: 'overallCleanliness',
            type: 'select',
            question: 'What is the overall cleanliness standard of the property?',
            options: ['Professionally cleaned', 'Domestic clean (good standard)', 'Acceptable but needs attention', 'Below acceptable standard'],
            validation: { required: true },
            description: 'This establishes the cleanliness baseline the tenant must return the property to at check-out.'
          },
          {
            id: 'overallCondition',
            type: 'select',
            question: 'What is the overall condition of the property?',
            options: ['Excellent (newly refurbished)', 'Good (well maintained)', 'Fair (some wear and tear)', 'Poor (needs repair work)'],
            validation: { required: true }
          },
          {
            id: 'meterElectricity',
            type: 'text',
            question: 'What is the electricity meter reading at inspection?',
            validation: { required: true },
            description: 'Record the exact reading shown on the electricity meter. Note the meter location.',
            examples: ['Reading: 45672 kWh (meter in hallway cupboard)', 'Smart meter: 12345.6 kWh (under stairs)', 'Economy 7 Day: 34521, Night: 12304 (meter in garage)'],
            exampleHint: 'Include reading and meter location'
          },
          {
            id: 'meterGas',
            type: 'text',
            question: 'What is the gas meter reading at inspection?',
            validation: { required: false },
            description: 'Record the exact reading. Leave blank if property has no gas supply.',
            examples: ['Reading: 1234 cubic metres (meter outside front of property)', 'Smart meter: 5678.9 m3 (kitchen utility cupboard)', 'N/A - all electric property'],
            exampleHint: 'Include reading and location, or state if all-electric'
          },
          {
            id: 'meterWater',
            type: 'text',
            question: 'What is the water meter reading at inspection (if metered)?',
            validation: { required: false },
            examples: ['Reading: 00123.456 m3 (front garden boundary)', 'Not metered - fixed rate water charges', 'Smart meter: 789.01 m3 (pavement outside)'],
            exampleHint: 'Include reading if metered, or state if unmetered'
          },
          {
            id: 'keysProvided',
            type: 'checkbox',
            question: 'Which keys and security items are being provided to the tenant?',
            options: ['Front door keys', 'Back door keys', 'Window keys', 'Garage/shed keys', 'Mailbox key', 'Alarm fob/code', 'Gate key/fob', 'Communal entrance fob/key', 'Storage unit key'],
            validation: { required: true },
            description: 'Record the exact number of each key type in the condition descriptions below. All keys must be returned at check-out.'
          },
          {
            id: 'keyQuantities',
            type: 'text',
            question: 'How many of each key type are being provided?',
            validation: { required: true },
            examples: ['2x front door, 2x back door, 1x mailbox, 1x window key set, 2x communal fobs', '3x front door (Yale + mortice), 1x garage remote, 1x shed padlock key'],
            exampleHint: 'List exact quantity for each key type'
          },
          {
            id: 'smokeAlarmLocations',
            type: 'text',
            question: 'Where are smoke alarms located and do they all work?',
            validation: { required: true },
            description: 'Test each alarm during inspection. Record location and test result.',
            examples: ['Hallway (ground floor) - tested OK, Kitchen (heat alarm) - tested OK, Landing (first floor) - tested OK', 'Ground floor hallway - working, First floor landing - working, Second floor landing - battery replaced and working'],
            exampleHint: 'Location and test result for each alarm'
          },
          {
            id: 'coAlarmLocations',
            type: 'text',
            question: 'Where are carbon monoxide alarms located and do they work?',
            validation: { required: false },
            description: 'Required in rooms with solid fuel appliances. Record location and test.',
            examples: ['Living room (near gas fire) - tested OK', 'Kitchen (near boiler) - tested OK, Bedroom 1 (near gas fire) - tested OK', 'N/A - no gas appliances or solid fuel burning'],
            exampleHint: 'Location and test result, or N/A if not required'
          },
          {
            id: 'hallwayCondition',
            type: 'textarea',
            question: 'Describe the condition of the hallway/entrance area:',
            validation: { required: true },
            rows: 4,
            placeholder: 'Describe: walls, ceiling, flooring, light fittings, doors, skirting boards, any furniture/fittings...',
            description: 'Include condition of walls (marks, scuffs, holes), ceiling, flooring type and condition, light fittings, doors, skirting boards, radiators, and any furniture or fittings.',
            examples: [
              'Walls: magnolia emulsion, good condition with minor scuff mark near front door (approx 5cm). Ceiling: white, clean, no cracks. Floor: laminate oak-effect, good condition with slight wear at entrance. Skirting: white gloss, good. Front door: UPVC white, working lock, slight scratch on inside panel. Radiator: white single panel, working. Light: pendant fitting with glass shade, working. Coat hooks x3 on wall, secure.',
              'Walls: grey feature wallpaper (left wall), white emulsion others - all good condition. Ceiling: white, small hairline crack above door frame. Floor: carpet (beige), recently cleaned, good. Mirror on wall (70x50cm), secure. Radiator: double panel white, thermostat working. Smoke alarm on ceiling - tested and working.'
            ],
            exampleHint: 'Cover walls, ceiling, floor, fixtures, and note any damage or wear'
          },
          {
            id: 'livingRoomCondition',
            type: 'textarea',
            question: 'Describe the condition of the living/reception room(s):',
            validation: { required: true },
            rows: 5,
            placeholder: 'Describe: walls, ceiling, flooring, windows, curtains/blinds, light fittings, fireplace, radiators, furniture (if furnished)...',
            description: 'Include condition of all surfaces, windows, curtains/blinds, light fittings, power sockets, TV aerial point, fireplace (if present), and all furniture items if furnished.',
            examples: [
              'Walls: cream emulsion throughout, good condition. Ceiling: white, ceiling rose in centre, good. Floor: carpet (grey), professional cleaned, good. Windows: 2x double-glazed UPVC, both open and lock correctly, venetian blinds (white) working. Curtains: grey lined curtains x2 pairs, clean, good. Radiator: double panel, thermostat working. Light: ceiling pendant + 2x wall lights, all working. TV aerial point on east wall. Fireplace: decorative Victorian surround, tiles intact. FURNITURE: 3-seater sofa (grey fabric, good condition, minor pilling on left arm), 2-seater sofa (matching, good), coffee table (oak effect, ring mark on surface), TV unit (white, good), bookshelf (4-tier, pine, good).',
              'Open-plan living/dining. Walls: white throughout, feature wall with exposed brick (sealed). Minor nail holes x3 on west wall. Ceiling: white, spotlights x6 all working. Floor: engineered wood (oak), good condition, small scratch near patio doors (10cm). Patio doors: double-glazed, sliding, lock working. Blinds: roller blinds (cream), good. Radiator x2: both working. FURNITURE: L-shaped sofa (charcoal), dining table (seats 4, oak), 4x dining chairs (grey upholstered, 1 has small stain on seat).'
            ],
            exampleHint: 'Detail every surface, fitting, and furniture item with condition'
          },
          {
            id: 'kitchenCondition',
            type: 'textarea',
            question: 'Describe the kitchen condition and list all appliances with their condition:',
            validation: { required: true },
            rows: 6,
            placeholder: 'Describe: worktops, cupboards, flooring, walls, splashback, taps, sink, and each appliance (oven, hob, extractor, fridge, freezer, dishwasher, washing machine, microwave, kettle, toaster)...',
            description: 'Detail every surface, fixture, and appliance. For each appliance note make/model if visible, condition, and whether tested working. Count cutlery, crockery, and cookware if furnished.',
            examples: [
              'Worktops: grey granite-effect laminate, good condition, minor knife mark near hob. Units: white gloss, 8 base + 6 wall units, all doors/drawers working, soft-close. Sink: stainless steel 1.5 bowl, mixer tap working (hot and cold), no drips. Splashback: white metro tiles, grouting clean. Floor: vinyl (grey slate effect), good, slight lifting at threshold. Walls: white emulsion, clean. APPLIANCES: Oven - Bosch HBN331E4B, electric, clean, tested working. Hob - Bosch PBP6B5B80, gas 4-burner, all burners working. Extractor - integrated, 3-speed, working. Fridge/freezer - Beko CFG3582W, freestanding, clean, working, slight dent on door. Dishwasher - Bosch SMV40C30GB, integrated, tested cycle OK. Washing machine - Samsung WW80T554DAW, good condition, working. Microwave - Russell Hobbs countertop, clean, working. CONTENTS: Cutlery set for 4, saucepan set x3, frying pan x1, baking tray x2, chopping board x1, kettle (Russell Hobbs), toaster (2-slice).',
              'Open kitchen. Worktops: solid wood (oak), recently oiled, good. Units: shaker-style grey, 10 base + 8 wall, one soft-close hinge needs adjustment on unit 3. Sink: Belfast ceramic, chrome mixer tap, working. Splashback: herringbone tiles (white), clean. Floor: terracotta tiles, good, grout clean. APPLIANCES: Range cooker - Rangemaster Classic 90, gas/electric, clean, all functions tested working. Integrated fridge - Bosch, working. Integrated freezer - Bosch, working, slight frost buildup. Dishwasher - Miele, integrated, working. Washing machine - in utility room, Miele W1, working. Extractor - chimney hood, 3-speed + boost, working but filter needs cleaning. UNFURNISHED - no crockery/cutlery provided.'
            ],
            exampleHint: 'List every appliance with make/model, condition, and test result'
          },
          {
            id: 'bedroomsCondition',
            type: 'textarea',
            question: 'Describe the condition of each bedroom (label as Bedroom 1, 2, etc.):',
            validation: { required: true },
            rows: 6,
            placeholder: 'For each bedroom describe: walls, ceiling, flooring, windows, curtains/blinds, light fittings, built-in storage, and furniture (if furnished: bed size, mattress condition, wardrobe, drawers, bedside tables)...',
            description: 'Detail each bedroom separately. Include all surfaces, windows, storage, and furniture. Note mattress condition, bed frame type, and any stains or damage.',
            examples: [
              'BEDROOM 1 (master, front): Walls: pale blue emulsion, good. Ceiling: white, good. Floor: carpet (cream), clean, good. Windows: 1x double-glazed, curtains (navy blackout, good). Radiator: working. Built-in wardrobe: sliding mirrored doors, rail and shelf inside, good. FURNITURE: Double bed frame (wooden, pine, good), mattress (memory foam, clean, no stains, mattress protector on), bedside tables x2 (white, good), chest of drawers (4-drawer, pine, good). BEDROOM 2 (rear): Walls: white, good. Ceiling: white, good. Floor: carpet (grey), good. Window: 1x double-glazed, blind (white roller, working). Radiator: working. FURNITURE: Single bed frame (metal, white, good), mattress (spring, clean), wardrobe (freestanding, pine, door slightly stiff), desk and chair (good).',
              'BEDROOM 1: Walls: feature wallpaper (geometric grey) behind bed, white emulsion others, good. Floor: engineered oak, good. Windows: 2x sash, both working, plantation shutters (white, all louvres working). En-suite door: painted white, handle secure. FURNITURE: King-size bed (upholstered grey, good), mattress (pocket sprung, very good, no stains), built-in wardrobes (fitted, white, 3 sections with hanging/shelves/drawers, good). BEDROOM 2: Walls: white, good. Floor: carpet (beige), good. Window: 1x casement, blackout roller blind. Wardrobe: built-in single, good. UNFURNISHED - no bed or furniture. BEDROOM 3 (box room): Walls: white, good. Floor: laminate, good. Window: 1x small casement, venetian blind. Currently set up as study - no furniture provided.'
            ],
            exampleHint: 'Describe each bedroom separately with all surfaces, storage, and furniture'
          },
          {
            id: 'bathroomsCondition',
            type: 'textarea',
            question: 'Describe the condition of each bathroom/shower room (label each one):',
            validation: { required: true },
            rows: 5,
            placeholder: 'For each bathroom describe: bath/shower, toilet, basin, taps, tiles, grouting, flooring, mirror, towel rail, extractor fan, and any accessories...',
            description: 'Detail each bathroom separately. Check for mould, silicone condition, tile grout, tap drips, toilet flush, shower pressure, and extractor fan operation.',
            examples: [
              'MAIN BATHROOM: Bath: white acrylic, good condition, no chips or stains. Shower over bath: Mira electric, working, good pressure. Shower screen: glass hinged, seals intact. Toilet: close-coupled, flushes correctly, seat secure. Basin: pedestal, white ceramic, good. Taps: chrome mixer on basin (no drips), bath mixer with shower attachment. Tiles: white wall tiles full height, all intact, grouting clean. Floor: vinyl (grey), good. Mirror: wall-mounted (60x40cm), secure. Towel rail: chrome heated, working. Extractor fan: working, runs on timer. Accessories: toilet roll holder, towel ring. EN-SUITE (off bedroom 1): Shower enclosure: quadrant, glass doors, tray good, silicone seal good. Toilet: wall-hung, good. Basin: wall-mounted, good. Tiles: grey wall and floor, good. Mirror: illuminated, working. Heated towel rail: working.',
              'BATHROOM: Walk-in shower with rainfall head and handheld, thermostatic mixer, tiled tray, glass screen - all good, silicone sealed well. Freestanding bath (roll-top), stand pipe taps (chrome), no stains. Toilet: back-to-wall, soft close seat, good. Basin: countertop on vanity unit, mixer tap, good. Tiles: marble-effect porcelain floor and walls, grouting good. Heated towel rail: working. Extractor fan: working. Small black mould spot in corner of ceiling near window - noted. DOWNSTAIRS WC: Toilet, small basin, tiled floor, all good.'
            ],
            exampleHint: 'Cover every fixture, check for mould/leaks, test taps and flush'
          },
          {
            id: 'gardenExterior',
            type: 'textarea',
            question: 'Describe the condition of any garden, patio, or exterior areas:',
            validation: { required: false },
            rows: 4,
            placeholder: 'Describe: front garden, rear garden, patio, decking, shed, fencing, gates, paths, bins, external lighting, parking...',
            description: 'Include gardens, patios, balconies, decking, sheds, fencing, gates, paths, parking areas, bins, and external lighting. Note garden maintenance standard.',
            examples: [
              'FRONT: Small paved front garden, low brick wall boundary, gate (iron, working but squeaks). Wheelie bin storage area (2 bins provided: general waste + recycling). REAR: Lawn (approx 5x8m, recently mown, good), patio area (concrete slabs, 3x3m, good), flower bed borders (planted, maintained). Fence: wooden panel 6ft, good condition all sides. Shed: wooden (6x4ft), padlock key provided, contains lawn mower and garden tools. External tap: working. Security light: PIR sensor, working. Washing line: rotary, in ground socket.',
              'BALCONY ONLY: Juliet balcony with glass balustrade off living room, good condition. No external garden. Communal bin store accessed via fob (included with keys). Allocated parking space #12 in underground car park.',
              'N/A - mid-floor flat with no private external space. Communal grounds maintained by management company.'
            ],
            exampleHint: 'Describe all external areas, or state N/A if no private outdoor space'
          },
          {
            id: 'existingDamage',
            type: 'textarea',
            question: 'List all pre-existing damage, defects, or areas of concern:',
            validation: { required: true },
            rows: 4,
            placeholder: 'List every item of damage, wear, or defect found during inspection...',
            description: 'This is critical for protecting both landlord and tenant. Document every scratch, stain, mark, dent, chip, crack, or defect found. Include location, size, and severity.',
            examples: [
              '1. Hallway wall - scuff mark (5cm) near front door at waist height. 2. Living room coffee table - ring mark on surface (3cm diameter). 3. Kitchen worktop - knife cut mark near hob (8cm). 4. Bedroom 1 carpet - small stain near window (2cm, unknown origin). 5. Bathroom silicone - slight discolouration around bath seal (cosmetic only, watertight). 6. Garden fence panel - 1 panel slightly leaning (rear left corner). 7. Back door - paint chipping at bottom edge.',
              '1. No significant damage found. Property in very good condition throughout. 2. Minor wear consistent with normal use on carpet in high-traffic areas (hallway, stairs). 3. Small hairline crack in ceiling plaster above living room door frame (cosmetic, no structural concern). 4. One kitchen cupboard door hinge slightly loose (unit 3, top hinge).'
            ],
            exampleHint: 'Number each item with location, description, and size'
          },
          {
            id: 'heatingSystem',
            type: 'textarea',
            question: 'Describe the heating system and hot water:',
            validation: { required: true },
            rows: 3,
            placeholder: 'Boiler type, make/model, location, thermostat type and location, radiators tested...',
            description: 'Record boiler details, thermostat type and location, and confirm all radiators tested.',
            examples: [
              'Boiler: Worcester Bosch Greenstar 30i combi, located in kitchen cupboard, last serviced 15/01/2025 (Gas Safe cert provided). Thermostat: Hive smart thermostat in hallway, connected and working. Radiators: tested all 7 radiators - all heating correctly. Hot water: instant from combi, tested at kitchen and bathroom taps, good pressure and temperature.',
              'Boiler: Vaillant ecoTEC plus 832, combi, in airing cupboard on landing. Programmer: built-in Vaillant timer, set to twice daily. TRVs on all radiators except bathroom (towel rail). All 5 radiators tested - working. Bedroom 2 radiator slow to heat (may need bleeding). Hot water: tested OK at all outlets.'
            ],
            exampleHint: 'Include boiler make/model, location, thermostat type, and radiator test results'
          },
          {
            id: 'additionalRooms',
            type: 'textarea',
            question: 'Describe any additional rooms or areas not covered above:',
            validation: { required: false },
            rows: 4,
            placeholder: 'E.g., utility room, conservatory, loft space, garage, study, cloakroom, storage areas...',
            description: 'Include utility rooms, conservatories, lofts, garages, studies, cellars, storage cupboards, or any other spaces.',
            examples: [
              'UTILITY ROOM (off kitchen): Walls: white, good. Floor: vinyl, good. Contains washing machine (Samsung, working) and tumble dryer (Hotpoint, working). Wall-mounted drying rack. Sink with single cold tap, working. Door to garden. UNDER-STAIRS CUPBOARD: Shelved storage, electricity meter located here. Hoover and ironing board stored. LOFT: Access hatch on landing, boarded, loft ladder (aluminium, working). Partially used for storage, insulated. Light fitted and working.',
              'CONSERVATORY: UPVC frame, polycarbonate roof, good condition. Floor: tiled, good. Radiator: working. Blinds: pleated roof blind, working. Door to garden. GARAGE: Single garage, up-and-over door (manual), working. Light fitted. Shelving on back wall. Contains gas meter. Not included as habitable space.'
            ],
            exampleHint: 'Describe each additional area with surfaces, fixtures, and contents'
          },
          {
            id: 'tenantAcknowledgement',
            type: 'radio',
            question: 'Has the tenant been present during or reviewed this inventory?',
            options: ['Yes - tenant present during inspection', 'Yes - tenant reviewed and agreed to report', 'No - tenant not yet reviewed', 'Tenant declined to attend'],
            validation: { required: true },
            description: 'Tenant agreement with the inventory strengthens its legal standing for deposit disputes. Tenants typically have 7 days to raise any discrepancies.'
          },
          {
            id: 'photographicEvidence',
            type: 'radio',
            question: 'Have photographs been taken to support this inventory?',
            options: ['Yes - comprehensive photos taken of all rooms and items', 'Yes - photos of key items and any damage only', 'No - written report only'],
            validation: { required: true },
            description: 'Photographic evidence with timestamps significantly strengthens the inventory report. Photos should be dated and referenced to the written descriptions above.'
          },
          {
            id: 'additionalNotes',
            type: 'textarea',
            question: 'Any additional notes, observations, or special instructions:',
            validation: { required: false },
            rows: 3,
            placeholder: 'E.g., items left for tenant use but not included in inventory, maintenance appointments pending, special care instructions...',
            examples: [
              'Tenant provided with appliance manuals for boiler, oven, and dishwasher. Garden maintenance is tenant responsibility per tenancy agreement. Bins are collected on Thursdays (recycling) and Fridays (general waste). Window cleaner visits monthly - included in service charge.',
              'Property alarm code provided separately via secure message. Boiler service due October 2025 - landlord to arrange. Carpets professionally cleaned 01/03/2025 - receipt provided. Note: garage is for storage only, not vehicle parking (clearance too low).'
            ],
            exampleHint: 'Any relevant details not covered in the sections above'
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'inventory and condition reports',
      'Generate a comprehensive inventory and condition report for a UK tenancy. Structure as a professional room-by-room inventory document with: property details header, inspection information, meter readings section, keys and security schedule, then detailed room-by-room sections covering walls, ceilings, floors, windows, doors, fixtures, fittings, furniture (if furnished), and appliances. Include a pre-existing damage schedule, overall condition summary, and signature blocks for landlord and tenant acknowledgement. Use the detailed room descriptions provided in the form data to populate each section. Incorporate ALL form data.',
      'Generate an enhanced professional inventory and condition report with: cover page with property details, detailed methodology description, comprehensive room-by-room assessment using a structured format (item, description, condition rating from 1-5, notes), appliance schedule with make/model/serial numbers, meter readings with photographic evidence references, keys and security schedule, pre-existing damage log with severity ratings, cleaning standard assessment per room, health and safety checklist (smoke alarms, CO alarms, fire escape), overall property condition matrix, recommendations section, and dual-signature acknowledgement page with 7-day dispute window notice. Reference photographic evidence throughout. Incorporate ALL form data.',
      'Generate a premium professional-grade inventory and condition report suitable for deposit dispute evidence. Include: professional cover page, terms of reference, inspection methodology and standards (AIIC compliant), executive condition summary, comprehensive room-by-room inventory with structured assessment (item description, quantity, condition grading A-E with definitions, fair wear and tear assessment, estimated replacement value for furnished items, detailed notes with photographic cross-references), complete appliance register (make, model, serial number, age estimate, condition, PAT test status), utility meter schedule with readings and locations, keys and security items register with quantities, fire safety compliance checklist, health and safety assessment, pre-existing damage schedule with severity classification (cosmetic/functional/structural), overall property condition scorecard, cleaning standard assessment per room against professional clean benchmark, maintenance recommendations with urgency ratings, legal notices (tenant dispute window, deposit scheme reference), and signature page with declarations. Format as a court-admissible evidence document. Incorporate ALL form data with professional formatting and cross-references.'
    )
  },
  'witness-statement': {
    formSections: {
      details: {
        id: 'details',
        title: 'Witness Statement Template',
        fields: [
          {
            id: 'witnessName',
            type: 'text',
            question: 'What is the witness\'s full name?',
            validation: { required: true },
            examples: ['Sarah Anne Williams', 'David Robert Thompson'],
            exampleHint: 'Full legal name as it would appear on official documents'
          },
          {
            id: 'witnessAddress',
            type: 'text',
            question: 'What is the witness\'s address?',
            validation: { required: true },
            description: 'Required for the statement of truth. This address will appear on the court record.',
            examples: ['15 Oak Avenue, Manchester, M15 6BH'],
            exampleHint: 'Full home or business address'
          },
          {
            id: 'witnessOccupation',
            type: 'text',
            question: 'What is the witness\'s occupation?',
            validation: { required: true },
            examples: ['Landlord', 'Letting agent', 'Neighbour (retired teacher)', 'Property manager', 'Building surveyor'],
            exampleHint: 'Current occupation or role'
          },
          {
            id: 'witnessRelationship',
            type: 'select',
            question: 'What is the witness\'s relationship to the case?',
            options: ['Landlord (party to proceedings)', 'Tenant (party to proceedings)', 'Letting agent', 'Neighbour', 'Professional (surveyor, tradesperson, etc.)', 'Independent witness', 'Other'],
            validation: { required: true },
            description: 'The court needs to understand the witness\'s connection to the case and any potential bias.'
          },
          {
            id: 'caseNumber',
            type: 'text',
            question: 'What is the court case number (if proceedings have started)?',
            validation: { required: false },
            examples: ['B00CL123', 'E10CL456', 'Not yet assigned'],
            exampleHint: 'Case reference from court, or leave blank if pre-proceedings'
          },
          {
            id: 'courtName',
            type: 'text',
            question: 'Which court is the case being heard at (or will be filed at)?',
            validation: { required: false },
            examples: ['Central London County Court', 'Manchester Civil Justice Centre', 'Birmingham County Court'],
            exampleHint: 'County court name'
          },
          {
            id: 'claimantName',
            type: 'text',
            question: 'Who is the claimant (the person bringing the case)?',
            validation: { required: true },
            examples: ['John Smith', 'Property Management Ltd'],
            exampleHint: 'Full name of claimant as on court documents'
          },
          {
            id: 'defendantName',
            type: 'text',
            question: 'Who is the defendant (the person being claimed against)?',
            validation: { required: true },
            examples: ['Emma Johnson', 'Michael Chen'],
            exampleHint: 'Full name of defendant as on court documents'
          },
          {
            id: 'statementPurpose',
            type: 'select',
            question: 'What is the purpose of this witness statement?',
            options: ['Supporting possession claim', 'Defending possession claim', 'Deposit dispute evidence', 'Disrepair claim evidence', 'Anti-social behaviour evidence', 'Rent arrears proceedings', 'General landlord-tenant dispute', 'Other'],
            validation: { required: true }
          },
          {
            id: 'witnessBackground',
            type: 'textarea',
            question: 'Provide background about the witness and how they have knowledge of the matters in this case:',
            validation: { required: true },
            rows: 3,
            placeholder: 'Explain who you are, your connection to the property or parties, and why you have relevant knowledge...',
            description: 'The court needs to understand why this witness has relevant knowledge. Start with your role and connection to the property or tenancy.',
            examples: [
              'I am the landlord of the property at 42 High Street, London, SW1A 1AA. I have owned this property since 2018 and have managed the tenancy with the defendant directly. I make this statement from my own personal knowledge and from records I have maintained throughout the tenancy.',
              'I live at 44 High Street, directly next door to the property in question. I have lived at my address for 12 years and have direct knowledge of the noise and disturbance that has occurred at the neighbouring property since the defendant moved in on 1 June 2024.'
            ],
            exampleHint: 'Who you are, your connection to the case, and basis of your knowledge'
          },
          {
            id: 'chronologicalAccount',
            type: 'textarea',
            question: 'Provide a chronological account of the relevant events:',
            validation: { required: true },
            rows: 6,
            placeholder: 'List events in date order. For each event include: the date, what happened, who was involved, what you personally saw/heard/did...',
            description: 'This is the core of the witness statement. Present facts in chronological order. Only include matters within your own knowledge. Be specific about dates, times, and what you personally observed.',
            examples: [
              '1. On 1 June 2024, the defendant commenced the tenancy. A check-in inventory was completed on that date (copy attached as Exhibit JS1). 2. On 15 August 2024, I attended the property for a routine inspection. I observed the living room carpet had a large red stain (approximately 30cm diameter) that was not present at check-in. I photographed this (Exhibit JS2). 3. On 1 September 2024, the defendant\'s rent payment of £1,200 was not received. I sent an email reminder on 5 September (Exhibit JS3). 4. On 10 September 2024, the defendant replied stating they had lost their job and could not pay. I offered a payment plan of £200/month on top of current rent (Exhibit JS4).',
              '1. I first noticed loud music from the neighbouring property (42 High Street) at approximately 11:30pm on Saturday 15 June 2024. The bass was so loud it vibrated my bedroom wall. 2. I knocked on the door at midnight but received no answer. 3. Between June and October 2024, I recorded 14 separate incidents of excessive noise after 11pm (see noise diary, Exhibit SW1). 4. On 1 August 2024, I reported the noise to the council environmental health team (reference: EH-2024-5678).'
            ],
            exampleHint: 'Numbered list of events with dates, facts, and what you personally observed'
          },
          {
            id: 'supportingDocuments',
            type: 'checkbox',
            question: 'What types of supporting documents or evidence do you have?',
            options: ['Photographs', 'Written correspondence (emails/letters)', 'Text messages', 'Inventory / inspection reports', 'Rent payment records', 'Invoices / receipts', 'Professional reports (surveyor, etc.)', 'Noise diary / incident log', 'Police reports', 'Council records', 'Other'],
            validation: { required: true },
            description: 'Each document should be labelled as an exhibit (e.g., Exhibit JS1, JS2) and referenced in your chronological account.'
          },
          {
            id: 'statementDate',
            type: 'date',
            question: 'What is the date of this statement?',
            validation: { required: true }
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'witness statements',
      'Generate a witness statement complying with CPR Part 32 and Practice Direction 32A. Include: heading (court, case number, parties), witness identification (name, address, occupation, relationship to case), numbered paragraphs with chronological account based on the provided events, reference to exhibits for supporting documents, statement of truth with signature block. Incorporate ALL form data.',
      'Generate an enhanced witness statement with: proper court heading format, witness identification and basis of knowledge, numbered chronological account with clear paragraphs (one topic per paragraph), exhibit references formatted correctly (e.g., "I refer to the photograph at page [X] of Exhibit [initials][number]"), cross-references to supporting documents, distinction between personal knowledge and information received from others, statement of truth with correct wording per CPR, and exhibit bundle index. Incorporate ALL form data.',
      'Generate a premium court-ready witness statement complying fully with CPR Part 32 and PD 32A including: (1) Proper court heading (case number, court name, parties, statement number, exhibit references), (2) Witness identification paragraph (full name, address, occupation, position, basis of knowledge), (3) Professionally structured chronological account with numbered paragraphs (each dealing with a discrete matter, facts distinguished from opinion, personal knowledge clearly stated, hearsay identified where applicable), (4) Exhibit references following Practice Direction format (Exhibit [initials][number] with descriptions), (5) Statement of truth in prescribed form ("I believe that the facts stated in this witness statement are true. I understand that proceedings for contempt of court may be brought against anyone who makes, or causes to be made, a false statement in a document verified by a statement of truth without an honest belief in its truth."), (6) Exhibit bundle index listing all supporting documents with descriptions. Professional legal formatting with correct margins, paragraph numbering, and court document styling. Incorporate ALL form data.'
    )
  },
  'section8-ground14': {
    formSections: {
      details: {
        id: 'details',
        title: 'Section 8 Notice - Ground 14 (Anti-social Behaviour)',
        fields: [
          {
            id: 'landlordName',
            type: 'text',
            question: 'What is the landlord\'s full name?',
            validation: { required: true },
            examples: ['John Smith', 'Property Management Ltd'],
            exampleHint: 'Name as on the tenancy agreement'
          },
          {
            id: 'landlordAddress',
            type: 'text',
            question: 'What is the landlord\'s correspondence address?',
            validation: { required: true },
            examples: ['15 Oak Avenue, Manchester, M15 6BH'],
            exampleHint: 'Full postal address'
          },
          {
            id: 'tenantName',
            type: 'text',
            question: 'What is the tenant\'s full name?',
            validation: { required: true },
            examples: ['Emma Johnson', 'Michael Chen'],
            exampleHint: 'Name as on the tenancy agreement'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the property address?',
            validation: { required: true },
            examples: ['42 High Street, London, SW1A 1AA'],
            exampleHint: 'Full property address including postcode'
          },
          {
            id: 'tenancyStartDate',
            type: 'date',
            question: 'When did the tenancy start?',
            validation: { required: true }
          },
          {
            id: 'affectedParties',
            type: 'checkbox',
            question: 'Who has been affected by the anti-social behaviour?',
            options: ['Neighbours', 'Other tenants in the building', 'Visitors to neighbouring properties', 'General public / passers-by', 'Landlord or their agent', 'Landlord\'s property or interests'],
            validation: { required: true },
            description: 'Ground 14 covers nuisance or annoyance to persons residing, visiting, or carrying on lawful activity in the locality, or conviction for an offence committed in the locality.'
          },
          {
            id: 'behaviourSeverity',
            type: 'select',
            question: 'How would you categorise the severity of the behaviour?',
            options: ['Low (persistent but minor nuisance)', 'Moderate (regular disturbance affecting quality of life)', 'Severe (significant impact, threats, or property damage)', 'Criminal (behaviour constituting criminal offences)'],
            validation: { required: true },
            description: 'Ground 14 is a discretionary ground - the court will consider the severity and impact when deciding whether to grant possession.'
          },
          {
            id: 'behaviourDescription',
            type: 'textarea',
            question: 'Describe the anti-social behaviour in detail:',
            validation: { required: true },
            rows: 5,
            placeholder: 'Describe the specific behaviour: what happened, how often, what impact it has had on neighbours and the community...',
            description: 'Be specific and factual. Include: the nature of the behaviour, its frequency, its impact on others, and any escalation over time.',
            examples: [
              'The tenant has been playing extremely loud music (bass audible through party walls) late at night (typically between 11pm-3am) on an average of 3-4 nights per week since moving in on 1 June 2024. Neighbours at numbers 40 and 44 have both complained in writing. The noise has caused sleep disturbance, resulting in neighbour at no. 44 reporting anxiety and their child being unable to sleep on school nights. The tenant has also hosted large gatherings (15-20 people) on at least 5 occasions, with shouting, arguments, and litter left in communal areas.',
              'Since September 2024, the tenant has engaged in threatening and abusive behaviour towards the neighbour at Flat 4B. This has included: verbal abuse in communal hallway (witnessed by 3 other residents), slamming doors repeatedly, leaving obstructions in the communal corridor (bicycles, rubbish bags), and on 15 November 2024, banging on the neighbour\'s door at 2am while shouting threats. Police were called and attended (crime reference: MET/2024/123456).'
            ],
            exampleHint: 'Specific, factual description of the behaviour and its impact'
          },
          {
            id: 'incidentDates',
            type: 'textarea',
            question: 'List the dates and details of specific incidents:',
            validation: { required: true },
            rows: 4,
            placeholder: 'Date 1: What happened...\nDate 2: What happened...\nDate 3: What happened...',
            description: 'Courts require specific incident details. List each incident separately with date, time (if known), what happened, and who was affected.',
            examples: [
              '15/06/2024 (11:30pm): Loud music until 3am, neighbours at no.40 and no.44 disturbed. 22/06/2024 (midnight): Large party, shouting in garden, police called by neighbour. 05/07/2024 (1am): Argument in street outside property, aggressive language. 12/07/2024: Rubbish dumped in communal garden. 20/07/2024 (11pm): Loud music again, bass vibrating party wall. 28/07/2024: Written warning letter sent to tenant. 03/08/2024 (midnight): Music resumed despite warning.',
              '01/10/2024: Verbal abuse towards Flat 4B resident in hallway. 15/10/2024: Bicycle blocking fire escape corridor (photographed). 28/10/2024: Door slamming at 11pm disturbing multiple residents. 15/11/2024 (2am): Banging on Flat 4B door, shouting threats - police attended (ref: MET/2024/123456). 20/11/2024: Intimidating behaviour in lift, witnessed by Flat 2A resident.'
            ],
            exampleHint: 'List each incident with date, time, and what happened'
          },
          {
            id: 'evidenceAvailable',
            type: 'checkbox',
            question: 'What evidence is available to support the claim?',
            options: ['Written complaints from neighbours', 'Police reports or crime references', 'Witness statements', 'Noise diary / incident log', 'Photographs or video', 'Audio recordings', 'Council environmental health records', 'CCTV footage', 'Written warnings previously sent', 'Letting agent records'],
            validation: { required: true },
            description: 'The more evidence available, the stronger the case. Ground 14 is discretionary, so courts need to be persuaded the behaviour warrants possession.'
          },
          {
            id: 'previousWarnings',
            type: 'textarea',
            question: 'What warnings or actions have been taken before serving this notice?',
            validation: { required: true },
            rows: 3,
            placeholder: 'List any verbal warnings, written warnings, meetings, or other steps taken to resolve the issue...',
            description: 'Courts expect landlords to have attempted to resolve issues before seeking possession. Document all steps taken.',
            examples: [
              'Verbal warning given during routine inspection 15/07/2024. Written warning letter sent by recorded delivery 28/07/2024 (copy retained). Meeting held with tenant 05/08/2024 to discuss behaviour - tenant agreed to reduce noise. Behaviour improved for 2 weeks then resumed. Second written warning sent 01/09/2024. No improvement.',
              'Email complaint forwarded to tenant 03/10/2024. Formal written warning 15/10/2024. Meeting arranged for 01/11/2024 - tenant did not attend. Second formal warning 10/11/2024 after police incident. Tenant responded denying all allegations.'
            ],
            exampleHint: 'Chronological list of all warnings and attempts to resolve'
          },
          {
            id: 'witnessDetails',
            type: 'textarea',
            question: 'Provide details of any witnesses who can support the claim:',
            validation: { required: false },
            rows: 3,
            placeholder: 'For each witness: name, address or flat number, relationship, and what they can confirm...',
            description: 'Witnesses strengthen the case significantly. Include their willingness to provide a written statement or attend court.',
            examples: [
              'Mr James Brown, 40 High Street (next door neighbour) - witnessed noise on multiple occasions, willing to provide written statement. Mrs Sarah Green, 44 High Street (neighbour) - disturbed by noise, kept incident diary, willing to attend court. PC David Wilson, Metropolitan Police (badge 1234) - attended incident 15/11/2024, can confirm police report.',
              'Flat 4B resident (affected party) - willing to provide witness statement. Flat 2A resident - witnessed intimidation in lift. Building concierge - can confirm communal area issues and CCTV footage availability.'
            ],
            exampleHint: 'Name, location, what they witnessed, willingness to support'
          },
          {
            id: 'noticeDate',
            type: 'date',
            question: 'What date should appear on this Section 8 notice?',
            validation: { required: true },
            description: 'Ground 14 requires a minimum of 2 weeks (14 days) notice before court proceedings can begin.'
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'Section 8 Notices (Ground 14)',
      'Generate a Section 8 Notice for Ground 14 (anti-social behaviour) complying with the Housing Act 1988. Include: landlord and tenant details, property address, Ground 14 particulars with specific incident descriptions, affected parties, evidence summary, required 2-week notice period, and court proceedings warning. Incorporate ALL form data.',
      'Generate an enhanced Section 8 Notice for Ground 14 with: formal notice in prescribed format, detailed incident schedule with dates and descriptions, affected parties and impact assessment, evidence list, previous warnings chronology, witness details, notice period calculation, tenant rights (this is a discretionary ground), covering letter explaining the notice and next steps, and signposting to advice services. Incorporate ALL form data.',
      'Generate a premium Section 8 Notice for Ground 14 with comprehensive court-ready documentation including: (1) Prescribed Form notice with Ground 14 particulars, (2) Detailed incident log with chronological evidence, (3) Impact statement describing effect on neighbours and community, (4) Evidence bundle index (complaints, police reports, photos, noise diaries), (5) Warning letters chronology with copies, (6) Witness details schedule, (7) Covering letter to tenant, (8) Guidance note on Ground 14 (discretionary ground, court considers reasonableness, tenant can contest, judge has discretion on possession order type - outright, suspended, postponed). Professional legal formatting. Incorporate ALL form data.'
    )
  },
  'possession-claim': {
    formSections: {
      details: {
        id: 'details',
        title: 'Possession Claim Form N5',
        fields: [
          {
            id: 'claimantName',
            type: 'text',
            question: 'What is the claimant\'s (landlord\'s) full name?',
            validation: { required: true },
            examples: ['John Smith', 'Property Management Ltd'],
            exampleHint: 'Full legal name as it should appear on court documents'
          },
          {
            id: 'claimantAddress',
            type: 'text',
            question: 'What is the claimant\'s address for service of documents?',
            validation: { required: true },
            description: 'This is where court documents will be sent. Can be a solicitor\'s address if represented.',
            examples: ['15 Oak Avenue, Manchester, M15 6BH', 'c/o Smith & Jones Solicitors, 100 Legal Row, London, EC1A 1BB'],
            exampleHint: 'Postal address for receiving court correspondence'
          },
          {
            id: 'claimantContact',
            type: 'text',
            question: 'What is the claimant\'s email address and phone number?',
            validation: { required: true },
            examples: ['john.smith@email.com / 07700 900123', 'info@propertymanagement.co.uk / 020 7946 0123'],
            exampleHint: 'Email and phone for court communications'
          },
          {
            id: 'solicitorDetails',
            type: 'text',
            question: 'If legally represented, provide solicitor/representative details:',
            validation: { required: false },
            description: 'Leave blank if acting as a litigant in person (self-representing).',
            examples: ['Smith & Jones Solicitors, Ref: SJ/2025/1234, Tel: 020 7946 0456', 'Acting in person (litigant in person)'],
            exampleHint: 'Firm name, reference, and contact, or leave blank'
          },
          {
            id: 'defendantName',
            type: 'text',
            question: 'What is the defendant\'s (tenant\'s) full name?',
            validation: { required: true },
            examples: ['Emma Johnson', 'Michael Chen and Sarah Chen (joint tenants)'],
            exampleHint: 'Full name(s) of all tenants named on the agreement'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the address of the property you are seeking possession of?',
            validation: { required: true },
            examples: ['42 High Street, London, SW1A 1AA'],
            exampleHint: 'Full property address including postcode'
          },
          {
            id: 'tenancyType',
            type: 'select',
            question: 'What type of tenancy is in place?',
            options: ['Assured Shorthold Tenancy (AST)', 'Assured Tenancy', 'Periodic Tenancy (post-2025 reform)', 'Contractual Periodic Tenancy', 'Statutory Periodic Tenancy'],
            validation: { required: true },
            description: 'The tenancy type determines which grounds and procedures apply.'
          },
          {
            id: 'tenancyStartDate',
            type: 'date',
            question: 'When did the tenancy start?',
            validation: { required: true }
          },
          {
            id: 'rentAmount',
            type: 'number',
            question: 'What is the monthly rent? (in GBP)',
            validation: { required: true, min: 0 },
            examples: ['1250', '850', '2100'],
            exampleHint: 'Monthly rent as stated in tenancy agreement'
          },
          {
            id: 'groundsForPossession',
            type: 'checkbox',
            question: 'Which grounds for possession are being relied upon?',
            options: ['Ground 8 - Serious rent arrears (mandatory)', 'Ground 10 - Some rent arrears (discretionary)', 'Ground 11 - Persistent late payment (discretionary)', 'Ground 12 - Breach of tenancy terms (discretionary)', 'Ground 14 - Anti-social behaviour (discretionary)', 'Ground 14A - Domestic violence (mandatory)', 'Ground 17 - False statement by tenant (discretionary)', 'Other grounds (specify in particulars)'],
            validation: { required: true },
            description: 'Mandatory grounds: court must grant possession if proved. Discretionary grounds: court considers reasonableness. Multiple grounds can be relied upon simultaneously.'
          },
          {
            id: 'section8NoticeServed',
            type: 'radio',
            question: 'Has a Section 8 notice been served on the tenant?',
            options: ['Yes', 'No - seeking to dispense with notice requirement'],
            validation: { required: true },
            description: 'A Section 8 notice must generally be served before possession proceedings. The court may dispense with the requirement in certain circumstances for Ground 14.'
          },
          {
            id: 'section8NoticeDate',
            type: 'date',
            question: 'When was the Section 8 notice served?',
            validation: { required: false },
            description: 'Proceedings must be started within 12 months of the notice date.'
          },
          {
            id: 'arrearsAmount',
            type: 'number',
            question: 'What is the total rent arrears amount (if applicable)? (in GBP)',
            validation: { required: false, min: 0 },
            description: 'If claiming on rent arrears grounds, state the arrears at the date of this form. Remember: for Ground 8, arrears must be at least 2 months at both notice date AND hearing date.',
            examples: ['3750', '5100', '0'],
            exampleHint: 'Total arrears, or 0 if not claiming for arrears'
          },
          {
            id: 'particularsOfClaim',
            type: 'textarea',
            question: 'Provide the particulars of claim (brief statement of facts):',
            validation: { required: true },
            rows: 5,
            placeholder: 'Summarise the factual basis for the claim: what happened, when, what ground(s) apply, and what you are asking the court to order...',
            description: 'This is the core of your court claim. Set out the material facts clearly and concisely. Reference the tenancy, the grounds relied on, and the specific breaches or events.',
            examples: [
              'The Claimant is the landlord of the property at 42 High Street, London, SW1A 1AA. The Defendant has been the tenant under an Assured Shorthold Tenancy dated 1 June 2023 at a monthly rent of £1,250. The Defendant has failed to pay rent since 1 January 2025. Arrears now total £3,750 (3 months). A Section 8 Notice relying on Grounds 8, 10, and 11 was served on the Defendant on 15 February 2025 by first class post. The Claimant seeks: (1) an order for possession of the property, (2) judgment for rent arrears of £3,750, and (3) daily occupation charges at the rate of £41.10/day from the date of this claim until possession is given.',
              'The Claimant is the freehold owner of Flat 3B, Riverside Court. The Defendant is the tenant under an AST dated 1 March 2024 at £1,500/month. The Defendant has caused nuisance and annoyance to neighbouring residents since June 2024 (Ground 14). The Defendant has also been convicted of criminal damage to communal property on 10 November 2024 (Ground 14). A Section 8 Notice was served on 1 December 2024. The Claimant seeks an order for possession.'
            ],
            exampleHint: 'Set out the facts: tenancy details, what happened, grounds relied on, what you seek'
          },
          {
            id: 'preActionProtocol',
            type: 'radio',
            question: 'Has the Pre-Action Protocol for Possession Claims been complied with?',
            options: ['Yes - all steps completed', 'Partially - explain in particulars'],
            validation: { required: true },
            description: 'The court will check compliance. For rent arrears claims, this includes: early contact with tenant, providing debt advice information, considering the tenant\'s circumstances, and offering a repayment plan.'
          },
          {
            id: 'courtFee',
            type: 'number',
            question: 'What is the court fee being paid? (in GBP)',
            validation: { required: false, min: 0 },
            description: 'Current fee for an N5 possession claim. Check the court fees schedule for the current amount.',
            examples: ['355', '130'],
            exampleHint: 'Check current HMCTS fee schedule'
          },
          {
            id: 'preferredCourt',
            type: 'text',
            question: 'Which county court should the claim be issued at?',
            validation: { required: false },
            description: 'Possession claims should normally be issued at the county court for the district where the property is located.',
            examples: ['Central London County Court', 'Manchester County Court', 'Birmingham County Court'],
            exampleHint: 'County court local to the property address'
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'Possession Claim Forms',
      'Generate a Possession Claim Form (N5 format) complying with CPR Part 55. Include: court heading, claimant and defendant details, property address, tenancy details (type, start date, rent), grounds relied upon, Section 8 notice details, arrears calculation (if applicable), particulars of claim, and pre-action protocol compliance statement. Incorporate ALL form data.',
      'Generate an enhanced Possession Claim Form with: N5 form format, detailed particulars of claim with chronological facts, grounds relied upon with specific legal references (Housing Act 1988 Schedule 2), Section 8 notice compliance verification, rent arrears schedule (if applicable), pre-action protocol compliance checklist, witness evidence summary, and hearing preparation notes. Include a draft witness statement for the claimant. Incorporate ALL form data.',
      'Generate a premium court-ready Possession Claim documentation pack including: (1) Form N5 Possession Claim with all required particulars, (2) Detailed Particulars of Claim as a separate document with numbered paragraphs (tenancy background, breaches/grounds with specific dates and evidence, Section 8 notice compliance, remedy sought), (3) Rent Arrears Schedule if applicable (month-by-month breakdown of rent due, paid, and balance), (4) Chronology of events, (5) Pre-Action Protocol Compliance Statement with evidence of each step, (6) Draft Witness Statement for claimant (CPR Part 32 compliant), (7) Evidence Bundle Index listing all supporting documents, (8) Draft order sought. All documents in court-ready format with correct case heading and pagination. Incorporate ALL form data.'
    )
  },
  'student-accommodation': {
    formSections: {
      details: {
        id: 'details',
        title: 'Student Accommodation Agreement',
        fields: [
          {
            id: 'landlordName',
            type: 'text',
            question: 'What is the landlord\'s full name or company name?',
            validation: { required: true },
            examples: ['Student Living Ltd', 'Campus Housing Services', 'Mr. Robert Davies', 'University Accommodation Partners'],
            exampleHint: 'Property management company or individual landlord'
          },
          {
            id: 'landlordEmail',
            type: 'text',
            question: 'What is the landlord\'s email address?',
            validation: { required: true, pattern: '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$' },
            examples: ['lettings@studentliving.co.uk', 'info@campushousing.com', 'robert.davies@properties.co.uk'],
            exampleHint: 'Primary contact for tenancy matters'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the property address?',
            validation: { required: true },
            examples: ['45 University Road, Manchester, M13 9PL', 'Student Village Block B, Room 312, Leeds, LS2 9JT', '12 College Street, Oxford, OX1 3BN'],
            exampleHint: 'Full address including room number if applicable'
          },
          {
            id: 'propertyType',
            type: 'select',
            question: 'What type of student accommodation is this?',
            options: ['Purpose-built student accommodation (PBSA)', 'HMO - House in Multiple Occupation', 'Shared house', 'Studio flat', 'Halls of residence'],
            validation: { required: true }
          },
          {
            id: 'universityPartnership',
            type: 'radio',
            question: 'Is this university-approved accommodation?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'University-approved accommodation may offer additional protections and accreditation.'
          },
          {
            id: 'universityName',
            type: 'text',
            question: 'Which university is the student attending?',
            validation: { required: true },
            examples: ['University of Manchester', 'Imperial College London', 'University of Edinburgh', 'University of Bristol', 'Durham University'],
            exampleHint: 'Full official university name'
          },
          {
            id: 'studentName',
            type: 'text',
            question: 'What is the student tenant\'s full name?',
            validation: { required: true },
            examples: ['Oliver Thompson', 'Priya Patel', 'James MacKenzie', 'Sophie Chen', 'Emily Roberts'],
            exampleHint: 'Full legal name as on university records'
          },
          {
            id: 'studentEmail',
            type: 'text',
            question: 'What is the student\'s email address?',
            validation: { required: true, pattern: '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$' },
            examples: ['oliver.thompson@student.manchester.ac.uk', 'priya.patel@imperial.ac.uk', 'j.mackenzie@ed.ac.uk'],
            exampleHint: 'University email address preferred'
          },
          {
            id: 'studentID',
            type: 'text',
            question: 'What is the student\'s university ID number?',
            validation: { required: false },
            examples: ['10234567', 'S1234567', 'UOM-2025-4321', '2025-12345'],
            exampleHint: 'University-issued student identification number'
          },
          {
            id: 'courseDetails',
            type: 'text',
            question: 'What course is the student studying?',
            validation: { required: true },
            description: 'Include course name and expected duration.',
            examples: ['BSc Computer Science, 3-year course', 'Medicine MBChB, 5-year program', 'BA History and Politics, 3 years', 'MEng Electrical Engineering, 4-year integrated masters'],
            exampleHint: 'Include qualification type and duration'
          },
          {
            id: 'academicYear',
            type: 'text',
            question: 'Which academic year does this tenancy cover?',
            validation: { required: true },
            description: 'e.g., 2025/2026 academic year',
            examples: ['2025/2026', '2026/2027', '2024/2025'],
            exampleHint: 'Format as YYYY/YYYY'
          },
          {
            id: 'startDate',
            type: 'date',
            question: 'When does the tenancy begin?',
            validation: { required: true },
            description: 'Typically aligned with semester start dates.',
            examples: ['2025-09-15', '2025-09-22', '2025-09-01'],
            exampleHint: 'Usually mid-September for autumn term start'
          },
          {
            id: 'endDate',
            type: 'date',
            question: 'When does the tenancy end?',
            validation: { required: true },
            description: 'Student tenancies often run for 44-51 weeks aligned with academic calendar.',
            examples: ['2026-06-30', '2026-07-15', '2026-08-31'],
            exampleHint: 'End of academic year (June/July) or full 52 weeks'
          },
          {
            id: 'termBreaks',
            type: 'select',
            question: 'Does the tenancy cover term breaks?',
            options: ['All year including breaks', 'Term time only', 'Excludes summer break', 'Custom arrangement'],
            validation: { required: true }
          },
          {
            id: 'vacationAccess',
            type: 'text',
            question: 'Describe vacation period access and storage arrangements:',
            validation: { required: true },
            examples: [
              'Full access retained during all vacation periods including Christmas and Easter',
              'Room locked during summer break, storage available in designated area',
              'Access maintained, belongings may remain in room throughout all breaks'
            ],
            exampleHint: 'Clarify access rights and storage during university breaks'
          },
          {
            id: 'isJointTenancy',
            type: 'radio',
            question: 'Is this a joint tenancy with multiple students?',
            options: ['Yes', 'No'],
            validation: { required: true }
          },
          {
            id: 'numberOfStudents',
            type: 'number',
            question: 'How many students will live in the property?',
            validation: { required: true, min: 1 }
          },
          {
            id: 'otherStudentNames',
            type: 'text',
            question: 'List all other student tenant names (comma separated):',
            validation: { required: false }
          },
          {
            id: 'jointSeveralLiability',
            type: 'radio',
            question: 'Acknowledge joint and several liability for rent:',
            options: ['Yes - All tenants jointly liable'],
            validation: { required: true },
            description: 'In joint tenancies, all tenants are responsible for the full rent even if one fails to pay.'
          },
          {
            id: 'rentAmount',
            type: 'number',
            question: 'What is the total monthly rent? (in GBP)',
            validation: { required: true, min: 0 },
            examples: ['2400', '1800', '3200', '1500'],
            exampleHint: 'Total rent for the property/room per month',
            smartDefaults: true
          },
          {
            id: 'rentPerStudent',
            type: 'number',
            question: 'What is the rent per student per month? (in GBP)',
            validation: { required: false, min: 0 },
            examples: ['600', '450', '800', '550'],
            exampleHint: 'Individual student contribution if shared tenancy',
            smartDefaults: true
          },
          {
            id: 'depositAmount',
            type: 'number',
            question: 'What is the deposit amount? (in GBP)',
            validation: { required: true, min: 0 },
            description: 'Maximum 5 weeks\' rent.',
            examples: ['2769', '2077', '3692'],
            exampleHint: 'Calculate: (Monthly rent ÷ 52) × 5 weeks',
            smartDefaults: true
          },
          {
            id: 'depositScheme',
            type: 'select',
            question: 'Which deposit protection scheme will be used?',
            options: ['Deposit Protection Service (DPS)', 'MyDeposits', 'Tenancy Deposit Scheme (TDS)'],
            validation: { required: true }
          },
          {
            id: 'requiresGuarantor',
            type: 'radio',
            question: 'Is a guarantor required?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'Most student tenancies require a UK-based guarantor (usually parent or guardian).'
          },
          {
            id: 'guarantorName',
            type: 'text',
            question: 'What is the guarantor\'s full name?',
            validation: { required: false },
            examples: ['Mr. David Thompson (father)', 'Mrs. Anita Patel (mother)', 'Dr. Sarah MacKenzie (guardian)', 'Mr. and Mrs. Chen (parents)'],
            exampleHint: 'Include relationship to student in parentheses'
          },
          {
            id: 'guarantorRelationship',
            type: 'select',
            question: 'What is the guarantor\'s relationship to the student?',
            options: ['Parent', 'Guardian', 'Other family member', 'Professional guarantor service'],
            validation: { required: false }
          },
          {
            id: 'guarantorAddress',
            type: 'text',
            question: 'What is the guarantor\'s address?',
            validation: { required: false }
          },
          {
            id: 'guarantorEmail',
            type: 'text',
            question: 'What is the guarantor\'s email address?',
            validation: { required: false, pattern: '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$' }
          },
          {
            id: 'guarantorFinancialCovenant',
            type: 'text',
            question: 'Describe the guarantor\'s financial obligations:',
            validation: { required: false },
            description: 'Typically covers rent arrears, damage costs, and other tenancy breaches.',
            examples: [
              'Guarantor liable for all rent arrears, property damage, and cleaning costs up to £5,000',
              'Full liability for rent and damages throughout tenancy term, annual income £45,000+',
              'Covers unpaid rent, utility bills, and repair costs incurred by student tenant'
            ],
            exampleHint: 'Specify scope of liability and any financial requirements'
          },
          {
            id: 'furnishingStatus',
            type: 'select',
            question: 'What is the furnishing status?',
            options: ['Fully furnished', 'Part furnished'],
            validation: { required: true },
            description: 'Student accommodation is typically fully furnished.'
          },
          {
            id: 'utilitiesIncluded',
            type: 'checkbox',
            question: 'Which utilities are included in the rent?',
            options: ['Gas', 'Electricity', 'Water', 'Internet', 'TV License', 'Council Tax', 'None'],
            validation: { required: true },
            description: 'Many student properties include all utilities in rent (all-inclusive bills).'
          },
          {
            id: 'isHMO',
            type: 'radio',
            question: 'Is this property a licensed HMO?',
            options: ['Yes', 'No', 'Not applicable'],
            validation: { required: true },
            description: 'Properties with 5+ unrelated occupants require HMO license.'
          },
          {
            id: 'hmoLicenseNumber',
            type: 'text',
            question: 'What is the HMO license number?',
            validation: { required: false }
          },
          {
            id: 'sharedFacilities',
            type: 'checkbox',
            question: 'Which facilities are shared?',
            options: ['Kitchen', 'Bathroom', 'Living room', 'Garden', 'Laundry', 'Study area', 'None - self-contained'],
            validation: { required: true }
          },
          {
            id: 'exclusiveAreas',
            type: 'text',
            question: 'Describe the student\'s exclusive bedroom/studio area:',
            validation: { required: true }
          },
          {
            id: 'communityStandards',
            type: 'text',
            question: 'Describe noise policy and community living standards:',
            validation: { required: true },
            description: 'Include quiet hours, guest policies, and respectful living expectations.'
          },
          {
            id: 'guestPolicy',
            type: 'text',
            question: 'What is the overnight guest policy?',
            validation: { required: true }
          },
          {
            id: 'maintenanceResponse',
            type: 'text',
            question: 'What are the maintenance response times?',
            validation: { required: true },
            description: 'Specify emergency (24 hours), urgent (72 hours), and routine (7 days) response timescales.'
          },
          {
            id: 'vacationMaintenance',
            type: 'text',
            question: 'How is maintenance handled during term breaks?',
            validation: { required: true }
          },
          {
            id: 'rightToRentChecked',
            type: 'radio',
            question: 'Has Right to Rent check been completed?',
            options: ['Yes', 'Pending'],
            validation: { required: true }
          },
          {
            id: 'studentSpecificClauses',
            type: 'text',
            question: 'Any additional student-specific terms or university requirements?',
            validation: { required: false }
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'student accommodation agreements',
      'Generate a comprehensive UK student accommodation agreement with essential student-specific clauses. Include: student and guarantor details, academic year alignment, term dates and vacation access, joint and several liability provisions, shared and exclusive areas, community living standards, all-inclusive utilities (if applicable), HMO licensing (if applicable), maintenance during term and breaks, noise and guest policies, and student tenant protections. Incorporate ALL form data.',
      'Generate an enhanced UK student accommodation agreement with detailed student protections. Include comprehensive provisions for: student and guarantor relationship with full financial covenant, academic calendar integration with semester start/end dates, term break arrangements and storage provisions, joint tenancy with joint and several liability explanation, shared facility usage rules and exclusive bedroom rights, all-inclusive utility packages, HMO compliance and licensing, community living standards with quiet hours and study-friendly environment, guest policies, maintenance response timescales (normal and vacation periods), deposit protection, university accreditation status, and student-specific dispute resolution. Incorporate ALL form data.',
      'Generate a premium professional-grade UK student accommodation agreement with exhaustive student-specific provisions. Include comprehensive clauses: student tenant identification with university details and course information, guarantor deed with full financial covenant (rent, damage, breach of tenancy), principal debtor provisions, academic year synchronization with precise term dates, vacation period access rights and storage arrangements, joint and several liability framework with explanation of collective responsibility, detailed property description (exclusive bedroom with locks, shared facilities with cleaning schedules), all-inclusive utility packages with fair usage policies, HMO licensing compliance with safety standards, community living charter (noise policies, quiet hours 11pm-7am, respectful behavior, study environment protection), overnight guest limitations, maintenance service level agreements (emergency 24hrs, urgent 72hrs, routine 7 days) with term-time and vacation procedures, deposit protection with scheme registration, deduction protocols, university partnership provisions (if applicable), accreditation standards, student support services, early termination provisions for course changes or hardship, subletting prohibitions, Right to Rent compliance, and student-specific dispute resolution with university mediation options. Incorporate ALL form data with clear language suitable for first-time renters and comprehensive guarantor documentation.'
    )
  },
  'property-inspection': {
    formSections: {
      details: {
        id: 'details',
        title: 'Property Inspection Checklist',
        fields: [
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the full address of the property being inspected?',
            validation: { required: true },
            examples: ['42 High Street, London, SW1A 1AA', 'Flat 5B, Riverside Tower, Leeds, LS1 4AP'],
            exampleHint: 'Full property address including postcode'
          },
          {
            id: 'inspectionType',
            type: 'select',
            question: 'What type of property inspection is this?',
            options: ['Move-in inspection', 'Periodic (routine) inspection', 'Move-out inspection', 'Emergency inspection', 'Pre-tenancy assessment', 'Post-repair verification'],
            validation: { required: true },
            description: 'Periodic inspections are typically conducted every 3-6 months. Landlords must give at least 24 hours written notice before entering the property.'
          },
          {
            id: 'inspectionDate',
            type: 'date',
            question: 'What is the date of this inspection?',
            validation: { required: true }
          },
          {
            id: 'inspectorName',
            type: 'text',
            question: 'Who is conducting this inspection?',
            validation: { required: true },
            description: 'Name and role of the person performing the inspection.',
            examples: ['John Smith (landlord)', 'ABC Lettings - Sarah Brown (property manager)', 'Independent surveyor: David Williams MRICS'],
            exampleHint: 'Name and role or company'
          },
          {
            id: 'landlordName',
            type: 'text',
            question: 'What is the landlord\'s full name?',
            validation: { required: true },
            examples: ['John Smith', 'Property Holdings Ltd'],
            exampleHint: 'Name as on tenancy agreement'
          },
          {
            id: 'tenantName',
            type: 'text',
            question: 'What is the tenant\'s full name?',
            validation: { required: false },
            examples: ['Emma Johnson', 'Michael Chen'],
            exampleHint: 'Leave blank if property is vacant'
          },
          {
            id: 'tenantPresent',
            type: 'radio',
            question: 'Was the tenant present during the inspection?',
            options: ['Yes', 'No - tenant gave access but was not present', 'No - property vacant'],
            validation: { required: true }
          },
          {
            id: 'numberOfRooms',
            type: 'number',
            question: 'How many rooms does the property have in total?',
            validation: { required: true, min: 1 },
            description: 'Include all rooms: bedrooms, living areas, kitchen, bathrooms, utility rooms, etc.'
          },
          {
            id: 'exteriorCondition',
            type: 'textarea',
            question: 'Describe the condition of the property exterior:',
            validation: { required: true },
            rows: 4,
            placeholder: 'Assess: roof (visible), guttering, external walls, windows, doors, paths, garden, fencing, parking...',
            description: 'Note any visible issues with the roof, guttering, downpipes, external walls (cracks, damp staining), windows, doors, paths, garden, fencing, and parking areas.',
            examples: [
              'Roof: tiles appear intact from ground level, no visible damage. Guttering: clear, no overflow marks. External walls: brick, good condition, no cracks. Windows: all UPVC double-glazed, seals intact. Front door: good condition, lock working. Garden: lawn mown, hedges trimmed. Fencing: all panels secure. Path: concrete, no trip hazards.',
              'Roof: 2 slipped tiles visible on south side - needs roofer. Guttering: overflow staining on rear wall suggests blockage. Walls: hairline crack on front bay window lintel - monitor. Windows: bedroom 2 window handle stiff. Garden: overgrown, tenant reminded of maintenance obligation. Fence: 1 panel loose on left boundary.'
            ],
            exampleHint: 'Work from roof down: roof, gutters, walls, windows, doors, grounds'
          },
          {
            id: 'interiorCondition',
            type: 'textarea',
            question: 'Describe the general interior condition (walls, ceilings, floors, doors):',
            validation: { required: true },
            rows: 5,
            placeholder: 'Walk through each area noting: wall condition, ceiling condition, flooring, doors and frames, skirting boards, general cleanliness...',
            description: 'Provide a room-by-room overview of interior surfaces. Note any new damage, staining, marks, or deterioration since the last inspection or check-in.',
            examples: [
              'HALLWAY: Walls clean, no new marks. Ceiling good. Carpet slightly worn at entrance (fair wear and tear). LIVING ROOM: Walls good, 2 small picture hook holes noted. Ceiling clean. Carpet clean. KITCHEN: Walls clean, splashback tiles intact. Floor clean. Units in good order. BEDROOM 1: Walls good. Carpet clean. BEDROOM 2: Small scuff mark on wall behind door. Otherwise good. BATHROOM: Walls and tiles clean. Silicone around bath starting to discolour - recommend resealing within 6 months.',
              'Generally well-maintained throughout. Tenant keeping property clean and tidy. Living room: slight paint scuff behind sofa (cosmetic). Kitchen: worktops clean, all units working. Bedrooms: good condition. Bathroom: grouting between floor tiles needs attention near shower - minor mould forming. All doors and windows opening/closing correctly.'
            ],
            exampleHint: 'Room-by-room assessment of surfaces, cleanliness, and any new damage'
          },
          {
            id: 'plumbingCondition',
            type: 'textarea',
            question: 'Assess the plumbing - are there any leaks, dripping taps, or drainage issues?',
            validation: { required: true },
            rows: 3,
            placeholder: 'Check: all taps for drips, under sinks for leaks, toilet flush, shower pressure, visible pipes, any damp patches...',
            description: 'Test all taps, check under sinks for leaks, flush toilets, run showers, and check for any signs of water damage or damp.',
            examples: [
              'All taps tested - no drips. Under kitchen sink: dry, no leaks. Under bathroom basin: dry. Toilet: flushes correctly, fill valve working. Shower: good pressure, no leaks at connections. No damp patches visible. Hot water: heating to temperature correctly.',
              'Kitchen mixer tap: slight drip when turned off firmly - washer may need replacing (non-urgent). Bathroom: all working correctly. Under kitchen sink: small damp patch on cabinet floor - appears to be condensation from waste pipe, not a leak. Monitor next visit. Shower: slow drain, may benefit from drain clearing.'
            ],
            exampleHint: 'Test every water outlet, check under sinks, note any damp'
          },
          {
            id: 'heatingCondition',
            type: 'textarea',
            question: 'Assess the heating system condition:',
            validation: { required: true },
            rows: 3,
            placeholder: 'Check: boiler status lights, radiators heating evenly, thermostat working, any unusual noises, boiler pressure...',
            description: 'Check the boiler is operational, note the pressure reading, test radiators for heat distribution, and check thermostatic controls.',
            examples: [
              'Boiler: operating normally, pressure at 1.3 bar (green zone). No error codes displayed. All radiators checked - heating evenly throughout. Thermostat: set to 20°C, responding correctly. TRVs all adjustable. No unusual noises from system.',
              'Boiler: pressure slightly low at 0.8 bar - topped up to 1.2 bar during inspection. Bedroom 2 radiator cold at top - likely needs bleeding (minor maintenance). Living room radiator: slight knocking when heating up - air in system. Thermostat: working but battery low indicator showing. Recommended: system service and bleed all radiators.'
            ],
            exampleHint: 'Check boiler pressure, test radiators, note any issues'
          },
          {
            id: 'ventilationCondition',
            type: 'textarea',
            question: 'Assess ventilation - are extractor fans working and is there any condensation or mould?',
            validation: { required: true },
            rows: 3,
            placeholder: 'Check: bathroom extractor fan, kitchen extractor, window trickle vents, condensation on windows, mould in corners or behind furniture...',
            description: 'Proper ventilation is critical for preventing damp and mould. Check all extractors, vents, and look for signs of condensation or mould growth.',
            examples: [
              'Bathroom extractor fan: working, activates with light switch. Kitchen extractor: working on all speeds. Window trickle vents: open on all windows. No condensation visible. No mould anywhere. Property well-ventilated.',
              'Bathroom extractor fan: working but slow, may need cleaning/replacing. Kitchen extractor: working. Condensation noted on bedroom 2 window (double glazed, trickle vent was closed - opened during inspection). Small mould spot (2cm) in bathroom ceiling corner - cleaned during inspection. Tenant advised to keep trickle vents open and use extractor during showers. Follow-up in 3 months to check.'
            ],
            exampleHint: 'Test fans, check vents, look for condensation and mould'
          },
          {
            id: 'decentHomesCompliant',
            type: 'radio',
            question: 'Does the property meet the Decent Homes Standard?',
            options: ['Yes - fully compliant', 'No - issues identified that need addressing'],
            validation: { required: true },
            description: '2025 Renters\' Rights Bill: All rental properties must meet the Decent Homes Standard, which requires: reasonable state of repair, reasonably modern facilities, adequate thermal comfort, and freedom from serious hazards (Category 1 under HHSRS).'
          },
          {
            id: 'decentHomesIssues',
            type: 'textarea',
            question: 'If not fully compliant, describe the Decent Homes Standard issues found:',
            validation: { required: false },
            rows: 3,
            placeholder: 'List specific failures: structural issues, outdated facilities, inadequate heating, Category 1 hazards...',
            examples: [
              'Kitchen units are over 30 years old with damaged doors and non-functional drawers - facilities not reasonably modern. Bathroom has no extractor fan and single-glazed window - inadequate ventilation. Recommend: kitchen refit and bathroom extractor installation.',
              'Property generally meets standard. One issue: bedroom 2 window has failed double-glazing seal (misting between panes) - impacts thermal comfort. Recommend replacement within 3 months.'
            ],
            exampleHint: 'Specific failures against the four Decent Homes criteria'
          },
          {
            id: 'awaabsLawHazards',
            type: 'textarea',
            question: 'Have you identified any serious hazards requiring action under Awaab\'s Law (damp, mould, ventilation)?',
            validation: { required: true },
            rows: 3,
            placeholder: 'Document: any damp patches (location, size), mould growth (location, colour, size), condensation issues, ventilation deficiencies...',
            description: 'Awaab\'s Law 2025 requires landlords to respond to hazardous damp and mould within statutory timescales: emergency within 24 hours, urgent within 7 days, non-urgent within 28 days. Document all findings.',
            examples: [
              'No damp, mould, or ventilation hazards identified. All rooms dry and well-ventilated. No musty smells. Walls and ceilings clear.',
              'HAZARD FOUND: Black mould growth on bathroom ceiling (approx 15x10cm patch in corner near shower). Cause: inadequate ventilation - extractor fan not working effectively. Classification: Urgent - requires response within 7 days. Action needed: 1) Repair/replace extractor fan, 2) Treat mould with specialist product, 3) Follow-up inspection in 14 days. No other damp or mould found elsewhere in property.'
            ],
            exampleHint: 'Describe any hazards found with location, severity, and recommended action'
          },
          {
            id: 'gasSafetyValid',
            type: 'radio',
            question: 'Is there a valid Gas Safety Certificate (CP12) for the property?',
            options: ['Yes - valid and within date', 'No - expired or missing', 'Not applicable - no gas supply'],
            validation: { required: true },
            description: 'Gas Safety Certificates must be renewed annually. It is a criminal offence to let a property without a valid certificate.'
          },
          {
            id: 'gasSafetyExpiry',
            type: 'date',
            question: 'When does the current Gas Safety Certificate expire?',
            validation: { required: false },
            description: 'Leave blank if no gas supply.'
          },
          {
            id: 'eicrValid',
            type: 'radio',
            question: 'Is there a valid Electrical Installation Condition Report (EICR)?',
            options: ['Yes - valid and satisfactory', 'Yes - valid but unsatisfactory (remedial work needed)', 'No - expired or missing'],
            validation: { required: true },
            description: 'EICRs are valid for 5 years in rental properties. Must be satisfactory or have remedial work completed within 28 days.'
          },
          {
            id: 'eicrExpiry',
            type: 'date',
            question: 'When does the current EICR expire?',
            validation: { required: false }
          },
          {
            id: 'smokeAlarmsWorking',
            type: 'radio',
            question: 'Are working smoke alarms installed on every floor of the property?',
            options: ['Yes - all tested and working', 'No - some not working (details in notes)'],
            validation: { required: true },
            description: 'Legal requirement: at least one working smoke alarm on each storey. Test every alarm during inspection by pressing the test button.'
          },
          {
            id: 'coAlarmsWorking',
            type: 'radio',
            question: 'Are carbon monoxide alarms installed in rooms with combustion appliances?',
            options: ['Yes - all tested and working', 'No - missing or not working', 'Not required - no combustion appliances'],
            validation: { required: true },
            description: 'Required in rooms with gas boilers, fires, wood-burning stoves, or any combustion appliance.'
          },
          {
            id: 'fireSafety',
            type: 'textarea',
            question: 'Assess fire safety - escape routes, fire doors, and firefighting equipment:',
            validation: { required: true },
            rows: 3,
            placeholder: 'Check: escape routes clear, fire doors closing properly (HMOs), fire blanket in kitchen, extinguisher if provided...',
            examples: [
              'Escape routes: front and rear doors both accessible and unobstructed. No fire doors required (non-HMO). Fire blanket in kitchen: present, accessible. No fire extinguisher provided (not required for standard let). All internal doors close properly.',
              'HMO PROPERTY: Fire doors on all bedrooms and kitchen - all closing fully on self-closers. Intumescent strips and smoke seals intact. Emergency lighting on landing and stairway - tested OK. Fire blanket in kitchen. Fire extinguisher in hallway - serviced, in date. Escape route clear. Fire action notice posted in hallway.'
            ],
            exampleHint: 'Assess escape routes, fire doors (if HMO), and any firefighting equipment'
          },
          {
            id: 'epcRating',
            type: 'select',
            question: 'What is the property\'s current EPC rating?',
            options: ['A', 'B', 'C', 'D', 'E'],
            validation: { required: true },
            description: 'Rental properties must have a minimum EPC rating of E. Ratings of F or G are illegal to let without a valid exemption registered on the PRS Exemptions Register.'
          },
          {
            id: 'tenantReportedIssues',
            type: 'textarea',
            question: 'Has the tenant reported any issues or concerns?',
            validation: { required: false },
            rows: 3,
            placeholder: 'Record any maintenance requests, complaints, or concerns raised by the tenant...',
            description: 'Document any issues the tenant has raised, whether verbally during the inspection or previously reported.',
            examples: [
              'Tenant reports: 1) Bedroom window sticks when opening - noted, will arrange repair. 2) Dishwasher occasionally not completing cycle - will arrange engineer visit. 3) Neighbour noise complaint - advised to contact council environmental health if persistent.',
              'No issues reported. Tenant confirmed everything working well. Tenant asked about repainting living room - advised to request in writing per tenancy agreement terms.',
              'Tenant not present - no issues reported at this inspection. Last maintenance request (dripping tap) was resolved 2 weeks ago.'
            ],
            exampleHint: 'Note any concerns raised, with actions to be taken'
          },
          {
            id: 'urgentRepairs',
            type: 'textarea',
            question: 'List any repairs needed, categorised by urgency:',
            validation: { required: false },
            rows: 4,
            placeholder: 'EMERGENCY (24hrs): ... URGENT (7 days): ... ROUTINE (28 days): ... PLANNED MAINTENANCE: ...',
            description: 'Categorise all required repairs by urgency. Include what needs doing, which room, and estimated cost if known.',
            examples: [
              'No repairs required at this inspection. Property in good condition.',
              'EMERGENCY: None. URGENT (within 7 days): 1) Bathroom extractor fan not working - arrange electrician (est. £80-120). 2) Bedroom 2 radiator needs bleeding (minor, can be done during visit). ROUTINE (within 28 days): 1) Kitchen tap washer needs replacing (slight drip). 2) Garden fence panel needs resecuring. PLANNED MAINTENANCE: 1) Exterior paintwork due within 12 months. 2) Boiler service due September 2025.'
            ],
            exampleHint: 'Categorise as Emergency / Urgent / Routine / Planned'
          },
          {
            id: 'overallAssessment',
            type: 'select',
            question: 'What is the overall property condition rating?',
            options: ['Excellent - property very well maintained', 'Good - minor issues only', 'Satisfactory - some maintenance required', 'Poor - significant repairs needed', 'Unacceptable - immediate action required'],
            validation: { required: true }
          },
          {
            id: 'followUpRequired',
            type: 'radio',
            question: 'Is a follow-up inspection required?',
            options: ['Yes - within 1 month', 'Yes - within 3 months', 'No - next routine inspection as scheduled'],
            validation: { required: true },
            description: 'Schedule a follow-up if repairs were identified or if monitoring is needed for potential issues like damp or mould.'
          },
          {
            id: 'inspectionNotes',
            type: 'textarea',
            question: 'Any additional inspection notes or observations:',
            validation: { required: false },
            rows: 3,
            placeholder: 'General observations, tenant behaviour regarding property care, neighbourhood issues, future recommendations...',
            examples: [
              'Property being well cared for by tenant. Garden maintained to good standard. No concerns about property use or condition. Recommend continued 6-monthly inspection schedule.',
              'Tenant has added temporary hooks for pictures (adhesive, no wall damage). Small number of personal items in communal hallway - reminded about keeping shared areas clear. Generally good housekeeping. Property smells fresh and clean. Note: building management replacing communal carpet next month - tenant aware.'
            ],
            exampleHint: 'Any observations not covered in the sections above'
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'property inspection checklists',
      'Generate a comprehensive UK property inspection checklist report. Structure with: inspection details header (date, type, inspector, parties), then sections for exterior assessment, interior condition room-by-room, plumbing, heating, ventilation, safety compliance (gas, electrical, smoke/CO alarms, fire safety, EPC), Decent Homes Standard assessment, Awaab\'s Law hazard identification, tenant-reported issues, repairs schedule categorised by urgency, overall condition rating, and follow-up actions. Use the detailed descriptions provided in the form data. Incorporate ALL form data.',
      'Generate an enhanced professional property inspection report with: cover page, executive summary with overall rating, detailed methodology, comprehensive exterior and interior assessments with photographic evidence references, safety compliance matrix (gas/EICR/smoke alarms/CO alarms/fire safety with certificate dates and status), Decent Homes Standard compliance scoring against all four criteria, Awaab\'s Law hazard protocol with response timescales (24hrs emergency, 7 days urgent, 28 days routine), plumbing and heating system assessment, ventilation and damp risk evaluation, tenant-reported issues with response plan, prioritised repairs schedule with estimated costs and timelines, maintenance forecast, overall condition scorecard, and action plan with responsibilities and deadlines. Incorporate ALL form data.',
      'Generate a premium professional-grade property inspection report suitable for regulatory and legal evidence. Include: professional cover page, terms of reference, inspection methodology (HHSRS compliant), executive condition summary with RAG status, comprehensive exterior structural assessment (roof, walls, windows, doors, grounds with photographic cross-references), detailed interior room-by-room assessment with condition grading matrix (A-E), complete safety compliance audit (Gas Safety CP12 status and expiry, EICR status and expiry with C1/C2/FI codes if applicable, smoke alarm locations and test results per floor, CO alarm locations and test results, fire safety including escape routes and fire doors for HMOs, EPC rating and recommendation), Decent Homes Standard compliance assessment against all four pillars (reasonable repair, modern facilities, thermal comfort, free from Category 1 hazards) with scoring matrix, Awaab\'s Law compliance framework with categorised hazards and mandatory response timescales, comprehensive plumbing assessment, heating system evaluation with efficiency recommendations, ventilation and condensation risk analysis with recommendations, tenant satisfaction record, comprehensive repairs and maintenance schedule (emergency/urgent/routine/planned with estimated costs, responsible party, and completion deadlines), 12-month maintenance forecast, condition comparison with previous inspection (if periodic), risk register, overall property compliance scorecard, and signed action plan with landlord obligations and timelines. Format as court-admissible evidence. Incorporate ALL form data with professional formatting.'
    )
  },
  'right-to-rent': {
    formSections: {
      details: {
        id: 'details',
        title: 'Right to Rent Checklist',
        fields: [
          {
            id: 'landlordName',
            type: 'text',
            question: 'What is the landlord\'s full legal name?',
            validation: { required: true },
            description: 'Under the Immigration Act 2014, landlords are legally required to verify that tenants have the right to rent in England before the start of a tenancy.',
            examples: ['John Smith', 'Property Management Ltd', 'Sarah Williams'],
            exampleHint: 'Full name or registered company name of the landlord'
          },
          {
            id: 'tenantName',
            type: 'text',
            question: 'What is the prospective tenant\'s full legal name as it appears on their identity documents?',
            validation: { required: true },
            description: 'The name must match exactly what appears on the immigration document being verified.',
            examples: ['Emma Johnson', 'Dr. Rajesh Patel', 'Marie-Claire Dupont'],
            exampleHint: 'Full name exactly as shown on identity documents'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the full address of the rental property being let?',
            validation: { required: true },
            description: 'The Right to Rent check must be completed for each property where the tenant will reside as their only or main home.',
            examples: ['42 High Street, London, SW1A 1AA', 'Flat 3, The Maples, Bristol, BS1 2AA'],
            exampleHint: 'Complete property address including postcode'
          },
          {
            id: 'checkDate',
            type: 'date',
            question: 'On what date was the Right to Rent check carried out?',
            validation: { required: true },
            description: 'The check must be completed no earlier than 28 days before the tenancy start date. Record the exact date the documents were verified.',
            examples: ['2025-03-01', '2025-04-15'],
            exampleHint: 'Must be within 28 days before tenancy start'
          },
          {
            id: 'tenantNationality',
            type: 'select',
            question: 'What is the tenant\'s nationality or immigration status category?',
            options: ['British citizen', 'EU settled/pre-settled status', 'Non-EU with visa', 'Refugee status', 'Other'],
            validation: { required: true },
            description: 'This determines which documents are acceptable for verification. British and Irish citizens have an automatic right to rent. EU citizens need settled or pre-settled status under the EU Settlement Scheme.',
            exampleHint: 'Select the category that best matches the tenant\'s immigration status'
          },
          {
            id: 'documentType',
            type: 'select',
            question: 'Which type of identity document was verified for this Right to Rent check?',
            options: ['UK passport', 'Biometric residence permit', 'Share code', 'EU settlement scheme', 'Other'],
            validation: { required: true },
            description: 'Only documents from the Home Office approved list (List A for unlimited right, List B for time-limited right) are acceptable. Original documents must be checked in the presence of the holder.',
            exampleHint: 'Select the primary document used for verification'
          },
          {
            id: 'documentNumber',
            type: 'text',
            question: 'What is the unique reference number on the verified identity document?',
            validation: { required: true },
            description: 'Record the passport number, BRP number, or share code exactly as it appears on the document. This creates an audit trail for your statutory excuse.',
            examples: ['533456789 (passport)', 'ZU1234567 (BRP)', 'W4K7X9B2R1 (share code)'],
            exampleHint: 'Passport number, BRP number, or Home Office share code'
          },
          {
            id: 'documentExpiry',
            type: 'date',
            question: 'What is the expiry date shown on the identity document?',
            validation: { required: false },
            description: 'Required for time-limited immigration status. If the document has a time limit, a follow-up check must be conducted before it expires to maintain your statutory excuse against a civil penalty.',
            examples: ['2026-06-30', '2027-12-01'],
            exampleHint: 'Leave blank for documents with no expiry (e.g., British passport holders)'
          },
          {
            id: 'onlineCheckUsed',
            type: 'radio',
            question: 'Was the Home Office online Right to Rent checking service used for this verification?',
            options: ['Yes', 'No - Original documents'],
            validation: { required: true },
            description: 'The Home Office online service is mandatory for holders of Biometric Residence Cards/Permits and EU Settlement Scheme status. For British and Irish passport holders, manual document checks are also acceptable.',
            exampleHint: 'Online checks produce a digital share code as proof of verification'
          },
          {
            id: 'followUpRequired',
            type: 'radio',
            question: 'Is a follow-up Right to Rent check required before the document expires?',
            options: ['Yes - Time-limited status', 'No - Indefinite leave'],
            validation: { required: true },
            description: 'If the tenant has time-limited permission to be in the UK, you must conduct a follow-up check before their permission expires. Failure to do so means you lose your statutory excuse and could face a civil penalty of up to £3,000 per occupier.',
            exampleHint: 'Time-limited visas and permits require follow-up checks'
          },
          {
            id: 'copiesRetained',
            type: 'radio',
            question: 'Have clear copies of the verified documents been retained securely on file?',
            options: ['Yes'],
            validation: { required: true },
            description: 'You must retain clear, unalterable copies of all documents checked for the duration of the tenancy and for at least 1 year after the tenancy ends. This provides your statutory excuse against civil penalties.',
            exampleHint: 'Copies must be retained for tenancy duration plus 1 year'
          },
          {
            id: 'gdprCompliant',
            type: 'radio',
            question: 'Has GDPR compliance been confirmed for the storage and handling of the tenant\'s identity documents?',
            options: ['Yes'],
            validation: { required: true },
            description: 'Under UK GDPR, identity documents must be stored securely with restricted access, used only for the purpose of Right to Rent verification, and the tenant must be informed of their data protection rights. A data protection notice should be provided.',
            exampleHint: 'Ensure secure storage, limited access, and data subject rights are respected'
          }
        ]
      }
    },
    prompts: createDocumentPrompts('Right to Rent checklists', 'Generate standard UK Right to Rent verification checklist with document types, verification methods, record retention, and landlord penalty awareness. Incorporate ALL form data.', 'Generate enhanced Right to Rent checklist with comprehensive document verification procedures, Home Office online check instructions, follow-up requirements for time-limited status, GDPR compliance, and discrimination prevention protocols. Incorporate ALL form data.', 'Generate premium Right to Rent compliance documentation with exhaustive verification procedures, acceptable document list, online vs manual check protocols, retention requirements (1 year post-tenancy), GDPR data protection framework, penalty avoidance (£3,000), discrimination safeguards, audit trail, and defendable record-keeping. Incorporate ALL form data.')
  },
  'dispute-framework': {
    formSections: {
      details: {
        id: 'details',
        title: 'Dispute Resolution Framework',
        fields: [
          {
            id: 'party1Name',
            type: 'text',
            question: 'What is the full name of the first party involved in the dispute?',
            validation: { required: true },
            description: 'This is typically the party initiating the dispute resolution process. Provide the full legal name as it appears on the tenancy agreement.',
            examples: ['John Smith', 'Residential Property Ltd', 'Emma Johnson'],
            exampleHint: 'Full legal name of the party raising the dispute'
          },
          {
            id: 'party2Name',
            type: 'text',
            question: 'What is the full name of the second party involved in the dispute?',
            validation: { required: true },
            description: 'This is the responding party. Provide the full legal name as it appears on the tenancy agreement.',
            examples: ['Sarah Williams', 'Premier Lettings Agency', 'Michael Chen'],
            exampleHint: 'Full legal name of the responding party'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the full address of the property related to this dispute?',
            validation: { required: true },
            description: 'The property address helps identify the tenancy and jurisdiction for resolution.',
            examples: ['42 High Street, London, SW1A 1AA', 'Flat 5B, Riverside Tower, Leeds, LS1 4AP'],
            exampleHint: 'Complete property address including postcode'
          },
          {
            id: 'disputeCategory',
            type: 'select',
            question: 'Which category best describes the nature of this dispute?',
            options: ['Rent arrears', 'Property damage', 'Deposit deductions', 'Repairs/maintenance', 'Harassment/illegal eviction', 'Lease breach', 'Other'],
            validation: { required: true },
            description: 'The dispute category determines the most appropriate resolution pathway. For example, deposit disputes may be referred to a tenancy deposit scheme, while harassment cases may require urgent court action.',
            exampleHint: 'Select the primary category that best fits the dispute'
          },
          {
            id: 'disputeValue',
            type: 'number',
            question: 'What is the estimated financial value of the dispute in GBP?',
            validation: { required: false, min: 0 },
            description: 'The financial value helps determine proportionality and the most cost-effective resolution pathway. Claims under £10,000 are typically suitable for small claims track.',
            examples: ['1500', '5000', '25000'],
            exampleHint: 'Estimated total financial value of all claims'
          },
          {
            id: 'disputeUrgency',
            type: 'select',
            question: 'How urgent is this dispute and what response timeframe is needed?',
            options: ['Emergency - Immediate action needed', 'Urgent - 7 days', 'Standard - 14 days', 'Non-urgent'],
            validation: { required: true },
            description: 'Emergency situations include illegal eviction, harassment, or health and safety risks. Urgency affects whether interim injunctions or emergency court orders may be necessary.',
            exampleHint: 'Select based on the immediacy of risk or harm'
          },
          {
            id: 'informalResolutionAttempted',
            type: 'radio',
            question: 'Has an informal resolution been attempted before initiating this formal dispute process?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'Courts and tribunals expect parties to have attempted to resolve disputes informally before resorting to formal proceedings. This is a requirement under the Pre-Action Protocol.',
            exampleHint: 'Informal attempts include direct conversations, emails, or phone calls'
          },
          {
            id: 'communicationHistory',
            type: 'textarea',
            question: 'Please provide a summary of all communications between the parties regarding this dispute, including dates, methods, and outcomes.',
            validation: { required: true },
            description: 'A clear communication history strengthens your case and demonstrates good faith efforts. Include dates, who initiated contact, what was discussed, and any agreements or outcomes.',
            examples: [
              '15/01/2025: Emailed landlord about damp issue with photos. 22/01/2025: Landlord acknowledged by phone, promised repairs within 2 weeks. 10/02/2025: No repairs completed, sent follow-up email. No response received.',
              '01/03/2025: Tenant notified of rent shortfall by letter. 05/03/2025: Tenant called to discuss financial difficulties. 10/03/2025: Agreed payment plan by email but tenant failed to make first payment on 15/03/2025.'
            ],
            exampleHint: 'Chronological summary with dates, methods of contact, and outcomes',
            rows: 5,
            placeholder: 'Provide a chronological summary of all communications between the parties, including dates, methods (email, phone, letter), what was discussed, and any outcomes or agreements reached...'
          },
          {
            id: 'preferredResolution',
            type: 'select',
            question: 'Which resolution pathway do you prefer for resolving this dispute?',
            options: ['Direct negotiation', 'Mediation', 'Tribunal (First-tier)', 'County Court', 'Unsure - Need guidance'],
            validation: { required: true },
            description: 'Each pathway has different costs, timescales, and formality levels. Direct negotiation is free and fastest. Mediation typically costs £100-£200. Tribunals are free for tenants. County Court fees range from £70 to £10,000+ depending on claim value.',
            exampleHint: 'Consider costs, timeframes, and the importance of preserving the relationship'
          },
          {
            id: 'evidenceAvailable',
            type: 'checkbox',
            question: 'Which types of evidence do you currently have available to support your case?',
            options: ['Written correspondence', 'Photos/videos', 'Witness statements', 'Financial records', 'Tenancy agreement', 'Inspection reports'],
            validation: { required: true },
            description: 'Strong evidence is critical for successful dispute resolution. Documentary evidence (written records, photos, receipts) is generally the most persuasive.',
            exampleHint: 'Select all categories of evidence you can provide'
          },
          {
            id: 'desiredOutcome',
            type: 'textarea',
            question: 'What is your desired outcome from this dispute resolution process? Please describe in detail what you would like to achieve.',
            validation: { required: true },
            description: 'Be specific about what you want to achieve, including any financial compensation, repairs, behavioral changes, or other remedies. A clear desired outcome helps guide the resolution strategy.',
            examples: [
              'Full repair of the damp and mould in the bedroom and living room within 14 days, compensation of £2,000 for damage to personal belongings, and a written commitment to regular property inspections.',
              'Recovery of £3,500 in rent arrears with a structured payment plan of £500/month, plus late payment interest at the contractual rate.'
            ],
            exampleHint: 'Be specific about the remedies, compensation, or actions you are seeking',
            rows: 4,
            placeholder: 'Describe what you want to achieve, including any financial amounts, specific repairs or actions, timescales, and any other remedies you are seeking...'
          },
          {
            id: 'acceptableCompromise',
            type: 'textarea',
            question: 'What compromise or alternative outcome would you be willing to accept if your ideal resolution is not achievable?',
            validation: { required: false },
            description: 'Identifying acceptable compromises in advance helps facilitate faster resolution. Mediation and negotiation are more successful when parties have considered their fallback positions.',
            examples: [
              'Would accept partial compensation of £1,000 if repairs are completed within 7 days and landlord agrees to quarterly property inspections going forward.',
              'Would accept a payment plan for arrears over 6 months provided tenant pays current rent on time and provides a guarantor.'
            ],
            exampleHint: 'Consider your minimum acceptable terms and what you could give up',
            rows: 3,
            placeholder: 'Describe what alternative outcomes or compromises you would find acceptable as a fallback position...'
          },
          {
            id: 'relationshipOngoing',
            type: 'radio',
            question: 'Is the tenancy relationship between the parties still ongoing?',
            options: ['Yes - Tenancy continues', 'No - Tenancy ended'],
            validation: { required: true },
            description: 'If the tenancy is ongoing, preserving the landlord-tenant relationship is typically a priority, making mediation or negotiation preferable to adversarial court proceedings. Affects whether relationship preservation is priority.'
          }
        ]
      }
    },
    prompts: createDocumentPrompts('dispute resolution frameworks', 'Generate standard dispute resolution framework template with dispute categorization, resolution pathway options (negotiation, mediation, tribunal, court), evidence requirements, and timescales. Incorporate ALL form data.', 'Generate enhanced dispute resolution framework with comprehensive pathway analysis, proportionality assessment, cost-benefit analysis for each route, evidence strength evaluation, 2025 tribunal rights, mediation protocols, and step-by-step escalation procedures. Incorporate ALL form data.', 'Generate premium dispute resolution framework with exhaustive pathway mapping, disputes categorization matrix, resolution method suitability analysis (negotiation for <£1000, mediation for preserving relationships, tribunal for tenant rights, court for >£10000), evidence requirements per pathway, cost projections (mediation £100-200, tribunal free for tenants, court £70-10000), success probability assessment, timescale estimates, proportionality evaluation, relationship preservation considerations, 2025 tenant rights under Renters\' Rights Bill, tribunal challenge procedures for rent increases, and comprehensive action plan with deadlines. Incorporate ALL form data.')
  },
  'mediation-request': {
    formSections: {
      details: {
        id: 'details',
        title: 'Mediation Request Form',
        fields: [
          {
            id: 'requestingPartyName',
            type: 'text',
            question: 'What is the full name of the party requesting mediation?',
            validation: { required: true },
            description: 'Provide the full legal name of the person or organisation submitting this mediation request.',
            examples: ['John Smith', 'Emma Johnson', 'Premier Property Management Ltd'],
            exampleHint: 'Full legal name as it appears on the tenancy agreement'
          },
          {
            id: 'requestingPartyRole',
            type: 'select',
            question: 'What is your role in relation to this tenancy?',
            options: ['Landlord', 'Tenant', 'Agent', 'Guarantor'],
            validation: { required: true },
            description: 'Your role determines your rights and obligations within the mediation process.',
            exampleHint: 'Select the role that best describes your relationship to the tenancy'
          },
          {
            id: 'requestingPartyContact',
            type: 'text',
            question: 'What is your preferred contact email address or phone number for mediation correspondence?',
            validation: { required: true },
            description: 'The mediator and other party will use this contact information for all mediation-related communications.',
            examples: ['john.smith@email.com', '07700 900123', 'admin@propertymanagement.co.uk'],
            exampleHint: 'Email address or phone number for mediation communications'
          },
          {
            id: 'respondingPartyName',
            type: 'text',
            question: 'What is the full name of the other party you wish to mediate with?',
            validation: { required: true },
            description: 'The responding party will be invited to participate in the mediation process.',
            examples: ['Sarah Williams', 'Landlord Properties Ltd', 'Michael Chen'],
            exampleHint: 'Full legal name of the other party in the dispute'
          },
          {
            id: 'respondingPartyContact',
            type: 'text',
            question: 'What is the best contact information you have for the other party (email or phone)?',
            validation: { required: false },
            description: 'If known, providing the other party\'s contact details helps expedite the mediation invitation process.',
            examples: ['sarah.w@outlook.com', '020 7946 0958'],
            exampleHint: 'Email or phone number if known; leave blank if unknown'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the full address of the property that is the subject of the dispute?',
            validation: { required: true },
            description: 'The property address identifies the tenancy and helps the mediator understand the context of the dispute.',
            examples: ['42 High Street, London, SW1A 1AA', 'Flat 3, Oak House, Manchester, M1 4BT'],
            exampleHint: 'Complete property address including postcode'
          },
          {
            id: 'tenancyReference',
            type: 'text',
            question: 'What is the tenancy agreement reference number, if one exists?',
            validation: { required: false },
            description: 'A tenancy reference helps quickly identify the specific agreement and any associated records.',
            examples: ['TEN-2024-00456', 'AST/2025/0012', 'REF-42HS-2024'],
            exampleHint: 'Reference number from your tenancy agreement, if applicable'
          },
          {
            id: 'disputeDescription',
            type: 'textarea',
            question: 'Please provide a detailed chronological description of the dispute, including what happened, when it happened, and its impact.',
            validation: { required: true },
            description: 'A clear, factual account of events helps the mediator understand the dispute and prepare for the session. Present events in date order and focus on facts rather than opinions.',
            examples: [
              'Tenancy started 01/06/2024 at £1,200/month. On 15/09/2024, reported persistent damp and mould in the bedroom to landlord by email with photos. Landlord acknowledged on 20/09/2024 but no repairs were carried out. Sent follow-up emails on 15/10/2024 and 01/11/2024. Environmental Health visited on 15/11/2024 and confirmed Category 1 hazard. Landlord still has not completed repairs as of 01/03/2025.',
              'Tenant fell into arrears from November 2024, owing £3,600 across 3 months. Tenant claims loss of employment. Landlord offered payment plan on 15/01/2025 but no agreement was reached. Informal discussions broke down on 01/02/2025.'
            ],
            exampleHint: 'Chronological account with specific dates, events, and outcomes',
            rows: 5,
            placeholder: 'Describe the dispute chronologically: what happened, when it happened, what actions were taken, and the current situation...'
          },
          {
            id: 'disputeAmountClaimed',
            type: 'number',
            question: 'What is the total financial amount being claimed in this dispute, in GBP?',
            validation: { required: false, min: 0 },
            description: 'Include the total of all financial claims, such as rent arrears, compensation for damage, deposit deductions, or repair costs. This helps the mediator assess proportionality.',
            examples: ['2500', '5000', '15000'],
            exampleHint: 'Total financial value of all claims combined'
          },
          {
            id: 'keyDates',
            type: 'textarea',
            question: 'What are the key dates relevant to this dispute, including incident dates, notice dates, and any upcoming deadlines?',
            validation: { required: true },
            description: 'Listing key dates helps establish a timeline and identify any time-sensitive matters such as limitation periods or court deadlines.',
            examples: [
              'Tenancy start: 01/06/2024. First report of damp: 15/09/2024. Environmental Health inspection: 15/11/2024. Improvement notice issued: 01/12/2024. Compliance deadline: 01/03/2025.',
              'Last rent payment: 31/10/2024. First arrears letter: 15/11/2024. Section 8 notice served: 15/01/2025. Court hearing date: 15/04/2025.'
            ],
            exampleHint: 'List all important dates in chronological order',
            rows: 4,
            placeholder: 'List key dates: tenancy start, incident dates, notices served, deadlines, court dates, etc...'
          },
          {
            id: 'previousResolutionAttempts',
            type: 'textarea',
            question: 'What previous attempts have been made to resolve this dispute, and what were the outcomes?',
            validation: { required: true },
            description: 'Demonstrating that you have attempted to resolve the dispute before requesting mediation is important. Courts expect parties to have explored alternative resolution methods.',
            examples: [
              'Direct negotiation by email on 01/12/2024 - landlord refused to carry out repairs. Phone call on 15/12/2024 - landlord claimed no funds available. Formal letter before action sent 01/01/2025 - no response received within 14-day deadline.',
              'Offered payment plan of £300/month on 15/01/2025 - tenant rejected as unaffordable. Tenant counter-offered £150/month on 20/01/2025 - landlord rejected. No further communication since 01/02/2025.'
            ],
            exampleHint: 'Describe each attempt, the date, method, and outcome',
            rows: 4,
            placeholder: 'Describe all previous attempts to resolve the dispute: direct negotiations, letters, phone calls, and their outcomes...'
          },
          {
            id: 'evidenceSubmitted',
            type: 'checkbox',
            question: 'Which types of supporting evidence will you be submitting with this mediation request?',
            options: ['Tenancy agreement', 'Correspondence', 'Photos/videos', 'Financial records', 'Witness statements', 'Inspection reports', 'Other'],
            validation: { required: true },
            description: 'Submitting relevant evidence helps the mediator prepare and understand the strength of each party\'s position before the session.',
            exampleHint: 'Select all categories of evidence you will provide'
          },
          {
            id: 'preferredMediationDate',
            type: 'date',
            question: 'What is your preferred date for the mediation session to take place?',
            validation: { required: false },
            description: 'Suggesting a preferred date helps with scheduling, though the actual date will need to be agreed with all parties and the mediator.',
            examples: ['2025-04-15', '2025-05-01'],
            exampleHint: 'Allow at least 14 days for the other party to prepare'
          },
          {
            id: 'mediatorPreference',
            type: 'select',
            question: 'Do you have a preference for the type of mediator or mediation service?',
            options: ['Independent mediator', 'Property Ombudsman', 'Tribunal-referred', 'No preference'],
            validation: { required: true },
            description: 'Independent mediators typically charge £100-£200 per party. The Property Ombudsman provides free dispute resolution for certain letting agent disputes. Tribunal-referred mediation is available during First-tier Tribunal proceedings.',
            exampleHint: 'Select your preferred mediator type or choose no preference'
          },
          {
            id: 'specialRequirements',
            type: 'textarea',
            question: 'Are there any special requirements that need to be accommodated during the mediation, such as accessibility needs, language interpretation, or vulnerability considerations?',
            validation: { required: false },
            description: 'Mediators are required to make reasonable adjustments for participants with disabilities and can arrange interpreters, accessible venues, or additional support for vulnerable parties.',
            examples: [
              'Wheelchair accessible venue required. Participant has hearing impairment and requires a British Sign Language interpreter.',
              'Party requires Mandarin interpreter. Participant has anxiety disorder and requests breaks every 45 minutes. Support worker will attend.'
            ],
            exampleHint: 'Describe any accessibility, language, or support needs',
            rows: 3,
            placeholder: 'Describe any special requirements: physical accessibility, language interpretation, mental health considerations, support persons, etc...'
          }
        ]
      }
    },
    prompts: createDocumentPrompts('mediation request forms', 'Generate standard mediation request form with party details, dispute summary, amount claimed, evidence list, and mediation preferences. Incorporate ALL form data.', 'Generate enhanced mediation request form with comprehensive case summary, chronological incident timeline, previous resolution attempts, detailed evidence schedule, preferred dates/locations, mediator selection criteria, and special requirements. Incorporate ALL form data.', 'Generate premium mediation request documentation with exhaustive case presentation (parties, property, tenancy reference, dispute categorization, detailed chronology with exhibits, financial claims breakdown, evidence index with exhibit numbers, witness list, previous negotiations summary, proposed resolution terms, mediation logistics (dates, times, locations, duration), mediator qualifications required, accessibility needs, confidentiality requirements, costs allocation proposal, and supporting documentation appendices. Incorporate ALL form data with professional legal formatting.')
  },
  'evidence-collection': {
    formSections: {
      details: {
        id: 'details',
        title: 'Evidence Collection Checklist',
        fields: [
          {
            id: 'caseType',
            type: 'select',
            question: 'What type of case is this evidence being collected for?',
            options: ['Rent arrears', 'Property damage', 'Deposit dispute', 'Possession claim', 'Disrepair', 'Harassment'],
            validation: { required: true },
            description: 'The case type determines which categories of evidence are most critical and what standards of proof apply.',
            exampleHint: 'Select the primary category that matches your case'
          },
          {
            id: 'partyCollecting',
            type: 'select',
            question: 'Which party is responsible for collecting and organising this evidence?',
            options: ['Landlord', 'Tenant'],
            validation: { required: true },
            description: 'Landlords typically bear the burden of proof in possession claims, while tenants bear the burden in disrepair and deposit claims. This determines the exhibit numbering prefix (L for landlord, T for tenant).',
            exampleHint: 'Select the party who is building the evidence bundle'
          },
          {
            id: 'tenancyAgreementCopy',
            type: 'radio',
            question: 'Do you have a copy of the signed tenancy agreement available?',
            options: ['Yes - Have copy', 'No - Need to obtain'],
            validation: { required: true },
            description: 'The tenancy agreement is the foundational document for almost every tenancy dispute. If you do not have a copy, you can request one from the other party or the letting agent.',
            exampleHint: 'The tenancy agreement is essential evidence in virtually all disputes'
          },
          {
            id: 'correspondenceLog',
            type: 'radio',
            question: 'Do you have a complete log of all correspondence related to this dispute?',
            options: ['Yes', 'Partial', 'No'],
            validation: { required: true },
            description: 'A complete correspondence log should include all emails, letters, text messages, and notes of phone calls with dates. Courts place significant weight on contemporaneous written records.',
            exampleHint: 'Include all emails, letters, texts, and notes of phone calls with dates'
          },
          {
            id: 'photographicEvidence',
            type: 'radio',
            question: 'Have you collected photographic or video evidence with timestamps to support your case?',
            options: ['Yes - Timestamped', 'Some evidence', 'No evidence'],
            validation: { required: true },
            description: 'Timestamped photographs and videos are powerful evidence, particularly in property damage and disrepair cases. Ensure images show the date taken in the metadata.',
            exampleHint: 'Timestamped photos with location data are the strongest visual evidence'
          },
          {
            id: 'witnessesAvailable',
            type: 'radio',
            question: 'Are there any witnesses who can provide statements to support your case?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'Witnesses can include neighbours, contractors, letting agents, environmental health officers, or any third party with relevant knowledge of the facts.',
            exampleHint: 'Consider neighbours, contractors, agents, and professionals who have relevant knowledge'
          },
          {
            id: 'witnessDetails',
            type: 'textarea',
            question: 'Please list the names, roles, and contact details of all available witnesses, along with a brief summary of what each can attest to.',
            validation: { required: false },
            description: 'Witness statements must comply with CPR Part 32 Practice Direction format for court proceedings. Each witness should be able to give evidence on specific facts they personally observed.',
            examples: [
              'Jane Smith (neighbour at No. 44), 07700 900456 - Can confirm persistent noise and anti-social behaviour from September to December 2024. Was present during incident on 15/10/2024.',
              'Bob Thompson (independent surveyor), bob@surveys.co.uk - Inspected property on 01/02/2025 and can confirm Category 1 damp hazard. Report reference: BT-2025-0034.'
            ],
            exampleHint: 'Include name, relationship/role, contact details, and what they can attest to',
            rows: 4,
            placeholder: 'List each witness with their name, relationship to the case, contact details, and a brief summary of what they can provide evidence about...'
          },
          {
            id: 'financialRecords',
            type: 'checkbox',
            question: 'Which types of financial records do you have available as evidence?',
            options: ['Bank statements', 'Payment receipts', 'Invoices', 'Rent arrears schedule', 'None'],
            validation: { required: true },
            description: 'Financial records are essential for rent arrears, deposit, and compensation claims. Bank statements provide independent verification of payments made or received.',
            exampleHint: 'Select all financial documents you can provide'
          },
          {
            id: 'inspectionReports',
            type: 'radio',
            question: 'Do you have any property inspection reports, such as inventory, check-in, check-out, or mid-tenancy inspection reports?',
            options: ['Yes - Have reports', 'No'],
            validation: { required: true },
            description: 'Inspection reports establish the condition of the property at key points in time and are crucial evidence in deposit disputes and property damage claims.',
            exampleHint: 'Include inventory reports, check-in/check-out reports, and periodic inspection reports'
          },
          {
            id: 'safetyCertificates',
            type: 'checkbox',
            question: 'Which current safety certificates do you have available for the property?',
            options: ['Gas Safety', 'EICR', 'EPC', 'None applicable'],
            validation: { required: true },
            description: 'Safety certificates are legally required and their absence can be used as evidence of landlord non-compliance. Gas Safety certificates must be renewed annually, EICR every 5 years.',
            exampleHint: 'Select all valid safety certificates currently held'
          },
          {
            id: 'statutoryNotices',
            type: 'radio',
            question: 'Do you have copies of all statutory notices that have been served in relation to this tenancy?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'Statutory notices include Section 8 possession notices, Section 13 rent increase notices, and any improvement notices from the local authority. Proof of proper service is essential.',
            exampleHint: 'Include Section 8, Section 13, improvement notices, and proof of service'
          },
          {
            id: 'expertEvidence',
            type: 'textarea',
            question: 'Is any expert evidence required for this case? If so, please describe what type of expert is needed and why.',
            validation: { required: false },
            description: 'Expert evidence may include surveyor reports for property condition, medical reports for health impacts, or accountant reports for financial losses. For court proceedings, experts may need to be jointly instructed under CPR Part 35.',
            examples: [
              'Independent building surveyor needed to assess extent of structural damp and estimated repair costs. Medical report from GP confirming respiratory condition exacerbated by mould exposure.',
              'Chartered surveyor to provide market rent valuation for tribunal rent increase challenge. Report on comparable local properties required.'
            ],
            exampleHint: 'Describe the type of expert, their purpose, and what they would assess',
            rows: 3,
            placeholder: 'Describe any expert evidence needed: surveyor reports, medical evidence, valuations, or other specialist assessments...'
          },
          {
            id: 'evidenceAuthentication',
            type: 'textarea',
            question: 'How will the evidence be authenticated and verified for use in proceedings? Please describe your planned approach.',
            validation: { required: true },
            description: 'Courts require evidence to be properly authenticated. Original documents are preferred. Copies must be certified as true copies. Digital evidence should include metadata. Witness statements must include a statement of truth.',
            examples: [
              'Original tenancy agreement and correspondence retained. Photos have EXIF metadata with dates. Bank statements obtained directly from bank. Witness statements will include statements of truth as required by CPR Part 32.',
              'Certified copies of all documents prepared by solicitor. Digital photos authenticated with original device metadata. Expert report signed with statement of truth and declaration of compliance with CPR Part 35.'
            ],
            exampleHint: 'Describe how originals, copies, digital evidence, and statements will be verified',
            rows: 3,
            placeholder: 'Describe your authentication approach: original documents, certified copies, digital metadata preservation, statements of truth, etc...'
          }
        ]
      }
    },
    prompts: createDocumentPrompts('evidence collection checklists', 'Generate standard evidence collection checklist with document types (tenancy agreement, correspondence, photos, financial records), witness information, and authentication requirements. Incorporate ALL form data.', 'Generate enhanced evidence collection checklist with comprehensive evidence categories, court submission standards, exhibit numbering system, witness statement preparation guidance, expert evidence requirements, and chain of custody protocols. Incorporate ALL form data.', 'Generate premium evidence collection framework with exhaustive evidence taxonomy (documentary, photographic, testimonial, expert, real), collection protocols with metadata requirements (dates, timestamps, authentication), court admissibility standards (CPR Part 32), exhibit numbering (landlord L1-L99, tenant T1-T99), witness statement templates (CPR 32 PD format), expert evidence procedures (single joint expert, party experts), authentication methods (original, certified copy, witness verification), chain of custody documentation, disclosure obligations, privilege considerations, and comprehensive evidence index with descriptions. Incorporate ALL form data.')
  },
  'communication-log': {
    formSections: {
      details: {
        id: 'details',
        title: 'Communication Log Template',
        fields: [
          {
            id: 'landlordName',
            type: 'text',
            question: 'What is the landlord\'s full name for this communication log?',
            validation: { required: true },
            description: 'The landlord\'s name will appear as a primary party in the communication log header and entries.',
            examples: ['John Smith', 'Property Management Ltd', 'Sarah Williams'],
            exampleHint: 'Full name or company name of the landlord'
          },
          {
            id: 'tenantName',
            type: 'text',
            question: 'What is the tenant\'s full name for this communication log?',
            validation: { required: true },
            description: 'The tenant\'s name will appear as a primary party in the communication log header and entries.',
            examples: ['Emma Johnson', 'Michael Chen', 'Dr. Rebecca Taylor'],
            exampleHint: 'Full name of the tenant as it appears on the tenancy agreement'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the full address of the property this communication log relates to?',
            validation: { required: true },
            description: 'The property address identifies the tenancy and provides context for the communications being logged.',
            examples: ['42 High Street, London, SW1A 1AA', 'Flat 5B, Riverside Tower, Leeds, LS1 4AP'],
            exampleHint: 'Complete property address including postcode'
          },
          {
            id: 'logStartDate',
            type: 'date',
            question: 'From what date should this communication log begin recording entries?',
            validation: { required: true },
            description: 'The start date establishes the beginning of the formal record-keeping period. For dispute documentation, this should ideally be the date the issue first arose.',
            examples: ['2025-01-01', '2024-09-15'],
            exampleHint: 'Start from when the issue first arose or the tenancy began'
          },
          {
            id: 'communicationMethods',
            type: 'checkbox',
            question: 'Which methods of communication are being used between the parties and need to be tracked?',
            options: ['Email', 'Phone', 'Text/SMS', 'Letter', 'In-person', 'Portal/app'],
            validation: { required: true },
            description: 'Tracking all communication methods ensures a complete record. Written methods (email, letter) create the strongest evidence trail. Phone calls and in-person meetings should be followed up with written confirmations.',
            exampleHint: 'Select all methods being used between the parties'
          },
          {
            id: 'logPurpose',
            type: 'select',
            question: 'What is the primary purpose of maintaining this communication log?',
            options: ['Routine tenancy management', 'Dispute documentation', 'Court proceedings', 'General record-keeping'],
            validation: { required: true },
            description: 'The purpose determines the level of detail required. Court proceedings require the most thorough documentation, including exact times, verbatim content where possible, and exhibit references.',
            exampleHint: 'Select the primary reason for creating this log'
          },
          {
            id: 'responseTimeframes',
            type: 'textarea',
            question: 'What are the expected response timeframes for different types of communications and issues?',
            validation: { required: true },
            description: 'Defining response timeframes creates clear expectations and helps identify when escalation is needed. These should be documented at the outset to support any future claims of unreasonable delay.',
            examples: [
              'Emergency repairs (health/safety risk): Immediate response, 24 hours to attend. Urgent repairs (no heating/hot water): Response within 24 hours, repair within 3 days. Routine maintenance: Response within 5 working days, repair within 28 days. General enquiries: Response within 5 working days.',
              'Rent-related queries: Response within 48 hours. Repair requests: Acknowledgement within 24 hours, resolution timeline within 5 days. Tenancy administration: Response within 5 working days. Complaints: Acknowledgement within 2 working days, full response within 10 working days.'
            ],
            exampleHint: 'Define timeframes by priority: emergency, urgent, routine, and general queries',
            rows: 4,
            placeholder: 'Define expected response timeframes for different categories: emergency issues, urgent repairs, routine maintenance, general queries, complaints, etc...'
          },
          {
            id: 'escalationProcedure',
            type: 'textarea',
            question: 'What is the escalation procedure if communications go unanswered or issues are not resolved within the expected timeframes?',
            validation: { required: true },
            description: 'A documented escalation procedure demonstrates due diligence and helps build a case if formal proceedings become necessary. Include who to escalate to and how.',
            examples: [
              'Step 1: Follow-up email or letter after response deadline passes. Step 2: Second follow-up within 7 days, sent by recorded delivery. Step 3: Formal complaint to letting agent or property manager. Step 4: Contact local council Environmental Health (for repairs) or seek legal advice. Step 5: Consider mediation or tribunal application.',
              'Day 1 after deadline: Phone call follow-up with written confirmation. Day 7: Formal written reminder sent by email and post. Day 14: Letter before action citing Pre-Action Protocol requirements. Day 28: Commence formal proceedings if no resolution.'
            ],
            exampleHint: 'Describe step-by-step escalation with timeframes and methods',
            rows: 4,
            placeholder: 'Describe the escalation steps: initial follow-up, formal reminders, complaints procedures, and when to seek legal advice or formal proceedings...'
          },
          {
            id: 'gdprCompliant',
            type: 'radio',
            question: 'Has GDPR-compliant storage been arranged for all communications in this log?',
            options: ['Yes'],
            validation: { required: true },
            description: 'Under UK GDPR, communication records containing personal data must be stored securely with appropriate access controls, a clear retention policy, and the ability to respond to data subject access requests. Secure storage, access controls, retention policy.'
          },
          {
            id: 'retentionPeriod',
            type: 'select',
            question: 'How long should the communication records be retained after the tenancy ends?',
            options: ['Duration of tenancy', '6 years post-tenancy', 'Ongoing for legal matters'],
            validation: { required: true },
            description: 'The limitation period for most tenancy-related claims is 6 years from the date of breach (Limitation Act 1980). For court proceedings, records should be retained until all matters are fully resolved.',
            exampleHint: 'Six years post-tenancy covers the standard limitation period for most claims'
          }
        ]
      }
    },
    prompts: createDocumentPrompts('communication log templates', 'Generate standard communication log template with date/time, method, participants, summary, actions, and response tracking. Incorporate ALL form data.', 'Generate enhanced communication log with comprehensive tracking (date, time, method, initiator, recipient, subject, detailed notes, attachments, actions required, deadlines, response received, escalation flags), filtering by category, response time monitoring, and dispute preparation features. Incorporate ALL form data.', 'Generate premium communication management system with exhaustive documentation framework: log entry template (unique reference, date, time, communication method, initiator, recipient(s), cc, subject/re, detailed content summary, tone/manner, attachments list, action items with owners and deadlines, response required by date, follow-up scheduled, category tags), chronological index, search functionality parameters, response time analysis, escalation tracking, dispute evidence compilation, GDPR compliance framework (secure storage, access controls, data retention 6 years, deletion protocols, subject access request procedures), audit trail, automated reminders system, and comprehensive reporting. Incorporate ALL form data.')
  },
  'settlement-agreement': {
    formSections: {
      details: {
        id: 'details',
        title: 'Settlement Agreement Template',
        fields: [
          {
            id: 'party1Name',
            type: 'text',
            question: 'What is the full legal name of the first party to this settlement agreement?',
            validation: { required: true },
            description: 'Provide the full legal name as it will appear on the binding settlement agreement. For companies, use the registered name.',
            examples: ['John Smith', 'Premier Lettings Ltd', 'Dr. Sarah Williams'],
            exampleHint: 'Full legal name or registered company name'
          },
          {
            id: 'party1Role',
            type: 'select',
            question: 'What is the first party\'s role in the tenancy?',
            options: ['Landlord', 'Tenant'],
            validation: { required: true },
            description: 'The party\'s role determines their rights and obligations under the settlement terms.',
            exampleHint: 'Select the role of the first party in the tenancy'
          },
          {
            id: 'party2Name',
            type: 'text',
            question: 'What is the full legal name of the second party to this settlement agreement?',
            validation: { required: true },
            description: 'Provide the full legal name as it will appear on the binding settlement agreement.',
            examples: ['Emma Johnson', 'Residential Holdings PLC', 'Michael Chen'],
            exampleHint: 'Full legal name or registered company name'
          },
          {
            id: 'party2Role',
            type: 'select',
            question: 'What is the second party\'s role in the tenancy?',
            options: ['Landlord', 'Tenant'],
            validation: { required: true },
            description: 'The party\'s role determines their rights and obligations under the settlement terms.',
            exampleHint: 'Select the role of the second party in the tenancy'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the full address of the property that is the subject of this settlement?',
            validation: { required: true },
            description: 'The property address identifies the tenancy to which this settlement agreement relates.',
            examples: ['42 High Street, London, SW1A 1AA', 'Flat 5B, Riverside Tower, Leeds, LS1 4AP'],
            exampleHint: 'Complete property address including postcode'
          },
          {
            id: 'disputeBackground',
            type: 'textarea',
            question: 'Please provide a brief background summary of the dispute that this settlement agreement is intended to resolve.',
            validation: { required: true },
            description: 'The dispute background forms the recitals of the settlement agreement, establishing the context for the agreed terms. Keep this factual and neutral, avoiding blame or inflammatory language.',
            examples: [
              'Dispute arose regarding the condition of the property, specifically persistent damp and mould in the bedroom reported by the tenant in September 2024. The tenant claimed the landlord failed to carry out repairs within a reasonable timeframe, and the landlord contended the damp was caused by tenant lifestyle. Both parties wish to settle the matter without further proceedings.',
              'The tenant fell into rent arrears totalling £4,800 over 4 months from November 2024 to February 2025. The landlord served a Section 8 notice on Ground 8. The tenant has since secured new employment and both parties agree to settle the arrears through a structured payment plan.'
            ],
            exampleHint: 'Provide a neutral, factual summary of the dispute and why parties are settling',
            rows: 4,
            placeholder: 'Summarise the dispute: what the issues are, how they arose, and why the parties are seeking to settle...'
          },
          {
            id: 'settlementDate',
            type: 'date',
            question: 'What is the date on which this settlement agreement will take effect?',
            validation: { required: true },
            description: 'The settlement date is when the agreement becomes binding on both parties. All obligations and deadlines run from this date unless otherwise specified.',
            examples: ['2025-04-01', '2025-05-15'],
            exampleHint: 'Date the agreement becomes binding on both parties'
          },
          {
            id: 'financialTerms',
            type: 'textarea',
            question: 'What are the financial terms of the settlement, including amounts to be paid, payment schedule, and payment methods?',
            validation: { required: true },
            description: 'Be specific about all financial aspects: exact amounts, who pays whom, payment dates, payment methods (bank transfer, cheque), and what happens if a payment is missed.',
            examples: [
              'Landlord to pay tenant £3,500 compensation within 14 days of settlement date by bank transfer. Tenant to waive all further claims for disrepair. Payment to be made to tenant\'s nominated bank account.',
              'Tenant to pay arrears of £4,800 in 6 monthly instalments of £800, starting 1st of the month following settlement date. Payments by standing order. If any payment is more than 7 days late, the full remaining balance becomes immediately due.'
            ],
            exampleHint: 'Specify exact amounts, payment dates, methods, and default provisions',
            rows: 4,
            placeholder: 'Detail the financial terms: amounts, who pays whom, payment schedule, payment method, and consequences of missed payments...'
          },
          {
            id: 'propertyActions',
            type: 'textarea',
            question: 'What property-related actions are agreed as part of this settlement, such as repairs, cleaning, access arrangements, or key returns?',
            validation: { required: false },
            description: 'Specify any physical actions required at the property, including who is responsible, timescales, and standards expected.',
            examples: [
              'Landlord to complete damp remediation works by a qualified contractor within 28 days of settlement. Tenant to provide reasonable access for works with 48 hours\' notice. Landlord to provide completion certificate.',
              'Tenant to return property in clean condition with all keys by the termination date. Professional end-of-tenancy clean to be arranged by tenant at tenant\'s cost. Landlord to conduct final inspection within 3 days of key return.'
            ],
            exampleHint: 'Describe repairs, cleaning, access, key returns, and inspection arrangements',
            rows: 3,
            placeholder: 'Describe property-related actions: repairs to be completed, cleaning standards, access arrangements, key returns, inspections...'
          },
          {
            id: 'tenancyTermination',
            type: 'radio',
            question: 'Does this settlement agreement include the termination of the tenancy?',
            options: ['Yes', 'No - Tenancy continues'],
            validation: { required: true },
            description: 'If the tenancy is being terminated as part of the settlement, a surrender agreement should be included to ensure the termination is legally effective.',
            exampleHint: 'Select whether the tenancy ends as part of this settlement'
          },
          {
            id: 'terminationDate',
            type: 'date',
            question: 'If the tenancy is being terminated, on what date will the tenancy come to an end?',
            validation: { required: false },
            description: 'The termination date should allow sufficient time for the tenant to find alternative accommodation. Both parties must agree to surrender the tenancy on this date.',
            examples: ['2025-05-31', '2025-06-30'],
            exampleHint: 'Allow reasonable time for the tenant to vacate'
          },
          {
            id: 'depositReturn',
            type: 'textarea',
            question: 'What are the arrangements for the return of the tenancy deposit, including any agreed deductions?',
            validation: { required: false },
            description: 'The deposit must be dealt with in accordance with the relevant deposit protection scheme rules. Specify the total deposit amount, any agreed deductions with reasons, the net amount to be returned, and the timeframe for return.',
            examples: [
              'Deposit of £1,442 held with DPS. Agreed deductions: £200 for professional cleaning, £150 for replacement of damaged carpet in hallway. Net return of £1,092 to be processed through DPS within 10 days of key return.',
              'Full deposit of £980 held with MyDeposits to be returned in full within 10 days of tenancy termination, with no deductions. Both parties to confirm agreement through the deposit scheme portal.'
            ],
            exampleHint: 'Specify deposit amount, deductions, net return, and timeframe',
            rows: 3,
            placeholder: 'Detail deposit arrangements: total amount, any agreed deductions with reasons, net amount to return, scheme to process through, and timeframe...'
          },
          {
            id: 'confidentiality',
            type: 'radio',
            question: 'Should this settlement agreement include a confidentiality clause preventing both parties from disclosing the terms?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'Confidentiality clauses prevent parties from discussing the settlement terms with third parties. Exceptions typically include legal advisors, tax authorities, and regulatory bodies.',
            exampleHint: 'Confidentiality is common in settlements involving compensation payments'
          },
          {
            id: 'nonDisparagement',
            type: 'radio',
            question: 'Should this settlement agreement include a non-disparagement clause preventing parties from making negative public statements about each other?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'Non-disparagement clauses prevent parties from making derogatory remarks about each other, including on social media or review platforms. This is particularly important for landlords concerned about online reviews.',
            exampleHint: 'Prevents negative public statements including on social media and review sites'
          },
          {
            id: 'referenceProvision',
            type: 'textarea',
            question: 'What are the agreed terms for providing references following this settlement?',
            validation: { required: false },
            description: 'Reference terms are important for tenants who need landlord references for future rentals. Typically limited to factual confirmation of tenancy dates, rent amount, and payment history.',
            examples: [
              'Landlord agrees to provide a factual reference confirming tenancy dates (01/06/2024 to 31/05/2025), monthly rent of £1,200, and that rent was paid on time during the tenancy. Reference to be provided within 5 working days of request.',
              'Landlord agrees to confirm tenancy details only: property address, tenancy dates, and rent amount. No commentary on tenant conduct or suitability. Reference requests to be directed to landlord\'s email address.'
            ],
            exampleHint: 'Define what the reference will include, response timeframes, and any limitations',
            rows: 3,
            placeholder: 'Describe reference terms: what will be confirmed, any limitations on content, response timeframes, and how requests should be made...'
          },
          {
            id: 'breachConsequences',
            type: 'textarea',
            question: 'What are the consequences if either party breaches the terms of this settlement agreement?',
            validation: { required: true },
            description: 'Breach consequences provide enforcement mechanisms and incentives for compliance. Consider including liquidated damages, the right to reinstate original claims, and provisions for recovering legal costs.',
            examples: [
              'If either party breaches the settlement terms, the non-breaching party may: (1) enforce the agreement through the courts, (2) seek damages for any loss caused by the breach, (3) recover reasonable legal costs incurred in enforcement. The breaching party shall pay interest on any overdue amounts at 8% above the Bank of England base rate.',
              'Material breach entitles the non-breaching party to terminate the settlement agreement and reinstate their original claims. For financial breaches, a 7-day written cure period applies. Breach of confidentiality or non-disparagement provisions entitles the injured party to liquidated damages of £1,000 per breach.'
            ],
            exampleHint: 'Specify remedies for breach: enforcement rights, damages, interest, and cost recovery',
            rows: 4,
            placeholder: 'Describe consequences of breach: enforcement rights, liquidated damages, interest on late payments, right to reinstate claims, cure periods, and cost recovery...'
          },
          {
            id: 'fullFinalSettlement',
            type: 'radio',
            question: 'Is this intended to be a full and final settlement, releasing both parties from all further claims related to this dispute?',
            options: ['Yes'],
            validation: { required: true },
            description: 'A full and final settlement clause releases all parties from any further claims arising out of or in connection with the dispute. This is a mutual waiver that prevents either party from bringing future proceedings on the same matters.'
          }
        ]
      }
    },
    prompts: createDocumentPrompts('settlement agreements', 'Generate standard settlement agreement with parties, dispute background, financial terms, property actions, tenancy termination (if applicable), confidentiality, and full/final settlement clause. Incorporate ALL form data.', 'Generate enhanced settlement agreement with comprehensive terms: parties identification, dispute summary, detailed financial settlement (amounts, schedule, method, default provisions), property-related actions with deadlines, tenancy termination procedures, deposit return arrangements, mutual releases, confidentiality provisions, non-disparagement clauses, reference provision, enforcement mechanisms, breach remedies, costs provisions, and full and final settlement clause. Incorporate ALL form data.', 'Generate premium settlement agreement with exhaustive legal provisions: parties (full names, addresses, representation), recitals (dispute background, tenancy details, negotiation history), agreed terms (detailed financial schedule with payment dates and methods, late payment provisions, property actions with specifications and deadlines, tenancy termination with notice provisions, deposit return with deductions schedule, possession date and condition), mutual covenants (do not sue clauses, release of all claims, confidentiality with exceptions, non-disparagement, cooperation with future matters), reference provision (factual confirmation only), warranties (authority to settle, no other agreements, disclosure of material facts), enforcement (jurisdiction, costs of breach, specific performance rights, liquidated damages), general provisions (entire agreement, amendments, severability, governing law, notices), costs allocation, and execution clause with witness blocks. Incorporate ALL form data with professional legal formatting.')
  },
  'mediation-guide': {
    formSections: {
      details: {
        id: 'details',
        title: 'Mediation Process Guide',
        fields: [
          {
            id: 'mediationDate',
            type: 'date',
            question: 'What date has been scheduled for the mediation session?',
            validation: { required: true },
            description: 'The mediation date should allow both parties sufficient time to prepare their positions and gather evidence. At least 14 days\' notice is recommended.',
            examples: ['2025-04-15', '2025-05-01'],
            exampleHint: 'Allow at least 14 days for both parties to prepare'
          },
          {
            id: 'mediationLocation',
            type: 'text',
            question: 'Where will the mediation session take place (physical address or online platform details)?',
            validation: { required: true },
            description: 'Mediation can take place at a neutral venue, online via video conference, or at the offices of the mediator. A neutral location helps create an impartial atmosphere.',
            examples: ['Mediation Centre, 15 Temple Row, Birmingham, B2 5LG', 'Online via Zoom (link to be provided)', 'Mediator\'s offices, 30 Fleet Street, London, EC4Y 1AA'],
            exampleHint: 'Neutral venue address or online platform details'
          },
          {
            id: 'mediationDuration',
            type: 'select',
            question: 'What is the expected duration of the mediation session?',
            options: ['Half day (3-4 hours)', 'Full day (6-8 hours)', 'Multiple sessions'],
            validation: { required: true },
            description: 'Most tenancy disputes can be resolved in a half day. More complex disputes involving multiple issues or large financial amounts may require a full day or multiple sessions.',
            exampleHint: 'Select based on the complexity and number of issues to resolve'
          },
          {
            id: 'mediatorName',
            type: 'text',
            question: 'What is the full name of the appointed mediator, if already selected?',
            validation: { required: false },
            description: 'If a mediator has been agreed upon or appointed, provide their full name. If not yet selected, leave blank and it will be arranged through the mediation service.',
            examples: ['James Robertson FRICS', 'Dr. Amira Khan CMC', 'Richard Wells CEDR Accredited'],
            exampleHint: 'Full name of the mediator including qualifications, if known'
          },
          {
            id: 'mediatorCredentials',
            type: 'text',
            question: 'What are the mediator\'s professional credentials, accreditations, or relevant qualifications?',
            validation: { required: false },
            description: 'Look for mediators accredited by recognised bodies such as CEDR, CMC, or the Civil Mediation Council. Property dispute experience is particularly valuable.',
            examples: ['CEDR Accredited Mediator, 15 years property dispute experience', 'Civil Mediation Council Registered, RICS Fellow', 'Property Ombudsman Panel Mediator'],
            exampleHint: 'Accreditation body, years of experience, and relevant specialisation'
          },
          {
            id: 'participantNames',
            type: 'text',
            question: 'What are the names of all participants who will attend the mediation session?',
            validation: { required: true },
            description: 'List all parties, their representatives, advisors, and any support persons who will attend. Each participant should have authority to negotiate and settle.',
            examples: ['John Smith (landlord), Emma Johnson (tenant), Mark Davies (landlord\'s solicitor)', 'Sarah Williams (tenant), Property Holdings Ltd represented by James Brown (director)'],
            exampleHint: 'List all attendees with their roles in brackets'
          },
          {
            id: 'legalRepresentation',
            type: 'radio',
            question: 'Will the parties have legal representation at the mediation session?',
            options: ['Yes', 'No', 'One party only'],
            validation: { required: true },
            description: 'Legal representation is not required for mediation but can be helpful for complex disputes. If only one party has representation, the mediator should ensure the unrepresented party is not disadvantaged.',
            exampleHint: 'Legal representation can help but is not mandatory for mediation'
          },
          {
            id: 'groundRules',
            type: 'textarea',
            question: 'What ground rules should be established for the mediation session to ensure a productive and respectful process?',
            validation: { required: true },
            description: 'Ground rules create a safe and constructive environment for negotiation. They should be agreed by all parties at the start of the session and enforced by the mediator throughout.',
            examples: [
              'Respectful communication at all times. No interrupting when the other party is speaking. All discussions are confidential and without prejudice. Mobile phones on silent. Breaks can be requested at any time. Each party will have equal opportunity to present their position.',
              'Active listening required. No personal attacks or blame. Focus on interests and solutions rather than positions. Confidentiality maintained after the session. All parties to participate in good faith. Mediator may call breaks at any time.'
            ],
            exampleHint: 'Define rules for communication, confidentiality, respect, and participation',
            rows: 4,
            placeholder: 'List the key ground rules: respectful communication, confidentiality, no interruptions, equal opportunity to speak, good faith participation, break protocols...'
          },
          {
            id: 'confidentialityScope',
            type: 'textarea',
            question: 'What is the scope of confidentiality for the mediation, and are there any exceptions where information may be disclosed?',
            validation: { required: true },
            description: 'Mediation confidentiality is fundamental to encouraging open discussion. However, there are standard exceptions for illegal activity, safeguarding concerns, and court-ordered disclosure.',
            examples: [
              'All discussions during mediation are strictly confidential and without prejudice. No statements made during mediation may be used in any subsequent legal proceedings. Exceptions: disclosure required by law, safeguarding of vulnerable persons or children, evidence of criminal activity.',
              'Confidentiality applies to all oral and written communications during mediation. The mediator cannot be called as a witness. Information shared in private caucus sessions will not be disclosed to the other party without express consent. Settlement agreement terms are not confidential unless a confidentiality clause is agreed.'
            ],
            exampleHint: 'Define what is covered by confidentiality and any recognised exceptions',
            rows: 4,
            placeholder: 'Define confidentiality scope: what is covered, without prejudice protections, exceptions for legal requirements or safeguarding, caucus confidentiality...'
          },
          {
            id: 'caucusAllowed',
            type: 'radio',
            question: 'Will private caucus sessions be permitted, where the mediator meets each party separately?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'Private caucus sessions allow the mediator to explore each party\'s interests, priorities, and settlement parameters in confidence. Information shared in caucus is not disclosed to the other party without express permission. Mediator meets each party separately.'
          },
          {
            id: 'decisionAuthority',
            type: 'textarea',
            question: 'Who has the authority to make binding decisions and agree to settlement terms on behalf of each party?',
            validation: { required: true },
            description: 'It is essential that the person attending the mediation has full authority to settle the dispute. For companies, this may require a board resolution or director approval. For individuals acting through agents, a power of attorney may be needed.',
            examples: [
              'Landlord John Smith attending in person with full authority to settle. Tenant Emma Johnson attending with her mother as support; Emma has full decision-making authority. No third-party approval required.',
              'Landlord represented by James Brown, Managing Director of Property Holdings Ltd, with board authority to settle up to £15,000. Tenant Sarah Williams attending with McKenzie friend; Sarah has full authority to settle.'
            ],
            exampleHint: 'Confirm who can make binding decisions and any financial limits on authority',
            rows: 3,
            placeholder: 'Identify who has settlement authority for each party, any financial limits, and whether third-party approval is needed...'
          },
          {
            id: 'documentationProcess',
            type: 'textarea',
            question: 'What process will be followed for note-taking and documentation during and after the mediation session?',
            validation: { required: true },
            description: 'Clear documentation processes ensure that any agreement reached is accurately recorded. The mediator typically drafts the settlement agreement, which both parties review and sign before leaving.',
            examples: [
              'Mediator will take confidential notes during the session (to be destroyed after mediation). If settlement is reached, the mediator will draft heads of agreement for both parties to review and sign before leaving. Each party will receive a signed copy. Detailed legal agreement to be drawn up by solicitors within 14 days.',
              'Parties may take their own notes for personal reference. The mediator will prepare a written summary of agreed terms at the end of the session. Both parties must sign to confirm agreement. Notes of caucus sessions remain strictly confidential to the mediator.'
            ],
            exampleHint: 'Describe who takes notes, how agreements are documented, and signing procedures',
            rows: 3,
            placeholder: 'Describe the documentation process: note-taking, agreement drafting, review and signing procedures, distribution of copies...'
          },
          {
            id: 'settlementAuthority',
            type: 'textarea',
            question: 'What level of authority is needed from each party to formally approve and execute a settlement agreement?',
            validation: { required: true },
            description: 'Ensure all parties can commit to a binding agreement at the mediation. If additional approval is required (e.g., from a co-owner, director, or insurer), this should be arranged in advance.',
            examples: [
              'Both parties have full authority to settle without requiring third-party approval. Any agreement signed at the mediation will be legally binding immediately.',
              'Landlord company requires co-director signature for settlements over £5,000. Director attending will have phone access to co-director for approval during the session. Tenant has full personal authority to settle.'
            ],
            exampleHint: 'Confirm authority levels and any approval processes needed for settlement',
            rows: 3,
            placeholder: 'Describe the authority required: personal authority, board approval, co-owner consent, financial limits, and how additional approvals will be obtained if needed...'
          }
        ]
      }
    },
    prompts: createDocumentPrompts('mediation process guides', 'Generate standard mediation process guide with session logistics, ground rules, confidentiality, mediator role, participant expectations, and post-mediation steps. Incorporate ALL form data.', 'Generate enhanced mediation process guide with comprehensive session structure (opening statements, issue identification, negotiation, caucuses, closing), ground rules, confidentiality framework, mediator impartiality principles, participant preparation checklist, document requirements, decision-making authority, settlement approval process, and follow-up procedures. Incorporate ALL form data.', 'Generate premium mediation handbook with exhaustive process documentation: pre-mediation (preparation checklist, document exchange, position statements), session logistics (date, time, location, duration, breaks, refreshments), participant roles (parties, representatives, advisors, observers), mediator introduction (credentials, impartiality, role explanation, process overview), ground rules agreement (respectful communication, active listening, no interruptions, mobile phones off, breaks as needed), confidentiality agreement (scope, exceptions for illegal activity/safeguarding, without prejudice privilege, mediator testimony prohibition), session structure (joint opening, issue identification, interests exploration, option generation, caucus sessions, reality testing, negotiation, agreement drafting), mediator facilitation techniques (active listening, reframing, reality testing, option generation), caucus protocol (voluntary, confidential, information sharing consent), power imbalance management, decision-making authority verification, agreement parameters (binding vs non-binding, subject to legal advice, conditional vs final), documentation (mediation agreement, settlement terms, implementation timeline), post-mediation (agreement execution, follow-up monitoring, enforcement mechanisms, costs allocation), and success strategies. Incorporate ALL form data.')
  },
  'dispute-assessment': {
    formSections: {
      details: {
        id: 'details',
        title: 'Dispute Category Assessment',
        fields: [
          {
            id: 'disputePrimaryCategory',
            type: 'select',
            question: 'What is the primary category of the dispute you need assessed?',
            options: ['Rent arrears', 'Property damage', 'Deposit deductions', 'Disrepair/maintenance', 'Harassment/illegal eviction', 'Lease breach', 'Anti-social behaviour', 'Possession claim', 'Other'],
            validation: { required: true },
            description: 'The primary category helps determine the most appropriate resolution pathway, applicable law, and likely costs. Some disputes fall into multiple categories; select the most significant one.',
            exampleHint: 'Select the category that best represents the core issue'
          },
          {
            id: 'disputeValue',
            type: 'number',
            question: 'What is the estimated total financial value of the dispute in GBP?',
            validation: { required: false, min: 0 },
            description: 'The financial value is a key factor in determining proportionality. Claims under £10,000 suit the small claims track (lower costs, no cost risk). Claims over £10,000 use the fast track (higher costs but more formal procedures).',
            examples: ['2500', '8000', '25000'],
            exampleHint: 'Total value of all financial claims combined'
          },
          {
            id: 'urgencyLevel',
            type: 'select',
            question: 'How would you assess the urgency level of this dispute?',
            options: ['Emergency (health/safety/security)', 'Urgent (7 days)', 'Standard (28 days)', 'Non-urgent'],
            validation: { required: true },
            description: 'Emergency situations (illegal eviction, imminent health risks, harassment) may require emergency court applications or injunctions within 24-48 hours. Urgent matters need attention within a week.',
            exampleHint: 'Base urgency on the risk of harm or loss if action is delayed'
          },
          {
            id: 'tenancyStatus',
            type: 'select',
            question: 'What is the current status of the tenancy related to this dispute?',
            options: ['Active - Ongoing', 'Ended - Within 6 months', 'Ended - Over 6 months ago'],
            validation: { required: true },
            description: 'The tenancy status affects available remedies and time limitations. Most tenancy claims must be brought within 6 years (Limitation Act 1980), but deposit claims have shorter deadlines.',
            exampleHint: 'Select the current status of the tenancy'
          },
          {
            id: 'relationshipDynamics',
            type: 'select',
            question: 'How would you describe the current relationship dynamics between the parties?',
            options: ['Cooperative', 'Strained but communicating', 'Hostile/no communication', 'Legal action threatened'],
            validation: { required: true },
            description: 'Relationship dynamics strongly influence the recommended resolution pathway. Cooperative relationships suit negotiation. Strained relationships benefit from mediation. Hostile situations may require formal proceedings.',
            exampleHint: 'Select the option that best describes current communications between parties'
          },
          {
            id: 'evidenceStrength',
            type: 'select',
            question: 'How strong is the available evidence to support the claim?',
            options: ['Strong - Documentary proof', 'Moderate - Some evidence', 'Weak - Minimal evidence'],
            validation: { required: true },
            description: 'Evidence strength is a critical factor in assessing the likelihood of success. Strong documentary evidence (written agreements, photographs, bank statements, professional reports) significantly improves outcomes.',
            exampleHint: 'Assess based on the quality and completeness of documentary evidence'
          },
          {
            id: 'legalComplexity',
            type: 'select',
            question: 'How would you rate the legal complexity of this dispute?',
            options: ['Simple - Clear facts/law', 'Moderate - Some ambiguity', 'Complex - Requires legal expertise'],
            validation: { required: true },
            description: 'Simple disputes with clear facts and law can often be resolved without legal representation. Complex disputes involving interpretation of lease terms, multiple grounds, or novel legal issues benefit from professional legal advice.',
            exampleHint: 'Consider whether the facts are disputed and the law is clear'
          },
          {
            id: 'recommendedPathway',
            type: 'select',
            question: 'Based on your assessment, which resolution pathway do you believe is most appropriate?',
            options: ['Direct negotiation', 'Mediation', 'Tribunal (First-tier)', 'County Court small claims', 'County Court fast track', 'Legal advice required'],
            validation: { required: true },
            description: 'Negotiation suits cooperative parties with clear claims under £1,000. Mediation works well for preserving relationships and disputes up to £10,000. Tribunals handle rent increases and some tenant rights for free. Small claims track covers up to £10,000 with limited costs risk. Fast track covers £10,000 to £25,000.',
            exampleHint: 'Consider the dispute value, relationship, complexity, and available evidence'
          },
          {
            id: 'costEstimate',
            type: 'textarea',
            question: 'What are the estimated costs for pursuing the recommended resolution pathway, including fees, legal representation, and other expenses?',
            validation: { required: false },
            description: 'Understanding costs helps assess proportionality. Include court/tribunal fees, legal representation costs, mediation fees, expert report costs, and any other anticipated expenses.',
            examples: [
              'Mediation: £150-£300 mediator fees (split between parties). No court fees. Estimated total per party: £150-£200. No legal representation costs if self-representing.',
              'County Court small claims: £70-£455 court fee (depending on claim value). No cost risk for legal fees. Surveyor report: £300-£500. Estimated total: £500-£1,000. If using solicitor: additional £500-£1,500.'
            ],
            exampleHint: 'Break down costs by category: fees, legal advice, expert reports, other expenses',
            rows: 3,
            placeholder: 'Estimate costs: court/tribunal fees, mediation fees, legal representation, expert reports, and any other anticipated expenses...'
          },
          {
            id: 'timeEstimate',
            type: 'textarea',
            question: 'What is the estimated timeframe for resolving this dispute through the recommended pathway?',
            validation: { required: false },
            description: 'Timeframes vary significantly by resolution method. Negotiation can resolve matters in days. Mediation typically takes 2-4 weeks to arrange. Tribunal hearings are scheduled within 2-6 months. County Court proceedings take 6-12 months on average.',
            examples: [
              'Direct negotiation: 1-4 weeks. If negotiation fails, mediation can be arranged within 2-4 weeks with resolution on the day. Total estimated timeline: 2-8 weeks.',
              'County Court small claims: Letter before action (14 days) + claim preparation (1-2 weeks) + court processing (4-8 weeks) + hearing date (8-16 weeks from issue). Total estimated: 4-7 months.'
            ],
            exampleHint: 'Provide a timeline from start to resolution for the recommended pathway',
            rows: 3,
            placeholder: 'Estimate the timeline: preparation time, filing periods, waiting times, hearing dates, and total expected duration to resolution...'
          },
          {
            id: 'successLikelihood',
            type: 'select',
            question: 'Based on the evidence strength, legal merits, and dispute category, what is the assessed likelihood of a successful outcome?',
            options: ['High', 'Moderate', 'Low', 'Uncertain'],
            validation: { required: true },
            description: 'Success likelihood considers evidence strength, legal merits, proportionality, and the specific resolution pathway. High likelihood suggests strong evidence with clear legal basis. Uncertain typically means legal advice is recommended before proceeding.',
            exampleHint: 'Assess based on evidence quality, legal clarity, and pathway suitability'
          }
        ]
      }
    },
    prompts: createDocumentPrompts('dispute category assessments', 'Generate standard dispute assessment with categorization, urgency level, recommended resolution pathway, and basic cost/time estimates. Incorporate ALL form data.', 'Generate enhanced dispute assessment with comprehensive analysis including dispute taxonomy, financial impact, urgency classification, evidence strength evaluation, legal complexity rating, pathway suitability matrix (negotiation/mediation/tribunal/court), proportionality assessment, cost-benefit analysis per route, success probability, and detailed recommendations. Incorporate ALL form data.', 'Generate premium dispute assessment report with exhaustive analysis: dispute categorization (primary, secondary, tertiary categories), financial quantification (claim value, potential costs, proportionality), urgency classification with response timescales (emergency immediate, urgent 7 days, standard 28 days), tenancy status impact analysis, relationship dynamics assessment, evidence strength evaluation (documentary, testimonial, expert, real evidence), legal complexity rating (simple/moderate/complex), resolution pathway analysis matrix (direct negotiation suitability for <£1000 cooperative relationships; mediation suitability for £1000-£10000 relationship preservation priority; tribunal suitability for tenant rights/rent increases/deposit protection free for tenants; county court small claims £70-£455 fees for £300-£10000; county court fast track for >£10000), detailed pathway comparison (costs, timescales, formality, success rates, enforcement), proportionality assessment (costs vs value, emotional impact, time investment), success probability modeling with confidence intervals, risk assessment, recommended strategy with action plan and timelines, alternative strategies, escalation triggers, and decision tree. Incorporate ALL form data with professional analysis.')
  },
  'rent-arrears-schedule': {
    formSections: {
      details: {
        id: 'details',
        title: 'Rent Arrears Schedule',
        fields: [
          {
            id: 'landlordName',
            type: 'text',
            question: 'What is the landlord\'s full legal name for this rent arrears schedule?',
            validation: { required: true },
            description: 'The landlord\'s name as it appears on the tenancy agreement and as it will appear on any court documents if proceedings are issued.',
            examples: ['John Smith', 'Property Holdings Ltd', 'Sarah Williams'],
            exampleHint: 'Full legal name or registered company name'
          },
          {
            id: 'tenantName',
            type: 'text',
            question: 'What is the tenant\'s full legal name as shown on the tenancy agreement?',
            validation: { required: true },
            description: 'The tenant\'s legal name is required for accurate record-keeping and for use in any formal proceedings or Section 8 notices.',
            examples: ['Emma Johnson', 'Michael Chen', 'Dr. Priya Patel'],
            exampleHint: 'Full legal name exactly as on the tenancy agreement'
          },
          {
            id: 'propertyAddress',
            type: 'text',
            question: 'What is the full address of the rental property where arrears have accrued?',
            validation: { required: true },
            description: 'The property address identifies the tenancy and is required for any court proceedings or statutory notices.',
            examples: ['42 High Street, London, SW1A 1AA', 'Flat 3, Oak House, Manchester, M1 4BT'],
            exampleHint: 'Complete property address including postcode'
          },
          {
            id: 'tenancyStartDate',
            type: 'date',
            question: 'When did this tenancy commence?',
            validation: { required: true },
            description: 'The tenancy start date establishes the duration of the tenancy and is relevant for calculating the total rent due and for certain possession grounds that require minimum tenancy periods.',
            examples: ['2023-06-01', '2024-01-15'],
            exampleHint: 'Start date as shown on the tenancy agreement'
          },
          {
            id: 'tenancyEndDate',
            type: 'date',
            question: 'If the tenancy has ended, on what date did it terminate?',
            validation: { required: false },
            description: 'If the tenancy has ended, arrears may still be pursued through civil proceedings. The end date helps calculate the final arrears balance.',
            examples: ['2025-01-31', '2025-03-15'],
            exampleHint: 'Leave blank if the tenancy is still active'
          },
          {
            id: 'monthlyRent',
            type: 'number',
            question: 'What is the contractual monthly rent amount in GBP?',
            validation: { required: true, min: 0 },
            description: 'The contractual rent amount as specified in the tenancy agreement. If the rent has changed during the tenancy, enter the current rate.',
            examples: ['1200', '850', '2100'],
            exampleHint: 'Current monthly rent as per the tenancy agreement'
          },
          {
            id: 'rentDueDate',
            type: 'number',
            question: 'On which day of the month is the rent contractually due?',
            validation: { required: true, min: 1, max: 31 },
            description: 'The rent due date determines when arrears begin to accrue for each period. This is important for calculating whether 8 weeks of arrears have accumulated for Ground 8 purposes.',
            examples: ['1', '15', '28'],
            exampleHint: 'Day of month (1-31) when rent falls due'
          },
          {
            id: 'firstMissedPayment',
            type: 'date',
            question: 'On what date was the first rent payment missed or short-paid?',
            validation: { required: true },
            description: 'The date of the first missed payment establishes the beginning of the arrears period and is important for court proceedings chronology.',
            examples: ['2024-11-01', '2025-01-01'],
            exampleHint: 'Date of the first full or partial non-payment'
          },
          {
            id: 'totalArrears',
            type: 'number',
            question: 'What is the total current arrears balance outstanding in GBP?',
            validation: { required: true, min: 0 },
            description: 'The total arrears should include all unpaid rent. For Ground 8 (mandatory possession), at least 8 weeks\' or 2 months\' rent must be outstanding both when the Section 8 notice is served and at the court hearing.',
            examples: ['3600', '7200', '1200'],
            exampleHint: 'Total unpaid rent as of today (excluding interest)'
          },
          {
            id: 'paymentHistory',
            type: 'textarea',
            question: 'Please provide a detailed summary of the payment history, listing each rent period, amounts due, amounts paid, and the dates payments were received.',
            validation: { required: true },
            description: 'A comprehensive payment history is essential evidence for court proceedings. It should show a period-by-period breakdown of what was due, what was paid, when it was paid, and the running arrears balance.',
            examples: [
              'June 2024: £1,200 due 01/06, paid 01/06 (on time). July 2024: £1,200 due 01/07, paid 03/07 (2 days late). August 2024: £1,200 due 01/08, paid 01/08 (on time). September 2024: £1,200 due 01/09, £800 paid 05/09 (short £400). October 2024: £1,200 due 01/10, no payment received. November 2024: £1,200 due 01/11, no payment received. Running arrears: £2,800.',
              'Jan 2025: £950 due 15/01, no payment. Feb 2025: £950 due 15/02, £500 paid 20/02. Mar 2025: £950 due 15/03, no payment. Total arrears: £2,350.'
            ],
            exampleHint: 'List each month: due date, amount due, amount paid, date paid, and running balance',
            rows: 5,
            placeholder: 'Provide a month-by-month payment history: rent period, due date, amount due, amount actually paid, date of payment, and running arrears balance...'
          },
          {
            id: 'partialPayments',
            type: 'textarea',
            question: 'Have any partial payments been made? If so, please list the dates, amounts, and how they were allocated against the arrears.',
            validation: { required: false },
            description: 'Partial payments should be allocated to the oldest debt first (the rule in Clayton\'s Case) unless otherwise agreed. This is important for Ground 8 calculations as partial payments may reduce the arrears below the threshold.',
            examples: [
              '05/09/2024: £800 partial payment (£400 short of £1,200 due). Allocated to September rent. 15/10/2024: £300 partial payment. Allocated to oldest debt (September shortfall of £400, leaving £100 against October). 01/12/2024: £500 partial payment. Allocated to oldest outstanding debt.',
              '20/02/2025: £500 paid against February rent of £950 (£450 shortfall). No further partial payments received.'
            ],
            exampleHint: 'List date, amount, and how each partial payment was allocated to rent periods',
            rows: 4,
            placeholder: 'List each partial payment: date received, amount, which rent period it was allocated to, and remaining shortfall...'
          },
          {
            id: 'latePaymentInterestRate',
            type: 'number',
            question: 'What is the contractual late payment interest rate, expressed as a percentage above the Bank of England base rate?',
            validation: { required: false, min: 0 },
            description: 'Late payment interest is typically calculated at the Bank of England base rate plus a contractual margin (commonly 3%). The current BoE base rate should be checked at the time of calculation.',
            examples: ['3', '4', '0'],
            exampleHint: 'Percentage above BoE base rate (e.g., 3 means base rate + 3%)'
          },
          {
            id: 'interestAmount',
            type: 'number',
            question: 'What is the total amount of late payment interest that has accrued on the arrears in GBP?',
            validation: { required: false, min: 0 },
            description: 'Interest is calculated daily on the outstanding arrears balance. The daily rate is the annual rate divided by 365. Interest accrues from the date each payment was due until the date of payment or the current date.',
            examples: ['125', '340', '50'],
            exampleHint: 'Total interest calculated on outstanding arrears to date'
          },
          {
            id: 'arrearsNoticeServed',
            type: 'radio',
            question: 'Has a formal rent arrears notice or warning letter been served on the tenant?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'Pre-Action Protocol for possession claims requires the landlord to have written to the tenant about the arrears and attempted to resolve the situation before issuing court proceedings.',
            exampleHint: 'A formal arrears notice is typically required before court proceedings'
          },
          {
            id: 'section8Served',
            type: 'radio',
            question: 'Has a Section 8 notice seeking possession been served on the tenant?',
            options: ['Yes', 'No', 'Pending'],
            validation: { required: false },
            description: 'A Section 8 notice is required before the landlord can apply to the court for a possession order. For Ground 8 (serious rent arrears), the minimum notice period is 2 weeks. The arrears threshold must be met both at the time of notice service and at the court hearing.',
            exampleHint: 'Section 8 notice is the formal step before court possession proceedings'
          },
          {
            id: 'courtProceedingsStatus',
            type: 'select',
            question: 'What is the current status of any court proceedings related to these rent arrears?',
            options: ['Not started', 'Claim issued', 'Hearing scheduled', 'Judgment obtained'],
            validation: { required: true },
            description: 'Court proceedings for rent arrears involve issuing a claim (Form N5 for possession, Form N1 for money), attending a hearing, and obtaining a judgment. If judgment is obtained, enforcement options include warrants of possession and attachment of earnings.',
            exampleHint: 'Select the current stage of any court proceedings'
          }
        ]
      }
    },
    prompts: createDocumentPrompts('rent arrears schedules', 'Generate standard rent arrears schedule with tenancy details, monthly rent, payment history, missed payments, total arrears, and legal action status. Incorporate ALL form data.', 'Generate enhanced rent arrears schedule with comprehensive payment tracking (each rent period, due dates, payments received, amounts, running balance), partial payment allocation, late payment interest calculations (Bank of England base rate + specified %), arrears notices served, Section 8 ground 8/ground 10 analysis, and possession proceedings timeline. Incorporate ALL form data.', 'Generate premium rent arrears schedule with exhaustive documentation: tenancy identification (parties, property, tenancy dates, reference), rent terms (amount, frequency, due date, payment method), comprehensive payment ledger (period-by-period breakdown with columns: period start, period end, rent due, payment due date, payment received date, amount paid, balance outstanding, days late), running arrears total with daily calculation, partial payment allocation methodology (oldest debt first principle), late payment interest calculation (Bank of England base rate currently 4.5% + contractual % typically 3% = 7.5% annual, daily rate 0.0205%, applied to outstanding balance), total debt breakdown (rent arrears principal, interest, court fees, legal costs), payment history analysis (payment patterns, missed payments count, consecutive arrears), arrears communication log (notices sent, responses received, payment promises), legal notices served (pre-action correspondence, Section 8 ground 8 serious arrears notice, Section 8 ground 10 some arrears notice, claim form N5), court proceedings chronology (claim issued date, hearing date, judgment details), ground 8 eligibility check (8 weeks/2 months arrears both at notice service and hearing date), enforcement options (warrant of possession, attachment of earnings, charging order), and court-compliant formatting with exhibit numbering. Incorporate ALL form data.')
  },
  'section8-assessment': {
    formSections: {
      details: {
        id: 'details',
        title: 'Section 8 Grounds Assessment Tool',
        fields: [
          {
            id: 'tenancyType',
            type: 'select',
            question: 'What type of tenancy is currently in place for this property?',
            options: ['Assured Shorthold Tenancy (AST)', 'Assured Tenancy', 'Periodic Tenancy (post-2025)'],
            validation: { required: true },
            description: 'The tenancy type determines which possession grounds are available and which procedural requirements apply. Post-2025 periodic tenancies are subject to the Renters\' Rights Bill reforms.',
            exampleHint: 'Select the tenancy type as specified in the tenancy agreement'
          },
          {
            id: 'tenancyStartDate',
            type: 'date',
            question: 'When did this tenancy commence?',
            validation: { required: true },
            description: 'The tenancy start date is critical for determining whether the 12-month protection period has elapsed for Grounds 1 and 1A under the 2025 reforms.',
            examples: ['2023-06-01', '2024-09-15', '2025-01-01'],
            exampleHint: 'Start date as shown on the tenancy agreement'
          },
          {
            id: 'assessmentDate',
            type: 'date',
            question: 'What is the date of this Section 8 grounds assessment?',
            validation: { required: true },
            description: 'The assessment date establishes the point in time at which the grounds are being evaluated. Arrears amounts and other conditions may change between assessment and court hearing.',
            examples: ['2025-03-15', '2025-04-01'],
            exampleHint: 'Today\'s date or the date this assessment is being prepared'
          },
          {
            id: 'ground8Applicable',
            type: 'radio',
            question: 'Is Ground 8 (serious rent arrears) applicable to this case?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'Ground 8 is a mandatory ground requiring the court to make a possession order if proven. The tenant must owe at least 8 weeks\' rent (weekly/fortnightly tenancy) or 2 months\' rent (monthly tenancy) both when the Section 8 notice is served AND at the date of the court hearing.',
            exampleHint: 'Ground 8 requires serious arrears at both notice service and hearing'
          },
          {
            id: 'ground8Arrears',
            type: 'number',
            question: 'If Ground 8 is applicable, what is the current total arrears amount in GBP?',
            validation: { required: false, min: 0 },
            description: 'For Ground 8, calculate whether the arrears meet the threshold: 8 weeks\' rent for weekly tenancies or 2 months\' rent for monthly tenancies. Remember this threshold must be met at both notice service and hearing.',
            examples: ['4800', '7200', '2400'],
            exampleHint: 'Current total arrears (must meet threshold at notice AND hearing)'
          },
          {
            id: 'ground10Applicable',
            type: 'radio',
            question: 'Is Ground 10 (some rent arrears) applicable as a discretionary ground?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'Ground 10 is a discretionary ground that applies when some rent is outstanding both at the time the Section 8 notice is served and at the court hearing. The court will consider whether it is reasonable to make a possession order, taking into account the tenant\'s circumstances.',
            exampleHint: 'Ground 10 applies when any arrears exist at notice and hearing dates'
          },
          {
            id: 'ground14Applicable',
            type: 'radio',
            question: 'Is Ground 14 (nuisance or anti-social behaviour) applicable to this case?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'Ground 14 is a discretionary ground covering nuisance, annoyance, or conviction for illegal or immoral use of the property. Evidence of the anti-social behaviour and its impact on neighbours or the locality is essential.',
            exampleHint: 'Ground 14 covers nuisance, annoyance, and criminal behaviour at the property'
          },
          {
            id: 'ground14Evidence',
            type: 'textarea',
            question: 'If Ground 14 is applicable, please describe the evidence of anti-social behaviour, including specific incidents, dates, and witnesses.',
            validation: { required: false },
            description: 'Detailed, dated evidence of each incident is essential for Ground 14. Include police reports, neighbour complaints, noise recordings, photographs, and any ASB warnings issued. The court will assess whether the behaviour justifies possession.',
            examples: [
              '15/09/2024: Loud music at 2am, police called by neighbours (crime ref: 12345). 01/10/2024: Verbal altercation in communal area witnessed by 3 neighbours. 20/10/2024: Noise abatement notice served by council. 05/11/2024: Further noise complaint, second police attendance. 15/11/2024: Written warning issued to tenant. Behaviour continued through December 2024.',
              'Multiple incidents of drug use in communal areas reported by 4 neighbours between October and December 2024. Police raid on 01/01/2025 resulted in arrest and caution. Witness statements from 3 neighbours available. Council anti-social behaviour team involved since November 2024.'
            ],
            exampleHint: 'List specific incidents with dates, witnesses, police references, and council involvement',
            rows: 5,
            placeholder: 'Describe each incident: date, what happened, who was affected, police involvement, council notices, witness details, and any evidence collected...'
          },
          {
            id: 'ground1Applicable',
            type: 'radio',
            question: 'Is Ground 1 (landlord intends to sell the property) applicable?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: '2025 Reform: Ground 1 allows landlords to seek possession if they genuinely intend to sell the property. Under the Renters\' Rights Bill, this ground cannot be used during the first 12 months of a tenancy. The landlord must demonstrate a genuine intention to sell.',
            exampleHint: 'Cannot be used in the first 12 months of a tenancy (2025 reform)'
          },
          {
            id: 'ground1AApplicable',
            type: 'radio',
            question: 'Is Ground 1A (landlord or family member needs to occupy the property) applicable?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: '2025 Reform: Ground 1A allows landlords to seek possession if they or a close family member needs to occupy the property as their only or main home. Cannot be used in the first 12 months, and a landlord who has previously used this ground cannot use it again for the same property.',
            exampleHint: 'Cannot be used in first 12 months or if previously used for same property'
          },
          {
            id: 'twelveMonthProtection',
            type: 'radio',
            question: 'Has the 12-month protection period elapsed since the tenancy start date?',
            options: ['Yes', 'No'],
            validation: { required: false },
            description: '2025 Reform: Under the Renters\' Rights Bill, Grounds 1 and 1A cannot be invoked during the first 12 months of a tenancy. This protection period gives tenants security of tenure when they first move in.',
            exampleHint: 'Calculate from tenancy start date whether 12 months have passed'
          },
          {
            id: 'mandatoryGrounds',
            type: 'checkbox',
            question: 'Which mandatory grounds for possession are you relying on? (Court must grant possession if proven)',
            options: ['Ground 8 (serious arrears)', 'Ground 1 (sale)', 'Ground 1A (occupation)', 'Other mandatory'],
            validation: { required: false },
            description: 'Mandatory grounds require the court to make a possession order if the ground is established. The court has no discretion to refuse. Ground 8 requires 8 weeks/2 months arrears at both notice and hearing.',
            exampleHint: 'Select all mandatory grounds you intend to rely on'
          },
          {
            id: 'discretionaryGrounds',
            type: 'checkbox',
            question: 'Which discretionary grounds for possession are you relying on? (Court considers reasonableness)',
            options: ['Ground 10 (some arrears)', 'Ground 11 (persistent delay)', 'Ground 12 (breach of obligation)', 'Ground 13 (deterioration)', 'Ground 14 (ASB)', 'Ground 15 (furniture damage)', 'Other'],
            validation: { required: false },
            description: 'Discretionary grounds require the court to be satisfied both that the ground is established AND that it is reasonable to make a possession order. The court will consider all circumstances including the tenant\'s situation.',
            exampleHint: 'Select all discretionary grounds you intend to rely on'
          },
          {
            id: 'noticePeriodsRequired',
            type: 'textarea',
            question: 'What are the required notice periods for each of the applicable grounds you are relying on?',
            validation: { required: true },
            description: 'Different grounds require different minimum notice periods in the Section 8 notice. Getting the notice period wrong will invalidate the notice. Ground 8: 2 weeks. Ground 14: immediately or 4 weeks depending on severity. Grounds 1/1A: 2 months (post-2025).',
            examples: [
              'Ground 8 (serious rent arrears): 2 weeks minimum notice. Ground 10 (some rent arrears): 2 weeks minimum notice. Both grounds being relied upon in parallel. Section 8 notice to be served giving 2 weeks\' notice.',
              'Ground 1 (sale): 2 months minimum notice under 2025 reforms. Ground 1A (occupation): 2 months minimum notice under 2025 reforms. 12-month protection period has elapsed (tenancy started January 2024).'
            ],
            exampleHint: 'Specify the required notice period for each ground being relied upon',
            rows: 3,
            placeholder: 'List each ground and its required notice period: Ground 8 (2 weeks), Ground 14 (immediate/4 weeks), Grounds 1/1A (2 months post-2025), etc...'
          },
          {
            id: 'evidenceStrength',
            type: 'select',
            question: 'How would you assess the overall strength of the evidence supporting the possession grounds?',
            options: ['Strong', 'Moderate', 'Weak'],
            validation: { required: true },
            description: 'Evidence strength is the most critical factor in possession proceedings. For mandatory grounds, the evidence must clearly prove the ground is met. For discretionary grounds, strong evidence is needed to persuade the court that possession is reasonable.',
            exampleHint: 'Assess based on documentary evidence, witness availability, and completeness'
          },
          {
            id: 'successLikelihood',
            type: 'select',
            question: 'Based on the applicable grounds and evidence strength, what is the assessed likelihood of obtaining a possession order?',
            options: ['Very likely (mandatory ground met)', 'Likely (strong discretionary)', 'Possible (discretionary with issues)', 'Unlikely'],
            validation: { required: true },
            description: 'Mandatory grounds (when clearly proven) have the highest success rate as the court has no discretion. Discretionary grounds depend on reasonableness and the court\'s assessment of all circumstances. Running mandatory and discretionary grounds in parallel maximises the chance of success.',
            exampleHint: 'Consider whether mandatory grounds are met and the strength of discretionary arguments'
          }
        ]
      }
    },
    prompts: createDocumentPrompts('Section 8 grounds assessments', 'Generate standard Section 8 grounds assessment with applicable grounds identification, mandatory vs discretionary classification, evidence requirements, notice periods, and basic success evaluation. Incorporate ALL form data.', 'Generate enhanced Section 8 grounds assessment with comprehensive grounds analysis (mandatory grounds 1, 1A, 8 with automatic possession if proven; discretionary grounds 10-17 requiring court discretion based on reasonableness), 2025 Renters\' Rights Bill impacts (Ground 1 landlord sale and Ground 1A occupation only after 12 months, abolished Section 21), evidence sufficiency evaluation per ground, notice period requirements (2 weeks Ground 8, 2 months Grounds 1/1A, 4 weeks Ground 14), procedural compliance checklist, and success probability assessment. Incorporate ALL form data.', 'Generate premium Section 8 grounds assessment report with exhaustive analysis: tenancy classification (AST pre-2025, periodic post-2025, assured), grounds taxonomy (mandatory grounds: 1 landlord intends to sell, 1A landlord/family needs to occupy, 8 serious rent arrears 8+ weeks at notice and hearing; discretionary grounds: 10 some arrears, 11 persistent delay, 12 breach of obligation, 13 deterioration/waste, 14 nuisance/ASB, 15 furniture damage, 16 employee accommodation, 17 false statement), 2025 Renters\' Rights Bill compliance verification (12-month protection for Grounds 1/1A from tenancy start, previous use of Ground 1A prohibition, Section 21 abolition confirmation), ground-by-ground assessment (applicability, evidence requirements, evidence strength, legal tests, defenses available), mandatory vs discretionary distinction (mandatory requires proof only, discretionary requires proof plus reasonableness), notice period requirements per ground with form specifications (Form 3 for assured, Form 6A for AST), evidence sufficiency evaluation (documentary, testimonial, expert as needed per ground), procedural compliance checklist (prescribed form used, correct notice period given, notice requirements met, service of notice proven), parallel grounds strategy (Ground 8 + Ground 10 for arrears, Ground 14 + Ground 12 for behavior), possession hearing preparation (grounds to rely on, evidence bundles, witness arrangements, court bundle specifications), discretionary factors for court consideration (tenant circumstances, proportionality, alternative accommodation, arrears reasons), success probability assessment with confidence analysis (mandatory grounds 95%+ if proven, discretionary 60-80% depending on reasonableness), procedural timeline (notice period, claim issue, hearing date, possession date), costs implications, and strategic recommendations. Incorporate ALL form data with 2025 reforms integration.')
  },
  'pre-action-protocol': {
    formSections: {
      details: {
        id: 'details',
        title: 'Pre-Action Protocol Checklist',
        fields: [
          {
            id: 'claimType',
            type: 'select',
            question: 'What type of court claim are you considering or preparing to issue?',
            options: ['Possession', 'Rent arrears (money)', 'Disrepair', 'Deposit', 'Other'],
            validation: { required: true },
            description: 'The claim type determines which Pre-Action Protocol applies. Possession claims follow the Pre-Action Protocol for Possession Claims by Social Landlords (or general PD for private landlords). Disrepair claims follow the Housing Disrepair Protocol.',
            exampleHint: 'Select the type of court claim you intend to pursue'
          },
          {
            id: 'letterBeforeActionSent',
            type: 'radio',
            question: 'Has a formal letter before action been sent to the other party setting out the claim?',
            options: ['Yes', 'No - Not required', 'No - Will send'],
            validation: { required: true },
            description: 'A letter before action is a critical pre-action requirement. It must clearly set out the claim, the legal basis, the remedy sought, and allow a reasonable period for response (typically 14 days for rent arrears, 28 days for disrepair).',
            exampleHint: 'A letter before action is required before most court claims'
          },
          {
            id: 'letterDate',
            type: 'date',
            question: 'On what date was the letter before action sent?',
            validation: { required: false },
            description: 'The date the letter was sent establishes when the response period begins. Keep proof of posting (recorded delivery receipt) or email delivery confirmation.',
            examples: ['2025-02-01', '2025-03-01'],
            exampleHint: 'Date the letter was posted or emailed'
          },
          {
            id: 'responsePeriodGiven',
            type: 'number',
            question: 'How many days were given for the other party to respond to the letter before action?',
            validation: { required: false, min: 0 },
            description: 'Standard response periods: 14 days for rent arrears claims, 20 days for early protocol response in disrepair, 28 days for full disrepair response. Insufficient response time may be criticised by the court.',
            examples: ['14', '21', '28'],
            exampleHint: 'Typically 14 days (arrears) or 28 days (disrepair)'
          },
          {
            id: 'responseReceived',
            type: 'radio',
            question: 'Was a response received from the other party within the specified timeframe?',
            options: ['Yes', 'No'],
            validation: { required: false },
            description: 'If no response was received, this strengthens the argument for proceeding to court. If a response was received, consider whether it offers a basis for settlement before issuing proceedings.',
            exampleHint: 'Record whether any response was received within the deadline'
          },
          {
            id: 'negotiationAttempted',
            type: 'radio',
            question: 'Have genuine good faith negotiations been attempted to resolve the dispute without court proceedings?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'Courts expect parties to have made genuine efforts to negotiate before issuing proceedings. Failure to engage in good faith negotiations can result in adverse costs orders, even if you win the case.',
            exampleHint: 'Good faith negotiations include offers, counter-offers, and meetings'
          },
          {
            id: 'mediationConsidered',
            type: 'radio',
            question: 'Has alternative dispute resolution (ADR) or mediation been considered or proposed to the other party?',
            options: ['Yes - Proposed', 'Yes - Rejected by other party', 'No'],
            validation: { required: true },
            description: 'Courts strongly encourage ADR under CPR 1.4(2)(e). Unreasonable refusal to consider mediation can result in adverse costs consequences, even for the winning party (Halsey v Milton Keynes [2004]). Courts expect ADR consideration.'
          },
          {
            id: 'keyDocumentsShared',
            type: 'checkbox',
            question: 'Which key documents have been disclosed to the other party as part of the pre-action process?',
            options: ['Tenancy agreement', 'Rent statements', 'Correspondence', 'Notices served', 'Inspection reports', 'Photos'],
            validation: { required: true },
            description: 'Pre-Action Protocol requires early disclosure of key documents that are relevant to the dispute. This allows both parties to assess the strength of their positions before committing to court proceedings.',
            exampleHint: 'Select all documents that have been shared with the other party'
          },
          {
            id: 'proportionalityConsidered',
            type: 'radio',
            question: 'Has the proportionality of proceeding with court action been assessed against the value of the claim and the issues involved?',
            options: ['Yes'],
            validation: { required: true },
            description: 'The court expects parties to consider whether the costs of court proceedings are proportionate to the value of the claim and the importance of the issues. Small claims (under £10,000) should generally use the small claims track. Costs proportionate to claim value and issues.'
          },
          {
            id: 'practiceDirectionCompliant',
            type: 'radio',
            question: 'Has the relevant Practice Direction for this type of claim been identified and followed?',
            options: ['Yes', 'N/A'],
            validation: { required: true },
            description: 'Different claim types have specific Practice Directions. Possession claims must follow PD 55A. Disrepair claims follow the Housing Disrepair Protocol. General claims follow PD Pre-Action Conduct and Protocols.',
            exampleHint: 'Ensure the specific Practice Direction for your claim type has been followed'
          },
          {
            id: 'noncompliance Risk',
            type: 'textarea',
            question: 'Are there any concerns about compliance with the Pre-Action Protocol requirements that could affect the court proceedings?',
            validation: { required: false },
            description: 'Non-compliance with Pre-Action Protocols can have serious consequences. The court may impose costs sanctions, stay proceedings until the protocol is complied with, or take non-compliance into account when making case management directions. Court may impose costs sanctions for non-compliance.',
            examples: [
              'Response period may have been insufficient (only 7 days given instead of recommended 14 days). May need to send follow-up letter with extended deadline before issuing claim.',
              'Mediation was not formally proposed in the letter before action. While negotiation was attempted, the court may view the absence of a specific ADR proposal unfavourably. Consider sending a supplementary letter proposing mediation before issuing proceedings.'
            ],
            exampleHint: 'Identify any gaps in protocol compliance and steps needed to address them',
            rows: 3,
            placeholder: 'Describe any compliance concerns: insufficient response periods, missing documents, lack of ADR proposals, or other protocol gaps that could attract court sanctions...'
          }
        ]
      }
    },
    prompts: createDocumentPrompts('pre-action protocol checklists', 'Generate standard pre-action protocol checklist with letter before action requirements, response periods, negotiation attempts, ADR consideration, document disclosure, and compliance verification. Incorporate ALL form data.', 'Generate enhanced pre-action protocol checklist with comprehensive compliance framework: letter before claim requirements (detailed claim, legal basis, time for response typically 14-30 days), good faith negotiation documentation, ADR proposals (mediation, arbitration), key document disclosure list, proportionality assessment (claim value vs legal costs), Practice Direction compliance (PD Pre-Action Conduct, specific protocols for possession/disrepair), and non-compliance risk warnings (costs sanctions, case management directions, strike out risk). Incorporate ALL form data.', 'Generate premium pre-action protocol compliance framework with exhaustive requirements: applicable protocol identification (PD Pre-Action Conduct general, Pre-Action Protocol for Possession Claims, Housing Disrepair Protocol), letter before claim template compliance (claimant/defendant details, clear claim summary, legal basis cited, time for response specified typically 14 days rent arrears 28 days disrepair, warning of court action), contents checklist (factual basis, alleged breaches, loss/damage quantified, supporting documents list, proposal for settlement, ADR invitation, warning of costs consequences), document disclosure obligations (key documents relevant to dispute, proportionate disclosure, tenancy agreement, rent records, correspondence, notices, certificates, inspection reports), good faith negotiation requirements (genuine attempts to settle, reasonable offers, consideration of counter-proposals, meeting/discussion attempts), ADR consideration framework (mediation suitability assessment, ADR proposal documentation, reasons for refusal if applicable, court expectations CPR 1.4(2)(e)), proportionality evaluation (claim value, costs likely to be incurred, complexity assessment, importance to parties, alternative remedies), Practice Direction compliance (PD Pre-Action Conduct objectives, compliance standards, consequences of non-compliance including costs penalties, specific protocol requirements for claim type), timeline management (letter sent dates, response deadlines, negotiation period, claim issue timing), compliance evidence log (correspondence, without prejudice offers, ADR proposals, document exchanges), non-compliance risks (wasted costs orders, adverse costs consequences, case management sanctions, interest on damages, indemnity costs, potential strike-out), and court-ready documentation with compliance statement for claim form. Incorporate ALL form data.')
  },
  'court-bundle': {
    formSections: {
      details: {
        id: 'details',
        title: 'Court Bundle Template',
        fields: [
          {
            id: 'courtName',
            type: 'text',
            question: 'What is the name of the court where the hearing will take place?',
            validation: { required: true },
            description: 'The full name of the court as it will appear on the bundle cover page. Include the court centre name for County Courts.',
            examples: ['Central London County Court', 'Manchester Civil Justice Centre', 'First-tier Tribunal (Property Chamber)'],
            exampleHint: 'Full court name including centre location'
          },
          {
            id: 'caseNumber',
            type: 'text',
            question: 'What is the court case reference number?',
            validation: { required: true },
            description: 'The case number is assigned by the court when the claim is issued and must appear on all documents filed with the court.',
            examples: ['A12CL345', 'E00MC678', 'LON/00AB/HMF/2025/0012'],
            exampleHint: 'Case number as assigned by the court on the claim form'
          },
          {
            id: 'hearingDate',
            type: 'date',
            question: 'What is the scheduled date of the court hearing?',
            validation: { required: true },
            description: 'The hearing date determines the deadline for filing the court bundle. Bundles must typically be filed at least 3 days before the hearing (or as directed by the court).',
            examples: ['2025-05-15', '2025-06-20'],
            exampleHint: 'Bundle must be filed at least 3 days before this date'
          },
          {
            id: 'hearingType',
            type: 'select',
            question: 'What type of hearing is the court bundle being prepared for?',
            options: ['Directions', 'Trial', 'Possession hearing', 'Application', 'Appeal'],
            validation: { required: true },
            description: 'The hearing type affects the required contents of the bundle. Trial bundles are the most comprehensive. Directions hearings may only need key documents. Possession hearings require specific prescribed documents.',
            exampleHint: 'Select the type of hearing this bundle is being prepared for'
          },
          {
            id: 'claimantName',
            type: 'text',
            question: 'What is the full legal name of the claimant (the party who issued the claim)?',
            validation: { required: true },
            description: 'The claimant\'s name as it appears on the claim form. For companies, use the registered company name.',
            examples: ['John Smith', 'Premier Property Holdings Ltd', 'Emma Johnson'],
            exampleHint: 'Full legal name as it appears on the court claim form'
          },
          {
            id: 'defendantName',
            type: 'text',
            question: 'What is the full legal name of the defendant (the party the claim is against)?',
            validation: { required: true },
            description: 'The defendant\'s name as it appears on the claim form. Must match exactly for service and enforcement purposes.',
            examples: ['Sarah Williams', 'Residential Lettings Ltd', 'Michael Chen'],
            exampleHint: 'Full legal name as it appears on the court claim form'
          },
          {
            id: 'bundlePreparedBy',
            type: 'select',
            question: 'Which party is responsible for preparing the court bundle?',
            options: ['Claimant', 'Defendant', 'Both parties (agreed)'],
            validation: { required: true },
            description: 'Under CPR Practice Direction 32, the claimant is usually responsible for preparing the trial bundle unless otherwise ordered by the court. An agreed bundle (prepared cooperatively) is preferred by judges.',
            exampleHint: 'The claimant typically prepares the bundle unless the court directs otherwise'
          },
          {
            id: 'chronologyIncluded',
            type: 'radio',
            question: 'Has a chronology of key events been prepared and included in the bundle with page references?',
            options: ['Yes'],
            validation: { required: true },
            description: 'A chronology is a table listing key events in date order with cross-references to the relevant page numbers in the bundle. It helps the judge quickly understand the timeline of events. Timeline of key events with page references.'
          },
          {
            id: 'documentsCount',
            type: 'number',
            question: 'Approximately how many documents will be included in the court bundle?',
            validation: { required: true, min: 1 },
            description: 'The number of documents helps estimate the bundle size and number of copies needed. For small claims, bundles should be concise (under 50 pages if possible). For trials, a core bundle of no more than 150 pages is recommended, with additional documents in supplementary bundles.',
            examples: ['25', '50', '120'],
            exampleHint: 'Aim for concise bundles; use supplementary bundles for large document sets'
          },
          {
            id: 'bundleFormat',
            type: 'select',
            question: 'In what format should the court bundle be prepared?',
            options: ['Electronic PDF', 'Paper (paginated)', 'Both'],
            validation: { required: true },
            description: 'Many courts now accept or prefer electronic bundles in PDF format with bookmarks and hyperlinked indexes. Check the specific court\'s requirements. Paper bundles must be paginated with consecutive page numbers at the bottom centre.',
            exampleHint: 'Check with the specific court for their format requirements'
          },
          {
            id: 'exhibitNumberingSystem',
            type: 'select',
            question: 'Which exhibit numbering system should be used for documents in the bundle?',
            options: ['Claimant C1-C99, Defendant D1-D99', 'Sequential 1-999', 'Custom'],
            validation: { required: true },
            description: 'A consistent exhibit numbering system is essential for court proceedings. The standard approach uses C1-C99 for claimant exhibits and D1-D99 for defendant exhibits, making it easy to identify which party produced each document.',
            exampleHint: 'The C/D prefix system is most commonly used in tenancy proceedings'
          },
          {
            id: 'witnessStatementsIncluded',
            type: 'radio',
            question: 'Will witness statements be included in the court bundle?',
            options: ['Yes', 'No'],
            validation: { required: true },
            description: 'Witness statements must comply with CPR Part 32 Practice Direction format: headed with case details, numbered paragraphs, statement of truth, and exhibited documents properly referenced. Witness statements stand as evidence-in-chief at trial.',
            exampleHint: 'Witness statements must include a statement of truth and be properly formatted'
          },
          {
            id: 'expertReportsIncluded',
            type: 'radio',
            question: 'Will expert reports be included in the court bundle?',
            options: ['Yes', 'No', 'N/A'],
            validation: { required: true },
            description: 'Expert reports must comply with CPR Part 35 requirements, including a declaration of compliance and statement of truth. For small claims, expert evidence requires court permission. Joint experts are preferred where possible.',
            exampleHint: 'Expert reports require court permission and must comply with CPR Part 35'
          },
          {
            id: 'legalAuthorities',
            type: 'textarea',
            question: 'What are the key legal authorities (statutes, case law, and regulations) that should be included in a separate authorities bundle?',
            validation: { required: false },
            description: 'Legal authorities should be included in a separate bundle or section. Include the specific provisions of statutes, key passages from case law, and relevant regulations that support your case. Only include authorities that are directly relevant and will be referred to at the hearing.',
            examples: [
              'Housing Act 1988, Schedule 2 (possession grounds). Landlord and Tenant Act 1985, s.11 (landlord repair obligations). Renters\' Rights Bill 2025 (periodic tenancy provisions). Dawnays Ltd v FG Minter [1971] (interest on arrears). CPR Part 55 (possession claims procedure).',
              'Protection from Eviction Act 1977, s.1 (unlawful eviction). Housing Act 2004, s.214 (deposit protection penalties). Deregulation Act 2015, s.33 (retaliatory eviction). Ayannuga v Swindells [2012] (deposit claim limitation). Superstrike v Rodrigues [2013] (prescribed information).'
            ],
            exampleHint: 'List statutes, case law, and regulations you will refer to at the hearing',
            rows: 4,
            placeholder: 'List the key legal authorities: relevant statutes and specific sections, case law with citations, regulations, and practice directions that support your case...'
          },
          {
            id: 'copiesRequired',
            type: 'number',
            question: 'How many copies of the court bundle are required for the hearing?',
            validation: { required: true, min: 1 },
            description: 'The standard requirement is one copy for the court (judge), one for each party, and additional copies for witnesses if they are giving evidence. Check with the specific court for their requirements, as some courts request additional copies. Typically: court + each party + judge + witnesses.',
            examples: ['3', '4', '5'],
            exampleHint: 'Typically: 1 for judge + 1 per party + 1 per witness giving evidence'
          }
        ]
      }
    },
    prompts: createDocumentPrompts('court bundle templates', 'Generate standard court bundle template with index, chronology, key documents (claim form, statements of case, witness statements, orders), exhibit numbering, and pagination. Incorporate ALL form data.', 'Generate enhanced court bundle with comprehensive structure: title page (court, case number, parties, hearing date/type), index with page numbers, chronology with exhibit references, statements of case (claim form, particulars, defense, counterclaim), evidence (witness statements with exhibits, expert reports, documentary exhibits in chronological order), orders and judgments, correspondence, legal authorities (case law, statutes), pagination throughout, exhibit numbering (C1-C99 claimant, D1-D99 defendant), and CPR-compliant formatting. Incorporate ALL form data.', 'Generate premium professional court bundle with exhaustive specifications complying with CPR Practice Direction 32: mandatory contents (title page with full case details, index with document descriptions and page numbers, chronology of relevant events with cross-references to bundle pages, statements of case in chronological order, witness statements with annexed exhibits properly numbered, expert reports if applicable, orders and judgments, correspondence if relevant, other documents in date order, legal authorities bundle if needed), formatting requirements (A4 paper, double-sided printing if paper, consecutive pagination bottom center, paragraph numbering on all statements and pleadings, exhibits individually paginated and numbered, clear legibility, black and white unless color essential), exhibit identification system (witness exhibits: Claimant C1-C99, Defendant D1-D99; document exhibits numbered sequentially), chronology requirements (table format with columns: date, event, page reference in bundle, parties involved; objective factual summary without argument; cross-referenced to supporting evidence), index requirements (document description, page numbers, author/recipient for correspondence, date of document), bundle preparation standards (agreed bundle where possible, prepared by claimant unless otherwise ordered, copies for court, each party, judge, witnesses as needed), electronic bundle requirements (PDF format with bookmarks, hyperlinked index, searchable OCR text, maximum file size compliant with court specifications, sequential page numbering), core bundle for long trials (key documents only, limited to 150 pages, additional documents in supplementary bundles), hearing bundle organization (trial bundle, chronological order within sections, avoid duplication, logical grouping), and compliance statement with certificate of service. Incorporate ALL form data with professional legal formatting.')
  },
  'written-statement-prescribed-terms': {
    formSections: {
      details: {
        id: 'details',
        title: 'Written Statement of Prescribed Terms',
        fields: [
          {
            id: 'existingDocument',
            type: 'file' as const,
            question: 'Upload your existing tenancy agreement or document',
            validation: { required: true },
            description: 'Upload the tenancy agreement you want to extract prescribed terms from. We will analyse the document and produce a compliant Written Statement of Prescribed Terms.',
            acceptedFileTypes: '.pdf,.doc,.docx,.txt',
            maxFileSizeMB: 10,
            placeholder: 'Paste the full text of your existing tenancy agreement here...'
          },
          {
            id: 'landlordName',
            type: 'text' as const,
            question: 'What is the landlord\'s full legal name?',
            validation: { required: true },
            description: 'The name that should appear on the Written Statement as the landlord. This must match the landlord named in the tenancy.',
            examples: ['John Smith', 'Sarah Williams', 'Premier Properties Ltd'],
            exampleHint: 'Full legal name or registered company name'
          },
          {
            id: 'tenantName',
            type: 'text' as const,
            question: 'What is the tenant\'s full legal name?',
            validation: { required: true },
            description: 'The tenant or tenants named in the tenancy agreement.',
            examples: ['Emma Johnson', 'Michael Chen and Rebecca Taylor'],
            exampleHint: 'Include all tenants if joint tenancy'
          },
          {
            id: 'propertyAddress',
            type: 'text' as const,
            question: 'What is the property address?',
            validation: { required: true },
            description: 'The full address of the rented property as it appears on the tenancy agreement.',
            examples: ['Flat 4, 28 Victoria Road, London, SW1A 1AA', '12 Maple Street, Manchester, M1 2AB'],
            exampleHint: 'Complete address including postcode'
          },
          {
            id: 'startDate',
            type: 'date' as const,
            question: 'What is the tenancy start date?',
            validation: { required: true },
            description: 'The date the current tenancy commenced or is due to commence.'
          },
          {
            id: 'additionalNotes',
            type: 'textarea' as const,
            question: 'Are there any additional terms or context to include?',
            validation: { required: false },
            description: 'Any special terms, side agreements, or additional context that may not appear in the uploaded document but should be reflected in the Written Statement.',
            placeholder: 'e.g., verbal agreements about pets, parking arrangements, or recent changes to terms...',
            rows: 4
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'Written Statement of Prescribed Terms',
      'Generate a standard Written Statement of Prescribed Terms based on the uploaded tenancy document. Extract all prescribed information required under Section 1 of the Landlord and Tenant Act 1985. Include: names of parties, property address, tenancy start date, rent amount and payment frequency, deposit amount and protection scheme, landlord service address, and repair obligations. Flag any missing prescribed terms. Incorporate ALL form data.',
      'Generate an enhanced Written Statement of Prescribed Terms with comprehensive extraction from the uploaded document. Include all requirements under Section 1 Landlord and Tenant Act 1985 (as amended): full names and addresses of all parties, property description, tenancy commencement date, rent amount and payment frequency and method, rent review mechanism, deposit amount and protection scheme details, landlord service address for notices, repair obligations under Section 11 Landlord and Tenant Act 1985, restrictions on use/alterations/subletting, break clause terms if any, notice periods, and all other prescribed information. Cross-reference with Renters\' Rights Bill 2025 requirements for periodic tenancies. Clearly flag any prescribed terms that could not be found in the uploaded document. Incorporate ALL form data.',
      'Generate a premium professional Written Statement of Prescribed Terms with exhaustive analysis of the uploaded tenancy document. This must comply with all current and forthcoming statutory requirements including: Section 1 Landlord and Tenant Act 1985 prescribed information (landlord name and address, tenant name, property address and description, tenancy start date, rent amount/frequency/payment method/due date, rent review provisions, deposit amount and which government-approved scheme protects it with scheme contact details, landlord service address for Section 48 notices), Section 11 Landlord and Tenant Act 1985 repair obligations (structure, exterior, installations for water/gas/electricity/sanitation/heating), Renters\' Rights Bill 2025 considerations (all new tenancies periodic by default, Section 21 abolished, new grounds for possession under amended Section 8, rent increase framework via Section 13 only once per year with minimum 2 months notice, Awaab\'s Law extension to private sector for hazard repairs, new Decent Homes Standard obligations, landlord redress scheme membership requirement, Pet permission requests framework, advertising rent restrictions), additional prescribed terms (permitted occupants, council tax responsibility, utility responsibilities, garden maintenance obligations, insurance requirements, access arrangements for inspections and repairs with 24-hour notice, subletting and assignment restrictions, alteration restrictions, break clause provisions and notice requirements, forfeiture/re-entry provisions), compliance checklist (identify every prescribed term found in the uploaded document with page/clause reference, flag every prescribed term NOT found with recommendation to add, highlight any terms that conflict with current legislation, note any terms that will become non-compliant under Renters\' Rights Bill 2025). Format as a professional standalone document with clear section numbering, table of contents, and compliance summary. Incorporate ALL form data.'
    )
  },
  'section-13-notice': {
    formSections: {
      details: {
        id: 'details',
        title: 'Section 13 Rent Increase Notice (2025 Compliant)',
        fields: [
          {
            id: 'existingDocument',
            type: 'file' as const,
            question: 'Upload the existing tenancy agreement',
            validation: { required: true },
            description: 'Upload the current tenancy agreement so we can extract landlord/tenant details, property address, current rent, and tenancy type to generate a compliant Section 13 notice.',
            acceptedFileTypes: '.pdf,.doc,.docx,.txt',
            maxFileSizeMB: 10,
            placeholder: 'Paste the full text of the existing tenancy agreement here...'
          },
          {
            id: 'proposedNewRent',
            type: 'number' as const,
            question: 'What is the proposed new rent amount (GBP)?',
            validation: { required: true, min: 1 },
            description: 'The new rent you wish to charge. Under the Renters\' Rights Bill 2025, rent can only be increased once per year via a Section 13 notice. The tenant can refer the notice to the First-tier Tribunal if they consider the increase unreasonable.',
            examples: ['1200', '1500', '1850'],
            exampleHint: 'Monthly amount in pounds sterling'
          },
          {
            id: 'rentIncreaseDate',
            type: 'date' as const,
            question: 'What date should the new rent take effect from?',
            validation: { required: true },
            description: 'The proposed start date for the new rent. You must give at least 2 months\' notice for yearly tenancies and at least 1 month for shorter periods. Under the Renters\' Rights Bill 2025, the minimum notice period is 2 months for all periodic tenancies.'
          },
          {
            id: 'rentPaymentPeriod',
            type: 'select' as const,
            question: 'What is the current rent payment period?',
            options: ['Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Yearly'],
            validation: { required: true },
            description: 'The frequency at which rent is currently paid. This determines the minimum notice period required for the Section 13 notice.'
          },
          {
            id: 'tenancyType',
            type: 'select' as const,
            question: 'What type of tenancy is currently in place?',
            options: ['Periodic (rolling)', 'Statutory periodic (fixed term expired)', 'Contractual periodic'],
            validation: { required: true },
            description: 'Section 13 notices can only be used for periodic tenancies (not during a fixed term). Under the Renters\' Rights Bill 2025, all tenancies will be periodic by default.'
          },
          {
            id: 'additionalNotes',
            type: 'textarea' as const,
            question: 'Any additional context or notes for the notice?',
            validation: { required: false },
            description: 'Include any relevant context such as market comparables, improvements made to the property, or reasons for the increase.',
            placeholder: 'e.g., New kitchen fitted in January 2025, comparable properties in area now let at higher rents...',
            rows: 4
          }
        ]
      }
    },
    prompts: createDocumentPrompts(
      'Section 13 Rent Increase Notice',
      'Generate a standard Section 13 Housing Act 1988 rent increase notice based on the uploaded tenancy agreement. Extract landlord/tenant names, property address, and current rent from the document. Include the statutory wording, correct notice period, proposed new rent, and tenant\'s right to refer to tribunal. Incorporate ALL form data.',
      'Generate an enhanced Section 13 Housing Act 1988 rent increase notice with full statutory compliance. Extract from the uploaded document: landlord and tenant full names, property address, current rent amount and payment frequency, tenancy type. Generate the notice including: prescribed Form 4 format and statutory wording, correct notice period calculation (minimum 1 month for monthly/shorter periods, 2 months for yearly tenancies), current rent vs proposed new rent clearly stated, effective date of increase, tenant\'s right to refer the notice to the First-tier Tribunal (Property Chamber), deadline for tribunal referral (before the proposed increase date), statement that the increase takes effect from the date specified if not referred. Include compliance notes highlighting Renters\' Rights Bill 2025 changes. Incorporate ALL form data.',
      'Generate a premium professional Section 13 Housing Act 1988 rent increase notice with exhaustive statutory compliance and practical guidance. Extract from the uploaded tenancy document: full legal names of all landlords and tenants, complete property address, current rent amount and payment frequency and method, tenancy commencement date, tenancy type (periodic/statutory periodic/contractual periodic), any existing rent review clauses. Generate a comprehensive notice package including: (1) THE NOTICE ITSELF - fully compliant Section 13(2) Housing Act 1988 notice in prescribed Form 4 format with all statutory required information (landlord name and address, tenant name, property address, current rent, proposed new rent, proposed effective date, rent payment period, statement of tenant rights), correct notice period calculation and verification (Section 13(3) requirements: minimum period equal to the rental period or 1 month whichever is longer, but 6 months for yearly tenancies; note Renters\' Rights Bill 2025 changes: minimum 2 months notice for ALL periodic tenancies regardless of payment period), (2) COMPLIANCE CHECKLIST - verification that notice period is sufficient, tenancy is periodic not fixed-term, no other Section 13 increase within the last 12 months (Section 13(2)(b)), rent is for a dwelling-house let on an assured tenancy, tenancy is not excluded (Section 13(1) exclusions), (3) COMMON ERRORS WARNING - insufficient notice period, serving during fixed term, wrong form used, notice not served on all joint tenants, increase date not aligned with rent period, serving more than once in 12 months, (4) RENTERS\' RIGHTS BILL 2025 IMPACT NOTES - all tenancies periodic by default (no more fixed terms), Section 21 abolished so Section 13 is the only route for rent increases, minimum 2 months notice regardless of payment period, rent can only be increased once per year, tenant tribunal referral right strengthened, tribunal can determine market rent (may be lower than proposed), landlord cannot give notice to quit in retaliation for tribunal referral, advertising rent restrictions may apply, (5) TENANT RESPONSE GUIDANCE - tenant\'s right to refer to First-tier Tribunal (Property Chamber) before the effective date, what happens if tenant does not respond (increase takes effect automatically), what happens at tribunal (market rent assessment), tenant cannot be evicted for referring to tribunal (protection from retaliatory eviction), (6) SERVICE REQUIREMENTS - notice must be served on every tenant, acceptable methods of service (personal delivery, first class post, leaving at last known address), proof of service recommendations. Format professionally with clear section numbering. Incorporate ALL form data.'
    )
  }
};
